import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ReactFlow,
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  addEdge,
  Controls,
  Background,
  BackgroundVariant,
  useStoreApi,
  useReactFlow,
  Handle,
  Position,
  NodeResizer,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import './Workflows.css';
import {
  getWorkflows,
  getWorkflowById,
  createWorkflow,
  updateWorkflow,
  deleteWorkflow,
  publishWorkflow,
  unpublishWorkflow,
  runWorkflow,
  runN8nWorkflow,
  getWorkflowRuns,
  getWorkflowRunById,
  applyWorkflowRunRepair,
  getRecentWorkflowRuns,
  getLastSnapshot,
  getSnapshotHtml,
  getEvents,
  getAccounts,
  getUserWebhooks,
  getAiConnectors,
  API_BASE_URL,
} from '../services/api';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import MaterialIcon from '../components/MaterialIcon';
import N8nWorkflowEditor from './N8nWorkflowEditor';
import Editor from 'react-simple-code-editor';
import { highlight, languages } from 'prismjs';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-python';

// --- Material Symbols ikony pre fragmenty ---
const ICON_SIZE = 16;

// Mapovanie typov fragmentov na Material Symbols ikony
const FRAGMENT_ICON_MAP = {
  'agent': 'smart_toy',
  'open_website': 'open_in_new',
  'redirect': 'arrow_forward',
  'browser_history_back': 'arrow_back',
  'fill_forms': 'edit_note',
  'click_element': 'touch_app',
  'wait_time': 'schedule',
  'random_delay': 'shuffle',
  'wait_for_element': 'hourglass_empty',
  'console_log': 'terminal',
  'run_python_script': 'code',
  'run_javascript': 'javascript',
  'if_ai': 'auto_awesome',
  'if_fixed': 'call_split',
  'if_operator': 'call_split',
  'if_multi_dom': 'call_split',
  'if_multi_operator': 'call_split',
  'extract_data': 'data_object',
  'get_html': 'code',
  'ai_prompt': 'wand_stars',
  'call_api': 'api',
  'subflow': 'account_tree',
  'group': 'view_module', // Visual subflow skupina
  'breakpoint': 'pause_circle_filled',
};

function FragmentIcon({ type, size = ICON_SIZE, style }) {
  const iconName = FRAGMENT_ICON_MAP[type] || 'circle';
  return (
    <MaterialIcon 
      icon={iconName} 
      size={size} 
      style={{ flexShrink: 0, ...style }}
      variant="outlined"
    />
  );
}

// --- CodeEditor komponenta s syntax highlighting ---
function CodeEditor({ value, onChange, language = 'javascript', placeholder, rows = 8, ...props }) {
  const lang = language === 'python' ? languages.python : languages.javascript;
  const editorRef = useRef(null);
  
  // Synchronizácia scroll pozície medzi textarea a pre elementom
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    
    const textarea = editor.querySelector('.code-editor-textarea');
    const pre = editor.querySelector('.code-editor-pre');
    if (!textarea || !pre) return;
    
    const syncScroll = () => {
      // Synchronizuj scroll pozíciu z textarea na pre
      pre.scrollTop = textarea.scrollTop;
      pre.scrollLeft = textarea.scrollLeft;
    };
    
    // Pridaj event listener na scroll
    textarea.addEventListener('scroll', syncScroll, { passive: true });
    
    // Synchronizuj aj pri zmene hodnoty
    syncScroll();
    
    return () => {
      textarea.removeEventListener('scroll', syncScroll);
    };
  }, [value]);
  
  const editorHeight = `${rows * 1.5 * 12 + 20}px`;
  
  return (
    <div className="code-editor-wrapper" style={{ 
      border: '1px solid #444', 
      borderRadius: '4px', 
      backgroundColor: '#1e1e1e',
      overflow: 'hidden',
      position: 'relative',
      minHeight: editorHeight,
      maxHeight: editorHeight,
    }}>
      <div ref={editorRef} className="code-editor-container" style={{ height: '100%' }}>
        <Editor
          value={value || ''}
          onValueChange={onChange}
          highlight={code => highlight(code, lang, language)}
          padding={10}
          style={{
            minHeight: editorHeight,
            height: '100%',
            outline: 'none',
            color: '#d4d4d4',
            backgroundColor: '#1e1e1e',
          }}
          textareaClassName="code-editor-textarea"
          preClassName="code-editor-pre"
          placeholder={placeholder}
          {...props}
        />
      </div>
    </div>
  );
}

// --- Extrahovanie {{ query.xxx }} a {{ body.xxx }} premenných z workflow nodes ---
function extractWorkflowVariables(nodes) {
  const queryVars = new Set();
  const bodyVars = new Set();
  const regex = /\{\{\s*(query|body)\.([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g;

  function scanValue(val) {
    if (!val) return;
    if (typeof val === 'string') {
      let match;
      while ((match = regex.exec(val)) !== null) {
        const [, type, name] = match;
        if (type === 'query') queryVars.add(name);
        else if (type === 'body') bodyVars.add(name);
      }
      regex.lastIndex = 0;
    } else if (Array.isArray(val)) {
      val.forEach(scanValue);
    } else if (typeof val === 'object') {
      Object.entries(val).forEach(([key, v]) => {
        if (key === 'base64' || key === 'dataUrl') return;
        scanValue(v);
      });
    }
  }

  (nodes || []).forEach((node) => {
    const data = node.data || {};
    scanValue(data.params);
    scanValue(data.globalVariables);
    scanValue(data.url);
    scanValue(data.fields);
    scanValue(data.selector);
    scanValue(data.text);
    scanValue(data.script);
    scanValue(data.value);
    scanValue(data.values);
    scanValue(data.source);
    scanValue(data.queryParams);
    scanValue(data.bodyParams);
  });

  return {
    query: Array.from(queryVars).sort(),
    body: Array.from(bodyVars).sort(),
  };
}

// --- Extract query/body variables from n8n workflow nodes (config.getParams, config.bodyParams, url, etc.) ---
function extractWorkflowVariablesFromN8n(nodes) {
  const queryVars = new Set();
  const bodyVars = new Set();
  const bracedRegex = /\{\{\s*(query|body)\.([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g;
  const bareRegex = /\b(query|body)\.([a-zA-Z_][a-zA-Z0-9_]*)\b/g;

  function scanString(s) {
    if (typeof s !== 'string') return;
    let m;
    bracedRegex.lastIndex = 0;
    while ((m = bracedRegex.exec(s)) !== null) {
      if (m[1] === 'query') queryVars.add(m[2]);
      else bodyVars.add(m[2]);
    }
    bareRegex.lastIndex = 0;
    while ((m = bareRegex.exec(s)) !== null) {
      if (m[1] === 'query') queryVars.add(m[2]);
      else bodyVars.add(m[2]);
    }
  }

  function scanValue(val) {
    if (val == null) return;
    if (typeof val === 'string') {
      scanString(val);
    } else if (Array.isArray(val)) {
      val.forEach(scanValue);
    } else if (typeof val === 'object') {
      Object.values(val).forEach(scanValue);
    }
  }

  (nodes || []).forEach((node) => {
    const config = node.data?.config ?? node.config ?? {};
    scanValue(config.getParams);
    scanValue(config.bodyParams);
    scanValue(config.url);
    scanValue(config.body);
    scanValue(config.headers);
    scanValue(config.schedule);
    scanValue(config.description);
    scanValue(config.code);
    scanValue(config.prompt);
    scanValue(config.systemPrompt);
    scanValue(node.data);
  });

  return {
    query: Array.from(queryVars).sort(),
    body: Array.from(bodyVars).sort(),
  };
}

/** Given a workflow object { workflowType, nodes }, return { query: [], body: [] } of required vars. */
function extractWorkflowVariablesByType(workflow) {
  if (!workflow) return { query: [], body: [] };
  const nodes = workflow.nodes || [];
  if (workflow.workflowType === 'n8n') {
    return extractWorkflowVariablesFromN8n(nodes);
  }
  return extractWorkflowVariables(nodes);
}

/** Obrázky pre fragment ai_prompt – max 5 MB, raster. Backend má pri behu zapísať do dočasného úložiska, po LLM volaní zmazať. */
const AI_PROMPT_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

function isAllowedAiPromptImageMime(mime) {
  return /^image\/(png|jpeg|gif|webp)$/i.test(String(mime || '').trim());
}

function guessImageMimeFromFileName(name) {
  const n = String(name || '').toLowerCase();
  if (n.endsWith('.png')) return 'image/png';
  if (n.endsWith('.jpg') || n.endsWith('.jpeg')) return 'image/jpeg';
  if (n.endsWith('.gif')) return 'image/gif';
  if (n.endsWith('.webp')) return 'image/webp';
  return '';
}

function fileToPromptImageAttachment(file) {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('Neplatný súbor.'));
      return;
    }
    let mime = String(file.type || '').trim();
    if (!mime) mime = guessImageMimeFromFileName(file.name);
    if (!isAllowedAiPromptImageMime(mime)) {
      reject(new Error('Povolené sú iba obrázky PNG, JPEG, GIF alebo WebP.'));
      return;
    }
    if (file.size > AI_PROMPT_IMAGE_MAX_BYTES) {
      reject(new Error('Maximálna veľkosť obrázku je 5 MB.'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '');
      const comma = text.indexOf(',');
      const base64 = comma >= 0 ? text.slice(comma + 1) : text;
      const id =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `img-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
      resolve({
        id,
        name: file.name || 'image',
        mimeType: mime.trim() || 'image/png',
        base64,
      });
    };
    reader.onerror = () => reject(new Error('Nepodarilo sa načítať súbor.'));
    reader.readAsDataURL(file);
  });
}

// --- Fragment categories (for palette) ---
const FRAGMENT_CATEGORIES = [
  { id: 'start', label: 'Start' },
  { id: 'subflow', label: 'Subflow' },
  { id: 'dom', label: 'DOM operations' },
  { id: 'code', label: 'Code & data' },
  { id: 'branching', label: 'Branching' },
  { id: 'debug', label: 'Debug' },
];

// --- Fragment types (WORKFLOW_BUILDER_SPEC.md); category for palette display ---
const FRAGMENT_TYPES = [
  { id: 'agent', label: 'Agent', description: 'Start – AI model, API kľúč', category: 'start', defaultParams: { description: '', modelName: '', aiConnectorId: '', maxTokens: 2048, temperature: 0.7, timeout: 30, language: 'sk', disableImages: false, disableFonts: false, blockAds: false, blockMedia: false, blockExternalJs: false, blockScriptsOtherThanMainDomain: false, lowBandwidthMode: false, autoCloseChrome: true, acceptEncoding: 'deflate', useProxy: false, proxyHost: '', proxyPort: '', proxyUsername: '', proxyPassword: '', globalVariables: [] } },
  { id: 'open_website', label: 'Open website', description: 'Opens browser and loads URL', category: 'dom', defaultParams: { description: '', url: '' } },
  { id: 'redirect', label: 'Redirect', description: 'Redirect to URL', category: 'dom', defaultParams: { description: '', url: '' } },
  { id: 'browser_history_back', label: 'Browser back', description: 'Go back in browser history', category: 'dom', defaultParams: { description: '' } },
  { id: 'fill_forms', label: 'Fill forms', description: 'Fill inputs, selects, radio, checkbox', category: 'dom', defaultParams: { description: '', fields: [] } },
  { id: 'click_element', label: 'Click on element', description: 'Click element by selector or text', category: 'dom', defaultParams: { description: '', selector: '', clickBy: 'selector', text: '', occurrenceIndex: 1, clickMultiple: false, clickCount: 2, clickDelayMs: 200, onFailed: 'fail', onFailedAiPrompt: '', onFailedAutorepair: false } },
  { id: 'wait_time', label: 'Wait time', description: 'Pause before next step', category: 'dom', defaultParams: { description: '', ms: 1000 } },
  { id: 'random_delay', label: 'Random delay', description: 'Random pause in range (ms)', category: 'dom', defaultParams: { description: '', minMs: 500, maxMs: 2000 } },
  { id: 'wait_for_element', label: 'Wait for element', description: 'Wait for element existence/absence in DOM', category: 'dom', defaultParams: { description: '', selector: '', selectorType: 'css', waitType: 'appear', visible: true, timeout: 10 } },
  { id: 'console_log', label: 'Console log', description: 'Show variable in log (no output value)', category: 'code', defaultParams: { description: '', variable: '' } },
  { id: 'run_python_script', label: 'Run Python script', description: 'Custom script + AI generation', category: 'code', hasOutput: true, defaultParams: { description: '', script: '', aiPrompt: '' } },
  { id: 'run_javascript', label: 'Run JavaScript', description: 'Run JS in page context (security limits)', category: 'code', hasOutput: true, defaultParams: { description: '', script: '' } },
  { id: 'call_api', label: 'Call API / Webhook', description: 'HTTP call – URL or selected webhook, query/body params', category: 'code', hasOutput: true, defaultParams: { description: '', url: '', urlSource: 'url', webhookId: '', method: 'GET', queryParams: [], bodyParams: [], headerParams: [], auth: { type: 'none' } } },
  { id: 'extract_data', label: 'Extract data', description: 'Select element and extract data', category: 'code', hasOutput: true, defaultParams: { description: '', structure: '', matchMode: 'one', containerSelector: '', occurrenceIndex: 1 } },
  { id: 'get_html', label: 'Get HTML', description: 'Return full HTML from selector in current DOM', category: 'code', hasOutput: true, defaultParams: { description: '', selector: 'body', occurrenceIndex: 1 } },
  { id: 'ai_prompt', label: 'AI prompt (text)', description: 'Čistý textový prompt k AI z agenta; výstup = odpoveď modelu. Voliteľné obrázky (max 5 MB, dočasne pri behu).', category: 'code', hasOutput: true, defaultParams: { description: '', prompt: '', promptImageAttachments: [] } },
  { id: 'if_ai', label: 'AI IF (DOM)', description: 'Branch by AI – describe condition in natural language (page)', category: 'branching', defaultParams: { description: '', prompt: '' } },
  { id: 'if_fixed', label: 'IF (DOM)', description: 'Branch: element / text in source / text in element on page', category: 'branching', defaultParams: { description: '', checkType: 'element', selector: '', text: '' } },
  { id: 'if_operator', label: 'IF (operator)', description: 'Branch by previous step output (previous.input)', category: 'branching', defaultParams: { description: '', operatorType: 'equals', value: '', values: '', source: '' } },
  { id: 'if_multi_dom', label: 'IF multi (DOM)', description: 'IF / else IF / … / else – multiple DOM conditions in order', category: 'branching', defaultParams: { description: '', conditions: [{ checkType: 'element', selector: '', text: '' }, { checkType: 'element', selector: '', text: '' }] } },
  { id: 'if_multi_operator', label: 'IF multi (operator)', description: 'IF / else IF / … / else – multiple output comparisons in order', category: 'branching', defaultParams: { description: '', conditions: [{ operatorType: 'equals', value: '', values: '', source: '' }, { operatorType: 'equals', value: '', values: '', source: '' }] } },
  { id: 'subflow', label: 'Subflow', description: 'Run another workflow as subflow (same run)', category: 'subflow', defaultParams: { description: '', workflowId: '', workflowName: '', subflowQueryParams: {}, subflowBodyParams: {} } },
  { id: 'breakpoint', label: 'Breakpoint', description: 'Stop workflow run and take snapshot of current page', category: 'debug', defaultParams: { description: '' } },
];

// --- Farba outline podľa kategórie fragmentu (pre workflow editor) ---
const FRAGMENT_CATEGORY_OUTLINES = {
  start: '#3498db',
  subflow: '#9b59b6',
  dom: '#27ae60',
  code: '#3498db',
  branching: '#f39c12',
  debug: '#e74c3c',
  default: '#555',
};
// Jemné plné pozadie (sivá + ~9% farby kategórie, bez priehľadnosti) – paleta
const FRAGMENT_CATEGORY_BG = {
  start: '#2b343a',
  subflow: '#352e37',
  dom: '#29362f',
  code: '#2b343a',
  branching: '#3d3328',
  debug: '#3d2b2b',
  default: '#2e2e2e',
};
const FRAGMENT_CATEGORY_BG_HOVER = {
  start: '#333c42',
  subflow: '#35343c',
  dom: '#323e37',
  code: '#333c42',
  branching: '#443c30',
  debug: '#453333',
  default: '#343434',
};
function getFragmentCategory(fragmentTypeId) {
  return FRAGMENT_TYPES.find((f) => f.id === fragmentTypeId)?.category || 'default';
}
function getOutlineClassNameForNode(node) {
  if (node.type === 'group') return 'workflow-outline workflow-outline--group';
  const cat = getFragmentCategory(node.data?.fragmentType || node.data?.type);
  return `workflow-outline workflow-outline--${cat}`;
}

// --- Zhrnutie parametrov na karte fragmentu ---
function getFragmentSummary(data) {
  const ft = data.fragmentType || data.type;
  const p = data.params || {};
  if (ft === 'agent') {
    const hasAi = !!(p.modelName && (p.apiKey || p.aiConnectorId));
    const noImg = p.disableImages === true || p.lowBandwidthMode === true;
    const noFonts = p.disableFonts === true || p.lowBandwidthMode === true;
    const noAds = p.blockAds === true || p.lowBandwidthMode === true;
    const noMedia = p.blockMedia === true || p.lowBandwidthMode === true;
    const noExtJs = p.blockExternalJs === true || p.lowBandwidthMode === true;
    const onlyMainDomainJs = p.blockScriptsOtherThanMainDomain === true;
    const autoClose = p.autoCloseChrome !== false;
    const proxyOn = p.useProxy === true && (p.proxyHost || '').trim();
    const proxyLine = proxyOn ? `Proxy: ${(p.proxyHost || '').trim()}${(p.proxyPort || '') ? ':' + p.proxyPort : ''}` : null;
    const globalCount = (p.globalVariables || []).filter((g) => g && (g.name || '').trim()).length;
    return (
      <div style={{ fontSize: 10, color: '#aaa', marginTop: 4, lineHeight: 1.4 }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          AI: {hasAi ? (
            <>
              <MaterialIcon icon="check_circle" size={12} />
              yes
            </>
          ) : '— no'}
        </div>
        {globalCount > 0 && <div>Global variables: {globalCount}</div>}
        <div>Data saving: img {noImg ? 'off' : 'on'}, fonts {noFonts ? 'off' : 'on'}, ads {noAds ? 'off' : 'on'}, media {noMedia ? 'off' : 'on'}, ext. JS {noExtJs ? 'off' : 'on'}{onlyMainDomainJs ? ', scripts from domain only' : ''} · Close Chrome: {autoClose ? 'yes' : 'no'}</div>
        {proxyLine && <div>{proxyLine}</div>}
      </div>
    );
  }
  if (ft === 'open_website' || ft === 'redirect') {
    const url = (p.url || '').trim();
    if (!url) return <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>— URL not set</div>;
    const short = url.length > 45 ? url.slice(0, 42) + '…' : url;
    return <div style={{ fontSize: 10, color: '#9cdcfe', marginTop: 4, wordBreak: 'break-all' }} title={url}>{short}</div>;
  }
  if (ft === 'browser_history_back') {
    return <div style={{ fontSize: 10, color: '#9cdcfe', marginTop: 4 }}>Späť v histórii</div>;
  }
  if (ft === 'click_element') {
    const byText = p.clickBy === 'text';
    const text = (p.text || '').trim();
    const sel = (p.selector || '').trim();
    const occRaw = p.occurrenceIndex !== undefined && p.occurrenceIndex !== null ? String(p.occurrenceIndex).trim() : '';
    const occNum = Math.max(1, parseInt(occRaw, 10) || 1);
    const orderSuffix = (occRaw && occRaw.includes('{{')) ? ' (previous.input)' : (occNum > 1 ? ` (${occNum}. in order)` : '');
    const clickMultiple = p.clickMultiple === true;
    const ccRaw = p.clickCount !== undefined && p.clickCount !== null ? String(p.clickCount).trim() : '';
    const ccNum = Math.max(1, parseInt(ccRaw, 10) || 1);
    const countSuffix = clickMultiple ? ((ccRaw && ccRaw.includes('{{')) ? ' · N× (dyn.)' : ` · ${ccNum}×`) : '';
    const delayRaw = p.clickDelayMs !== undefined && p.clickDelayMs !== null ? String(p.clickDelayMs).trim() : '';
    const delaySuffix = clickMultiple && delayRaw && !String(delayRaw).includes('{{') ? `, ${delayRaw} ms` : (clickMultiple && delayRaw && String(delayRaw).includes('{{') ? ', dyn. ms' : '');
    if (byText) {
      if (!text) return <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>— text not set</div>;
      const short = text.length > 35 ? text.slice(0, 32) + '…' : text;
      return <div style={{ fontSize: 10, color: '#ce9178', marginTop: 4 }} title={text}>Click (text): {short}{orderSuffix}{countSuffix}{delaySuffix}</div>;
    }
    if (!sel) return <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>— selector not set</div>;
    const short = sel.length > 35 ? sel.slice(0, 32) + '…' : sel;
    return <div style={{ fontSize: 10, color: '#ce9178', marginTop: 4 }} title={sel}>Click: {short}{orderSuffix}{countSuffix}{delaySuffix}</div>;
  }
  if (ft === 'fill_forms') {
    const fields = Array.isArray(p.fields) ? p.fields : [];
    const filled = fields.filter((f) => (typeof f === 'object' ? f.selector : String(f).trim()));
    if (filled.length === 0) return <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>— no fields</div>;
    const title = filled.map((f) => (typeof f === 'object' ? `${f.selector} → ${f.value}` : f)).join('\n');
    const preview = filled.slice(0, 2).map((f) => (typeof f === 'object' ? `${(f.selector || '').slice(0, 20)}${(f.selector || '').length > 20 ? '…' : ''} → ${String(f.value || '').slice(0, 12)}${String(f.value || '').length > 12 ? '…' : ''}` : f)).join('; ');
    const more = filled.length > 2 ? ` (+${filled.length - 2})` : '';
    return <div style={{ fontSize: 10, color: '#9cdcfe', marginTop: 4 }} title={title}>Fills: {preview}{more}</div>;
  }
  if (ft === 'wait_time') {
    const ms = parseInt(p.ms, 10);
    if (Number.isNaN(ms) || ms < 0) return <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>— not set</div>;
    return <div style={{ fontSize: 10, color: '#b5cea8', marginTop: 4 }}>{ms} ms</div>;
  }
  if (ft === 'random_delay') {
    const minMs = parseInt(p.minMs, 10);
    const maxMs = parseInt(p.maxMs, 10);
    if (Number.isNaN(minMs) && Number.isNaN(maxMs)) return <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>— not set</div>;
    const lo = Number.isNaN(minMs) ? '?' : minMs;
    const hi = Number.isNaN(maxMs) ? '?' : maxMs;
    return <div style={{ fontSize: 10, color: '#b5cea8', marginTop: 4 }}>{lo} – {hi} ms</div>;
  }
  if (ft === 'wait_for_element') {
    const selector = (p.selector || '').trim();
    const waitType = p.waitType || 'appear';
    const visible = p.visible !== false;
    const timeout = parseInt(p.timeout, 10) || 10;
    if (!selector) return <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>— selector not set</div>;
    const action = waitType === 'disappear' ? 'disappears' : 'appears';
    const visibility = visible ? 'visible' : 'in DOM';
    const short = selector.length > 30 ? selector.slice(0, 27) + '…' : selector;
    return <div style={{ fontSize: 10, color: '#b5cea8', marginTop: 4 }} title={selector}>{action} ({visibility}), {timeout}s: {short}</div>;
  }
  if (ft === 'console_log') {
    const variable = (p.variable || p.var || '').trim();
    if (!variable) return <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>— variable not set</div>;
    const short = variable.length > 35 ? variable.slice(0, 32) + '…' : variable;
    return <div style={{ fontSize: 10, color: '#b5cea8', marginTop: 4 }} title={variable}>Log: {short}</div>;
  }
  if (ft === 'if_ai') {
    const prompt = (p.prompt || '').trim();
    if (!prompt) return <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>— prompt not set</div>;
    const short = prompt.length > 28 ? prompt.slice(0, 25) + '…' : prompt;
    return <div style={{ fontSize: 10, color: '#ce9178', marginTop: 4 }} title={prompt}>{short}</div>;
  }
  if (ft === 'ai_prompt') {
    const prompt = (p.prompt || '').trim();
    const imgs = Array.isArray(p.promptImageAttachments) ? p.promptImageAttachments.filter((x) => x && (x.base64 || x.dataUrl)) : [];
    const imgNote = imgs.length ? ` · ${imgs.length} obr.` : '';
    if (!prompt) return <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>— prompt not set{imgNote}</div>;
    const short = prompt.length > 24 ? prompt.slice(0, 21) + '…' : prompt;
    return <div style={{ fontSize: 10, color: '#c4a5f5', marginTop: 4 }} title={prompt}>{short}{imgNote}</div>;
  }
  if (ft === 'if_fixed') {
    const ct = p.checkType || 'element';
    const sel = (p.selector || '').trim();
    const txt = (p.text || '').trim();
    if (ct === 'element' && !sel) return <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>— selector</div>;
    if (ct === 'text_in_source' && !txt) return <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>— text in source</div>;
    if (ct === 'text_in_element' && (!sel || !txt)) return <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>— selector + text</div>;
    const label = ct === 'element' ? sel : ct === 'text_in_source' ? `"${txt}"` : `${sel} ∋ "${txt}"`;
    const short = label.length > 28 ? label.slice(0, 25) + '…' : label;
    return <div style={{ fontSize: 10, color: '#9cdcfe', marginTop: 4 }} title={label}>{short}</div>;
  }
  if (ft === 'if_operator') {
    const op = p.operatorType || 'equals';
    const val = (p.value || '').trim();
    const vals = (p.values || '').trim();
    if (op === 'in_list' && !vals) return <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>— values (comma-separated)</div>;
    if (val === '' && op !== 'in_list') return <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>— value</div>;
    const label = op === 'in_list' ? vals : val;
    const short = label.length > 28 ? label.slice(0, 25) + '…' : label;
    return <div style={{ fontSize: 10, color: '#b5cea8', marginTop: 4 }} title={label}>{op}: {short}</div>;
  }
  if (ft === 'if_multi_dom') {
    const conditions = Array.isArray(p.conditions) ? p.conditions : [];
    const n = conditions.length;
    if (n === 0) return <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>— add conditions</div>;
    return <div style={{ fontSize: 10, color: '#9cdcfe', marginTop: 4 }}>{n} IF + ELSE</div>;
  }
  if (ft === 'if_multi_operator') {
    const conditions = Array.isArray(p.conditions) ? p.conditions : [];
    const n = conditions.length;
    if (n === 0) return <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>— add conditions</div>;
    return <div style={{ fontSize: 10, color: '#b5cea8', marginTop: 4 }}>{n} IF + ELSE</div>;
  }
  if (ft === 'run_javascript') {
    const script = (p.script || '').trim();
    if (!script) return <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>— script not set</div>;
    const firstLine = script.split('\n')[0].trim();
    const short = firstLine.length > 35 ? firstLine.slice(0, 32) + '…' : firstLine;
    return <div style={{ fontSize: 10, color: '#b5cea8', marginTop: 4 }} title={script.slice(0, 200)}>{short}</div>;
  }
  if (ft === 'call_api') {
    const url = (p.url || '').trim();
    const method = (p.method || 'GET').toUpperCase();
    if (!url) return <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>— URL not set</div>;
    const qCount = Array.isArray(p.queryParams) ? p.queryParams.filter((e) => e && (e.key || '').trim()).length : 0;
    const bCount = Array.isArray(p.bodyParams) ? p.bodyParams.filter((e) => e && (e.key || '').trim()).length : 0;
    const short = url.length > 40 ? url.slice(0, 37) + '…' : url;
    return (
      <div style={{ fontSize: 10, color: '#9cdcfe', marginTop: 4 }} title={url}>
        {method} {short}
        {(qCount || bCount) ? ` · query: ${qCount}, body: ${bCount}` : ''}
      </div>
    );
  }
  if (ft === 'extract_data') {
    const matchMode = p.matchMode || 'one';
    const container = (p.containerSelector || '').trim();
    const occRaw = p.occurrenceIndex !== undefined && p.occurrenceIndex !== null ? String(p.occurrenceIndex).trim() : '';
    const occNum = Math.max(1, parseInt(occRaw, 10) || 1);
    const occLabel = (occRaw && occRaw.includes('{{')) ? 'previous.input' : (occNum > 1 ? `${occNum}. in order` : 'first element');
    if (matchMode === 'all') {
      if (!container) return <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>All — container selector not set</div>;
      const short = container.length > 28 ? container.slice(0, 25) + '…' : container;
      return <div style={{ fontSize: 10, color: '#b5cea8', marginTop: 4 }} title={container}>All: {short}</div>;
    }
    return <div style={{ fontSize: 10, color: '#b5cea8', marginTop: 4 }}>One ({occLabel})</div>;
  }
  if (ft === 'get_html') {
    const sel = (p.selector || '').trim();
    const occRaw = p.occurrenceIndex !== undefined && p.occurrenceIndex !== null ? String(p.occurrenceIndex).trim() : '';
    const occNum = Math.max(1, parseInt(occRaw, 10) || 1);
    const occLabel = (occRaw && occRaw.includes('{{')) ? 'previous.input' : (occNum > 1 ? `${occNum}. in order` : 'first');
    if (!sel) return <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>— Selector not set</div>;
    const short = sel.length > 28 ? sel.slice(0, 25) + '…' : sel;
    return <div style={{ fontSize: 10, color: '#b5cea8', marginTop: 4 }} title={sel}>{short} ({occLabel})</div>;
  }
  if (ft === 'subflow') {
    const wName = (p.workflowName || '').trim();
    const wId = (p.workflowId || '').trim();
    if (!wId && !wName) return <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>— workflow not selected</div>;
    const short = wName.length > 28 ? wName.slice(0, 25) + '…' : wName;
    const nQuery = Object.keys(p.subflowQueryParams || {}).length;
    const nBody = Object.keys(p.subflowBodyParams || {}).length;
    const inputsLine = (nQuery || nBody) ? (
      <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>Inputs: {nQuery ? `${nQuery} query` : ''}{nQuery && nBody ? ', ' : ''}{nBody ? `${nBody} body` : ''}</div>
    ) : null;
    return (
      <>
        <div style={{ fontSize: 10, color: '#9cdcfe', marginTop: 4 }} title={wName || wId}>{short || wId}</div>
        {inputsLine}
      </>
    );
  }
  return null;
}

// --- Vlastný uzol fragmentu (dvojklik = nastavenia) ---
function FragmentNode({ data, selected }) {
  const fragmentType = FRAGMENT_TYPES.find((t) => t.id === (data.fragmentType || data.type));
  const label = data.label || fragmentType?.label || data.fragmentType || 'Fragment';
  const ft = data.fragmentType || data.type;
  const isIf = ft === 'if_ai' || ft === 'if_fixed' || ft === 'if_operator';
  const isIfMulti = ft === 'if_multi_dom' || ft === 'if_multi_operator';
  const conditions = Array.isArray((data.params || {}).conditions) ? (data.params || {}).conditions : [];
  const numHandles = isIfMulti ? Math.max(2, conditions.length + 1) : 0;
  const summary = getFragmentSummary(data);

  return (
    <div className={`workflow-fragment-node ${selected ? 'selected' : ''}`} style={{ padding: '10px 14px', minWidth: 160, maxWidth: 220 }}>
      <Handle type="target" position={Position.Top} style={{ left: '50%' }} />
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
        {fragmentType && <FragmentIcon type={fragmentType.id} size={14} />}
        {label}
      </div>
      {(data.params?.description || '').trim() ? (
        <div style={{ fontSize: 13, color: '#aaa', fontWeight: 500, marginTop: 2, lineHeight: 1.35 }}>{(data.params?.description || '').trim()}</div>
      ) : null}
      {summary}
      {isIf && !isIfMulti && (
        <>
          <Handle type="source" position={Position.Bottom} id="a" style={{ left: '30%' }} />
          <Handle type="source" position={Position.Bottom} id="b" style={{ left: '70%' }} />
          <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>A / B</div>
        </>
      )}
      {isIfMulti && numHandles > 0 && (
        <>
          {Array.from({ length: numHandles }, (_, i) => (
            <Handle key={i} type="source" position={Position.Bottom} id={String(i)} style={{ left: `${((i + 0.5) / numHandles) * 100}%`, transform: 'translateX(-50%)' }} />
          ))}
          <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>IF 0…{numHandles - 1} / ELSE</div>
        </>
      )}
      {!isIf && !isIfMulti && <Handle type="source" position={Position.Bottom} style={{ left: '50%' }} />}
    </div>
  );
}

// --- Vizuálna skupina (subflow) – kontajner, resizable, bez handle-ov (React Flow Sub Flows) ---
function GroupNode({ data, selected }) {
  const label = data?.label || 'Subflow';
  return (
    <>
      <NodeResizer
        minWidth={200}
        minHeight={120}
        isVisible={selected}
      />
      <div style={{ width: '100%', height: '100%', minWidth: 200, minHeight: 120, boxSizing: 'border-box', padding: '8px 12px' }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'inherit' }}>{label}</div>
      </div>
    </>
  );
}

// --- Last snapshot: sticky div pri fragmente (nie súčasť React Flow), otvára modal ---
const LAST_SNAPSHOT_COLOR = '#7d3c98';
const LAST_SNAPSHOT_BUTTON_SIZE = 40;
const LAST_SNAPSHOT_GAP = 7; // medzera od pravého okraja fragmentu
// Šírka uzla: React Flow môže nastaviť width/measured; fallback podľa typu (fragment maxWidth 220, group 280)
function getNodeWidth(node) {
  if (!node) return 220;
  const w = node.width ?? node.measured?.width ?? (node.style && (typeof node.style.width === 'number' ? node.style.width : parseFloat(node.style.width)));
  if (typeof w === 'number' && !isNaN(w) && w > 0) return w;
  return node.type === 'group' ? 280 : 220;
}

const nodeTypes = { fragment: FragmentNode, group: GroupNode };

// --- Panel nastavení skupiny (subflow): názov ---
function GroupSettingsPanel({ node, onSave, onClose }) {
  const [label, setLabel] = useState(node?.data?.label || 'Subflow');
  useEffect(() => setLabel(node?.data?.label || 'Subflow'), [node?.id, node?.data?.label]);
  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ label: label.trim() || 'Subflow' });
    onClose();
  };
  return (
    <div className="workflow-settings-panel">
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ padding: 16, borderBottom: '1px solid #444', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <h3 style={{ margin: 0 }}>Visual subflow</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" className="btn btn-primary btn-sm">Save</button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
          </div>
        </div>
        <div style={{ padding: 16, flex: 1, overflowY: 'auto' }}>
          <div className="form-group">
            <label>Group name</label>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Subflow"
              style={{ width: '100%' }}
            />
          </div>
          <p style={{ fontSize: 12, color: '#888', marginTop: 12 }}>
            Resize by dragging the group corners/edges when selected. Move a fragment into the group via the fragment settings panel → "Group" item.
          </p>
        </div>
      </form>
    </div>
  );
}

// --- Uzol fragmentu len na zobrazenie (stepState: pending | current | done | failed) ---
function ReadOnlyFragmentNode({ data, selected }) {
  const fragmentType = FRAGMENT_TYPES.find((t) => t.id === (data.fragmentType || data.type));
  const label = data.label || fragmentType?.label || data.fragmentType || 'Fragment';
  const ft = data.fragmentType || data.type;
  const isIf = ft === 'if_ai' || ft === 'if_fixed' || ft === 'if_operator';
  const isIfMulti = ft === 'if_multi_dom' || ft === 'if_multi_operator';
  const conditions = Array.isArray((data.params || {}).conditions) ? (data.params || {}).conditions : [];
  const numHandles = isIfMulti ? Math.max(2, conditions.length + 1) : 0;
  const summary = getFragmentSummary(data);
  const stepState = data.stepState || 'pending';
  const isCurrent = stepState === 'current';
  const stateStyle =
    stepState === 'failed'
      ? { borderLeftColor: '#ff4444', borderLeftWidth: 5, backgroundColor: 'rgba(255, 68, 68, 0.15)', border: '2px solid rgba(255, 68, 68, 0.3)' }
      : stepState === 'current'
        ? { borderLeftColor: '#ffd700', borderLeftWidth: 5, backgroundColor: 'rgba(255, 215, 0, 0.2)', border: '2px solid rgba(255, 215, 0, 0.4)', boxShadow: '0 0 12px rgba(255, 215, 0, 0.5)' }
        : stepState === 'done'
          ? { borderLeftColor: '#4caf50', borderLeftWidth: 5, backgroundColor: 'rgba(76, 175, 80, 0.15)', border: '2px solid rgba(76, 175, 80, 0.3)' }
          : { borderLeftColor: '#555', backgroundColor: 'transparent' };

  return (
    <div
      className="workflow-fragment-node read-only"
      style={{
        position: 'relative',
        padding: '10px 14px',
        minWidth: 160,
        maxWidth: 220,
        borderLeftStyle: 'solid',
        borderRadius: stepState !== 'pending' ? 6 : 4,
        ...stateStyle,
      }}
    >
      {isCurrent && (
        <span
          style={{
            position: 'absolute',
            top: 4,
            right: 4,
            width: 10,
            height: 10,
            border: '2px solid rgba(212,168,75,0.5)',
            borderTopColor: '#d4a84b',
            borderRadius: '50%',
            animation: 'workflow-spin 0.8s linear infinite',
          }}
        />
      )}
      <Handle type="target" position={Position.Top} style={{ left: '50%' }} />
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
        {fragmentType && <FragmentIcon type={fragmentType.id} size={14} />}
        {label}
      </div>
      {(data.params?.description || '').trim() ? (
        <div style={{ fontSize: 13, color: '#aaa', fontWeight: 500, marginTop: 2, lineHeight: 1.35 }}>{(data.params?.description || '').trim()}</div>
      ) : null}
      {summary}
      {stepState === 'failed' && data.errorMessage && (
        <div style={{ marginTop: 6, padding: '6px 8px', background: 'rgba(200,68,68,0.2)', borderRadius: 4, fontSize: 11, color: '#f88', border: '1px solid #c44' }}>
          {data.errorMessage}
        </div>
      )}
      {isIf && !isIfMulti && (
        <>
          <Handle type="source" position={Position.Bottom} id="a" style={{ left: '30%' }} />
          <Handle type="source" position={Position.Bottom} id="b" style={{ left: '70%' }} />
          <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>A / B</div>
        </>
      )}
      {isIfMulti && numHandles > 0 && (
        <>
          {Array.from({ length: numHandles }, (_, i) => (
            <Handle key={i} type="source" position={Position.Bottom} id={String(i)} style={{ left: `${((i + 0.5) / numHandles) * 100}%`, transform: 'translateX(-50%)' }} />
          ))}
          <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>IF 0…{numHandles - 1} / ELSE</div>
        </>
      )}
      {!isIf && !isIfMulti && <Handle type="source" position={Position.Bottom} style={{ left: '50%' }} />}
    </div>
  );
}

const nodeTypesReadOnly = { fragment: ReadOnlyFragmentNode, group: GroupNode };

// --- Stav kroku pre read-only zobrazenie ---
function getStepState(nodeId, run) {
  if (!run) return 'pending';
  const stepsDone = run.stepsDone || [];
  const currentId = run.currentStepId;
  const isRunning = run.status === 'running';
  const isFailed = run.status === 'failed';
  const failedAtStep = isFailed && stepsDone.length ? stepsDone[stepsDone.length - 1] : null;
  if (nodeId === failedAtStep) return 'failed';
  if (isRunning && nodeId === currentId) return 'current';
  if (stepsDone.includes(nodeId)) return 'done';
  return 'pending';
}

// --- Pri behu workflow centrovať view na aktuálne bežiaci fragment (vnorené v ReactFlow) ---
function CenterOnCurrentStep({ currentStepId, isRunning }) {
  const { fitView, getNode } = useReactFlow();
  React.useEffect(() => {
    if (!isRunning || !currentStepId) return;
    const node = getNode(currentStepId);
    if (!node) return;
    fitView({
      nodes: [{ id: currentStepId }],
      padding: 0.35,
      maxZoom: 1,
      duration: 400,
    }).catch(() => {});
  }, [currentStepId, isRunning, fitView, getNode]);
  return null;
}

// --- Workflow iba na zobrazenie s priebehom (farebné uzly) ---
function ReadOnlyWorkflowFlow({ workflow, run }) {
  const nodes = React.useMemo(() => {
    const list = workflow?.nodes || [];
    // Mapuj uzly s potrebnými vlastnosťami
    const mappedNodes = list.map((n) => {
      if (n.type === 'group') {
        // Skontroluj, či n.style má width a height, inak použij default
        // MongoDB môže vracať čísla ako stringy, takže konvertujeme na čísla
        let groupStyle = { width: 280, height: 200 };
        if (n.style && typeof n.style === 'object') {
          const width = typeof n.style.width === 'number' ? n.style.width : 
                       (typeof n.style.width === 'string' ? parseFloat(n.style.width) : null);
          const height = typeof n.style.height === 'number' ? n.style.height : 
                        (typeof n.style.height === 'string' ? parseFloat(n.style.height) : null);
          if (width !== null && !isNaN(width) && width > 0 && 
              height !== null && !isNaN(height) && height > 0) {
            groupStyle = { width, height };
          }
        }
        return {
          id: n.id,
          type: 'group',
          position: n.position || { x: 0, y: 0 },
          style: groupStyle,
          data: { label: n.data?.label || 'Subflow' },
          className: getOutlineClassNameForNode({ type: 'group' }),
        };
      }
      return {
        id: n.id,
        type: 'fragment',
        position: n.position || { x: 0, y: 0 },
        parentId: n.parentId,
        extent: n.extent,
        className: getOutlineClassNameForNode({ 
          type: 'fragment', 
          data: { fragmentType: n.data?.fragmentType || n.type } 
        }),
        data: {
          ...(n.data || {}),
          fragmentType: n.data?.fragmentType || n.type,
          label: n.data?.label,
          params: n.data?.params,
          stepState: getStepState(n.id, run),
          errorMessage: getStepState(n.id, run) === 'failed' && run?.error ? run.error : undefined,
        },
      };
    });
    // Zorad nodes: parent nodes musia byť pred child nodes (React Flow requirement)
    const parents = mappedNodes.filter((n) => !n.parentId);
    const children = mappedNodes.filter((n) => n.parentId);
    return [...parents, ...children];
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run used for stepsDone/currentStepId/status/error
  }, [workflow?.nodes, run?.stepsDone, run?.currentStepId, run?.status, run?.error]);

  const edges = React.useMemo(() => {
    if (!run) return (workflow?.edges || []).map((e) => ({
      id: e.id || `${e.source}-${e.target}`,
      source: e.source,
      target: e.target,
      sourceHandle: e.sourceHandle,
      targetHandle: e.targetHandle,
    }));
    
    const stepsDone = run.stepsDone || [];
    const currentStepId = run.currentStepId;
    const isRunning = run.status === 'running';
    const isFailed = run.status === 'failed';
    const failedAtStep = isFailed && stepsDone.length ? stepsDone[stepsDone.length - 1] : null;
    // Skutočná vykonávacia cesta (aj pri IF len jedna vetva)
    const path = [...stepsDone, currentStepId].filter(Boolean);
    
    return (workflow?.edges || []).map((e) => {
      const srcIdx = path.indexOf(e.source);
      const edgeIsOnPath = srcIdx >= 0 && path[srcIdx + 1] === e.target;
      
      let edgeStyle = {};
      let className = '';
      
      if (isFailed && e.target === failedAtStep && edgeIsOnPath) {
        edgeStyle = { stroke: '#ff4444', strokeWidth: 3 };
        className = 'workflow-edge-failed';
      } else if (isRunning && edgeIsOnPath && e.target === currentStepId) {
        edgeStyle = { stroke: '#ffd700', strokeWidth: 3 };
        className = 'workflow-edge-current';
      } else if (edgeIsOnPath) {
        edgeStyle = { stroke: '#4caf50', strokeWidth: 2.5 };
        className = 'workflow-edge-done';
      } else {
        edgeStyle = { stroke: '#555', strokeWidth: 2 };
        className = 'workflow-edge-pending';
      }
      
      return {
        id: e.id || `${e.source}-${e.target}`,
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle,
        targetHandle: e.targetHandle,
        style: edgeStyle,
        className: className,
      };
    });
  }, [workflow?.edges, run]);

  if (!nodes.length) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: '#888', background: '#1a1a1a', borderRadius: 8 }}>
        Workflow has no nodes.
      </div>
    );
  }

  return (
    <div style={{ height: '100%', minHeight: 200, background: '#1a1a1a', borderRadius: 8, border: '1px solid #444' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypesReadOnly}
        fitView
        fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
        colorMode="dark"
        className="workflow-react-flow-dark"
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        panOnDrag={true}
        zoomOnScroll={true}
        zoomOnPinch={true}
        preventScrolling={false}
      >
        <CenterOnCurrentStep
          currentStepId={run?.currentStepId}
          isRunning={run?.status === 'running'}
        />
        <Controls className="workflow-controls-dark" showInteractive={false} />
        <Background variant={BackgroundVariant.Dots} gap={12} size={1} color="#555" />
      </ReactFlow>
    </div>
  );
}

// --- Typ riadka logu podľa prefixu [Tag] z Python workflow (každý typ = vlastná farba) ---
const LOG_TAG_TO_TYPE = {
  'network': 'network',
  'agent': 'agent',
  'open website': 'open_website',
  'redirect': 'redirect',
  'browser back': 'browser_history_back',
  'breakpoint': 'breakpoint',
  'fill forms': 'fill_forms',
  'click': 'click',
  'wait': 'wait',
  'random delay': 'random_delay',
  'wait for element': 'wait_for_element',
  'console log': 'console_log',
  'python script': 'python_script',
  'run javascript': 'run_javascript',
  'if ai': 'if_ai',
  'ai prompt': 'ai_prompt',
  'if (dom)': 'if_dom',
  'if operator': 'if_operator',
  'if element': 'if_element',
  'if multi dom': 'if_multi_dom',
  'if multi operator': 'if_multi_operator',
  'extract data': 'extract_data',
  'get html': 'get_html',
  'html from selector': 'get_html',
  'call api': 'call_api',
  'call api / webhook': 'call_api',
  'subflow': 'subflow',
  'start': 'start',
  'štart': 'start',
  'snapshot css': 'snapshot',
};

function getLogLineType(line) {
  const t = (line || '').trim();
  if (!t) return 'empty';
  if (/\bChyba\b|chyba:?|Error|error:?/i.test(t)) return 'error';
  if (/\b(yes|áno|OK|success|done|dokončené|hotovo)\b/i.test(t)) return 'success';
  if (/\b(warning|varovanie|pozor|caution)\b/i.test(t)) return 'warning';
  if (/https?:\/\/[^\s]+/.test(t)) return 'url';
  if (/^\d{4}-\d{2}-\d{2}T|\d{2}:\d{2}:\d{2}/.test(t)) return 'timestamp';
  // Extrahuj prvý [Tag] a mapuj na typ
  const bracketMatch = t.match(/^\[([^\]]+)\]/);
  if (bracketMatch) {
    const tag = bracketMatch[1].trim().toLowerCase();
    const exact = LOG_TAG_TO_TYPE[tag];
    if (exact) return exact;
    // čiastočné zhody (napr. "Snapshot CSS:" v texte)
    if (/^snapshot\s*css/i.test(t)) return 'snapshot';
    for (const [key, type] of Object.entries(LOG_TAG_TO_TYPE)) {
      if (tag.includes(key) || key.includes(tag)) return type;
    }
  }
  if (/Snapshot CSS:/i.test(t)) return 'snapshot';
  if (/^\[.+\]/.test(t) || /\[[^\]]+\]/.test(t)) return 'info';  // iný [tag]
  return 'info';
}

// Gradient farby pre každý typ riadka logu (Network, Agent, Open website, Wait, ...) ---
const LOG_LINE_GRADIENTS = {
  empty: { background: 'transparent', minHeight: 4, borderLeft: '4px solid transparent' },
  network: {
    background: 'linear-gradient(135deg, rgba(20, 184, 166, 0.22) 0%, rgba(45, 212, 191, 0.08) 100%)',
    borderLeft: '4px solid rgba(20, 184, 166, 0.65)',
  },
  agent: {
    background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.22) 0%, rgba(167, 139, 250, 0.1) 100%)',
    borderLeft: '4px solid rgba(139, 92, 246, 0.65)',
  },
  open_website: {
    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(147, 197, 253, 0.08) 100%)',
    borderLeft: '4px solid rgba(59, 130, 246, 0.6)',
  },
  redirect: {
    background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.2) 0%, rgba(34, 211, 238, 0.08) 100%)',
    borderLeft: '4px solid rgba(6, 182, 212, 0.6)',
  },
  browser_history_back: {
    background: 'linear-gradient(135deg, rgba(22, 160, 133, 0.2) 0%, rgba(26, 188, 156, 0.08) 100%)',
    borderLeft: '4px solid rgba(22, 160, 133, 0.6)',
  },
  breakpoint: {
    background: 'linear-gradient(135deg, rgba(251, 146, 60, 0.18) 0%, rgba(253, 186, 116, 0.08) 100%)',
    borderLeft: '4px solid rgba(251, 146, 60, 0.6)',
  },
  fill_forms: {
    background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(134, 239, 172, 0.08) 100%)',
    borderLeft: '4px solid rgba(34, 197, 94, 0.55)',
  },
  click: {
    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(129, 140, 248, 0.08) 100%)',
    borderLeft: '4px solid rgba(99, 102, 241, 0.6)',
  },
  wait: {
    background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(252, 211, 77, 0.08) 100%)',
    borderLeft: '4px solid rgba(245, 158, 11, 0.6)',
  },
  random_delay: {
    background: 'linear-gradient(135deg, rgba(217, 119, 6, 0.18) 0%, rgba(253, 186, 116, 0.08) 100%)',
    borderLeft: '4px solid rgba(217, 119, 6, 0.55)',
  },
  wait_for_element: {
    background: 'linear-gradient(135deg, rgba(234, 179, 8, 0.2) 0%, rgba(253, 224, 71, 0.08) 100%)',
    borderLeft: '4px solid rgba(234, 179, 8, 0.6)',
  },
  console_log: {
    background: 'linear-gradient(135deg, rgba(100, 116, 139, 0.2) 0%, rgba(148, 163, 184, 0.08) 100%)',
    borderLeft: '4px solid rgba(100, 116, 139, 0.5)',
  },
  python_script: {
    background: 'linear-gradient(135deg, rgba(55, 65, 81, 0.22) 0%, rgba(107, 114, 128, 0.1) 100%)',
    borderLeft: '4px solid rgba(55, 65, 81, 0.6)',
  },
  run_javascript: {
    background: 'linear-gradient(135deg, rgba(234, 179, 8, 0.18) 0%, rgba(253, 224, 71, 0.08) 100%)',
    borderLeft: '4px solid rgba(234, 179, 8, 0.55)',
  },
  if_ai: {
    background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.2) 0%, rgba(216, 180, 254, 0.08) 100%)',
    borderLeft: '4px solid rgba(168, 85, 247, 0.6)',
  },
  ai_prompt: {
    background: 'linear-gradient(135deg, rgba(167, 139, 250, 0.22) 0%, rgba(196, 181, 253, 0.09) 100%)',
    borderLeft: '4px solid rgba(167, 139, 250, 0.62)',
  },
  if_dom: {
    background: 'linear-gradient(135deg, rgba(192, 132, 252, 0.18) 0%, rgba(216, 180, 254, 0.08) 100%)',
    borderLeft: '4px solid rgba(192, 132, 252, 0.55)',
  },
  if_operator: {
    background: 'linear-gradient(135deg, rgba(167, 139, 250, 0.18) 0%, rgba(196, 181, 253, 0.08) 100%)',
    borderLeft: '4px solid rgba(167, 139, 250, 0.55)',
  },
  if_element: {
    background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.16) 0%, rgba(196, 181, 253, 0.08) 100%)',
    borderLeft: '4px solid rgba(139, 92, 246, 0.5)',
  },
  if_multi_dom: {
    background: 'linear-gradient(135deg, rgba(126, 34, 206, 0.18) 0%, rgba(167, 139, 250, 0.08) 100%)',
    borderLeft: '4px solid rgba(126, 34, 206, 0.55)',
  },
  if_multi_operator: {
    background: 'linear-gradient(135deg, rgba(107, 33, 168, 0.18) 0%, rgba(192, 132, 252, 0.08) 100%)',
    borderLeft: '4px solid rgba(107, 33, 168, 0.55)',
  },
  extract_data: {
    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(52, 211, 153, 0.08) 100%)',
    borderLeft: '4px solid rgba(16, 185, 129, 0.6)',
  },
  get_html: {
    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(52, 211, 153, 0.08) 100%)',
    borderLeft: '4px solid rgba(16, 185, 129, 0.6)',
  },
  call_api: {
    background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.2) 0%, rgba(56, 189, 248, 0.08) 100%)',
    borderLeft: '4px solid rgba(14, 165, 233, 0.6)',
  },
  subflow: {
    background: 'linear-gradient(135deg, rgba(219, 39, 119, 0.2) 0%, rgba(244, 114, 182, 0.08) 100%)',
    borderLeft: '4px solid rgba(219, 39, 119, 0.6)',
  },
  start: {
    background: 'linear-gradient(135deg, rgba(71, 85, 105, 0.2) 0%, rgba(148, 163, 184, 0.08) 100%)',
    borderLeft: '4px solid rgba(71, 85, 105, 0.5)',
  },
  snapshot: {
    background: 'linear-gradient(135deg, rgba(100, 116, 139, 0.15) 0%, rgba(203, 213, 225, 0.06) 100%)',
    borderLeft: '4px solid rgba(100, 116, 139, 0.45)',
  },
  url: {
    background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(134, 239, 172, 0.06) 100%)',
    borderLeft: '4px solid rgba(34, 197, 94, 0.5)',
  },
  error: {
    background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(252, 165, 165, 0.08) 100%)',
    borderLeft: '4px solid rgba(239, 68, 68, 0.7)',
  },
  success: {
    background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.18) 0%, rgba(134, 239, 172, 0.1) 100%)',
    borderLeft: '4px solid rgba(34, 197, 94, 0.5)',
  },
  warning: {
    background: 'linear-gradient(135deg, rgba(234, 179, 8, 0.18) 0%, rgba(253, 224, 71, 0.08) 100%)',
    borderLeft: '4px solid rgba(234, 179, 8, 0.6)',
  },
  timestamp: {
    background: 'linear-gradient(135deg, rgba(148, 163, 184, 0.15) 0%, rgba(203, 213, 225, 0.06) 100%)',
    borderLeft: '4px solid rgba(148, 163, 184, 0.4)',
  },
  info: {
    background: 'linear-gradient(135deg, rgba(100, 116, 139, 0.12) 0%, rgba(148, 163, 184, 0.05) 100%)',
    borderLeft: '4px solid rgba(100, 116, 139, 0.35)',
  },
};

// --- Jednoduché zvýraznenie riadku logu (tagy [xxx], URL, Chyba, áno/OK) ---
function highlightLogLine(line, lineIndex) {
  const segments = [];
  let lastEnd = 0;
  const regex = /(\[[^\]]+\])|(https?:\/\/[^\s]+)|(\bChyba\b|chyba:?|\bError\b|error:?)|(\báno\b|\byes\b|\bOK\b)/gi;
  let m;
  while ((m = regex.exec(line)) !== null) {
    if (m.index > lastEnd) {
      segments.push(<span key={`${lineIndex}-${lastEnd}`}>{line.slice(lastEnd, m.index)}</span>);
    }
    const val = m[0];
    let style = {};
    if (m[1]) style = { color: '#7dd3fc', fontWeight: 500 };
    else if (m[2]) style = { color: '#86efac', wordBreak: 'break-all' };
    else if (m[3]) style = { color: '#fca5a5', fontWeight: 500 };
    else if (m[4]) style = { color: '#86efac', fontWeight: 500 };
    segments.push(<span key={`${lineIndex}-${m.index}`} style={style}>{val}</span>);
    lastEnd = regex.lastIndex;
  }
  if (lastEnd < line.length) {
    segments.push(<span key={`${lineIndex}-tail`}>{line.slice(lastEnd)}</span>);
  }
  return segments.length ? segments : [line];
}

// --- Zlúči viacriadkový log do záznamov: nový záznam len keď riadok začína [Tag] ---
function groupLogLines(lines) {
  if (!lines || lines.length === 0) return [];
  const groups = [];
  let current = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    const startsNewEntry = /^\[[^\]]+\]/.test(trimmed);
    if (startsNewEntry && current.length > 0) {
      groups.push(current);
      current = [line];
    } else {
      current.push(line);
    }
  }
  if (current.length > 0) groups.push(current);
  return groups;
}

// --- Translate Slovak log tags/messages to English for display ---
const LOG_DISPLAY_TRANSLATIONS = [
  [/\[Štart\]/gi, '[Start]'],
  [/\[štart\]/g, '[Start]'],
  [/\[Chyba\]/gi, '[Error]'],
  [/\[chyba\]/g, '[Error]'],
  [/\bChyba:\s*/gi, 'Error: '],
  [/\bChyba\b/g, 'Error'],
  [/\báno\b/gi, 'yes'],
  [/\bnie\b/gi, 'no'],
  [/\bdokončené\b/gi, 'done'],
  [/\bhotovo\b/gi, 'done'],
  [/\bprebieha\b/gi, 'running'],
  [/\bčaká\b/gi, 'waiting'],
  [/\bvarovanie\b/gi, 'warning'],
  [/\bpozor\b/gi, 'caution'],
  [/\bnačítavam\b/gi, 'loading'],
  [/\bspustené\b/gi, 'started'],
  [/\bzmizne\b/gi, 'disappears'],
  [/\bobjaví sa\b/gi, 'appears'],
  [/\bviditeľný\b/gi, 'visible'],
  [/\bneplatí\b/gi, 'false'],
  [/\bplatí\b/gi, 'true'],
];
function translateLogForDisplay(text) {
  if (!text || typeof text !== 'string') return text;
  let out = text;
  for (const [from, to] of LOG_DISPLAY_TRANSLATIONS) {
    out = out.replace(from, to);
  }
  return out;
}

// --- Run log: layout with header and highlighting ---
function RunLogViewer({ logText, error, isFailed, onCopy }) {
  const scrollContainerRef = useRef(null);
  const autoScrollRef = useRef(true);
  const displayText = translateLogForDisplay(logText);
  const lines = (displayText || '').split('\n');
  const entries = useMemo(() => groupLogLines(lines), [lines]);
  const isEmpty = lines.length === 1 && !lines[0].trim();

  useEffect(() => {
    if (!autoScrollRef.current || !scrollContainerRef.current) return;
    const el = scrollContainerRef.current;
    el.scrollTop = el.scrollHeight - el.clientHeight;
  }, [logText]);

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const { scrollTop, clientHeight, scrollHeight } = el;
    const atBottom = scrollHeight - scrollTop - clientHeight < 20;
    autoScrollRef.current = atBottom;
  }, []);

  return (
    <div
      style={{
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        background: '#1e1e1e',
        borderRadius: 12,
        border: '1px solid #333',
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
      }}
    >
      <div
        style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          background: '#252525',
          borderBottom: '1px solid #333',
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 600, color: '#e5e5e5' }}>Run log</span>
        <button
          type="button"
          onClick={() => {
            navigator.clipboard.writeText(logText || '');
            if (onCopy) onCopy();
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 12px',
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 6,
            color: '#b0b0b0',
            fontSize: 13,
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'background 0.15s, border-color 0.15s, color 0.15s',
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.18)';
            e.currentTarget.style.color = '#e0e0e0';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)';
            e.currentTarget.style.color = '#b0b0b0';
          }}
        >
          <MaterialIcon icon="content_copy" size={16} />
          Copy
        </button>
      </div>
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="workflow-dark-scrollbar"
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          padding: '16px 20px',
          fontSize: 13,
          lineHeight: 1.65,
          fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
        }}
      >
        {isEmpty ? (
          <div style={{ color: '#666', fontStyle: 'italic' }}>No entries</div>
        ) : (
          entries.map((entryLines, entryIndex) => {
            const firstLine = entryLines[0] || '';
            const lineType = getLogLineType(firstLine);
            const gradientStyle = LOG_LINE_GRADIENTS[lineType] || LOG_LINE_GRADIENTS.info;
            return (
              <div
                key={entryIndex}
                style={{
                  padding: '8px 12px',
                  marginBottom: 6,
                  borderRadius: 8,
                  borderLeft: gradientStyle.borderLeft || '4px solid transparent',
                  background: gradientStyle.background,
                  minHeight: gradientStyle.minHeight,
                  color: '#d4d4d4',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                }}
              >
                {entryLines.map((line, lineIndex) => (
                  <div key={`${entryIndex}-${lineIndex}`} style={{ marginBottom: lineIndex < entryLines.length - 1 ? 4 : 0 }}>
                    {highlightLogLine(line, `${entryIndex}-${lineIndex}`)}
                  </div>
                ))}
              </div>
            );
          })
        )}
      </div>
      {isFailed && error && (
        <div
          style={{
            padding: '14px 20px',
            background: 'rgba(239,68,68,0.08)',
            borderTop: '1px solid rgba(239,68,68,0.25)',
            color: '#fca5a5',
            fontSize: 13,
          }}
        >
          <span style={{ fontWeight: 600, marginRight: 8 }}>Error:</span>
          {error}
        </div>
      )}
    </div>
  );
}

// --- Názov kroku z workflow nodes (pre históriu) ---
function getStepLabel(workflow, stepId) {
  const node = workflow?.nodes?.find((n) => n.id === stepId);
  const data = node?.data || {};
  return data.label || data.fragmentType || node?.type || stepId;
}

// --- Štatistiky workflow pre zoznam (počet fragmentov, či má AI) ---
// eslint-disable-next-line no-unused-vars
function getWorkflowStats(workflow) {
  const nodes = workflow?.nodes || [];
  const fragmentNodes = nodes.filter((n) => n.type === 'fragment');
  const edges = workflow?.edges || [];
  let hasAi = false;
  for (const n of fragmentNodes) {
    const ft = n.data?.fragmentType || n.data?.type;
    if (ft === 'agent') {
      const p = n.data?.params || {};
      const hasKey = (p.apiKey || '').trim() || (p.aiConnectorId || '').trim();
      if ((p.modelName || '').trim() && hasKey) {
        hasAi = true;
        break;
      }
    }
  }
  return {
    fragmentCount: fragmentNodes.length,
    edgeCount: edges.length,
    hasAi,
  };
}

// --- História spustení: kroky (šedé/žlté/zelené/červené) + log ---
// eslint-disable-next-line no-unused-vars
function RunHistory({ runs, workflow, loading, onRunClick, pagination, onPageChange }) {
  if (loading) {
    return (
      <div style={{ padding: 16, color: '#aaa', minHeight: 60 }}>
        Loading history...
      </div>
    );
  }
  if (!runs?.length) {
    return (
      <div style={{ padding: 16, color: '#aaa', minHeight: 60, lineHeight: 1.5 }}>
        No runs yet. Run the workflow with the <strong>Run</strong> button (on the workflow card or in the builder).
      </div>
    );
  }

  // n8n runs listing is intentionally minimal (no steps/metadata/code). Load full run in detail view.
  if (workflow?.workflowType === 'n8n') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxHeight: '100%', minHeight: 0, overflowY: 'auto' }}>
        {runs.map((run) => {
          const runKey = run._id?.toString?.() ?? run.id ?? run._id ?? 'run';
          const isFailed = run.status === 'failed' || run.status === 'error';
          const statusLabel = run.status === 'running' ? 'Running' : run.status === 'success' ? 'OK' : isFailed ? 'Error' : run.status || '—';
          const statusColor = run.status === 'running' ? '#d4a84b' : run.status === 'success' ? '#4a9b4a' : isFailed ? '#c44' : '#aaa';
          return (
            <div
              key={runKey}
              role={onRunClick ? 'button' : undefined}
              tabIndex={onRunClick ? 0 : undefined}
              onClick={onRunClick ? () => onRunClick(run) : undefined}
              onKeyDown={onRunClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRunClick(run); } } : undefined}
              style={{
                background: '#252525',
                borderRadius: 8,
                border: '1px solid #444',
                overflow: 'hidden',
                cursor: onRunClick ? 'pointer' : undefined,
              }}
            >
              <div style={{ padding: '10px 12px', fontSize: 12, color: '#aaa', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <span>
                  Started: {run.startedAt ? new Date(run.startedAt).toLocaleString() : '—'}
                  {run.finishedAt && ` · Finished: ${new Date(run.finishedAt).toLocaleString()}`}
                </span>
                <span style={{ fontWeight: 700, color: statusColor }}>{statusLabel}</span>
              </div>
              {onRunClick && (
                <div style={{ padding: '8px 12px', borderTop: '1px solid #444', fontSize: 11, color: '#666' }}>
                  Click for detail →
                </div>
              )}
            </div>
          );
        })}
        {pagination && pagination.totalPages > 1 && (
          <div className="workflows-history-pagination" style={{ padding: '12px 0', marginTop: 0, borderTop: 'none' }}>
            <button
              type="button"
              className="pagination-btn"
              onClick={() => onPageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}
            >
              ← Previous
            </button>
            <span className="workflows-history-pagination__info" style={{ fontSize: 13 }}>
              Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
            </span>
            <button
              type="button"
              className="pagination-btn"
              onClick={() => onPageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxHeight: '100%', minHeight: 0, overflowY: 'auto' }}>
      {runs.map((run) => {
        const stepsDone = run.stepsDone || [];
        const currentId = run.currentStepId;
        const isRunning = run.status === 'running';
        const isFailed = run.status === 'failed' || run.status === 'error';
        const failedAtStep = isFailed && stepsDone.length ? stepsDone[stepsDone.length - 1] : null;
        const runKey = run._id?.toString?.() ?? run.id ?? run._id ?? 'run';
        return (
          <div
            key={runKey}
            role={onRunClick ? 'button' : undefined}
            tabIndex={onRunClick ? 0 : undefined}
            onClick={onRunClick ? () => onRunClick(run) : undefined}
            onKeyDown={onRunClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRunClick(run); } } : undefined}
            style={{
              background: '#252525',
              borderRadius: 8,
              border: '1px solid #444',
              overflow: 'hidden',
              cursor: onRunClick ? 'pointer' : undefined,
            }}
          >
            <div style={{ padding: '10px 12px', borderBottom: '1px solid #444', fontSize: 12, color: '#aaa', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <span>
                Started: {run.startedAt ? new Date(run.startedAt).toLocaleString() : '—'}
                {run.finishedAt && ` · Finished: ${new Date(run.finishedAt).toLocaleString()}`}
                <span style={{ marginLeft: 8, fontWeight: 600 }}>
                  {run.status === 'running' && <span style={{ color: '#d4a84b' }}>Running</span>}
                  {run.status === 'success' && <span style={{ color: '#4a9b4a' }}>OK</span>}
                  {(run.status === 'failed' || run.status === 'error') && <span style={{ color: '#c44' }}>Error</span>}
                </span>
              </span>
              {onRunClick && <span style={{ fontSize: 11, color: '#666' }}>Click for log →</span>}
            </div>
            <div style={{ padding: '10px 12px', display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
              {stepsDone.map((stepId, idx) => {
                const isCurrent = stepId === currentId && isRunning;
                const isFailedStep = stepId === failedAtStep;
                let bg = '#555';
                if (isCurrent) bg = '#b8860b';
                else if (isFailedStep) bg = '#8b3a3a';
                else bg = '#2d5a2d';
                const label = getStepLabel(workflow, stepId);
                return (
                  <span
                    key={`${runKey}-${stepId}-${idx}`}
                    style={{
                      padding: '4px 10px',
                      borderRadius: 6,
                      fontSize: 12,
                      background: bg,
                      color: '#fff',
                      display: 'inline-flex',
                      flexDirection: isFailedStep && run.error ? 'column' : 'row',
                      alignItems: isFailedStep && run.error ? 'flex-start' : 'center',
                      gap: 4,
                      maxWidth: 320,
                    }}
                    title={isFailedStep && run.error ? run.error : undefined}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      {isCurrent && (
                        <span className="workflow-step-spinner" style={{ display: 'inline-block', width: 12, height: 12, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'workflow-spin 0.8s linear infinite' }} />
                      )}
                      {label}
                    </span>
                    {isFailedStep && run.error && (
                      <span style={{ fontSize: 11, color: 'rgba(255,200,200,0.95)', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {run.error}
                      </span>
                    )}
                  </span>
                );
              })}
              {isRunning && !currentId && stepsDone.length === 0 && (
                <span style={{ color: '#888', fontSize: 12 }}>Waiting for start...</span>
              )}
            </div>
            {isFailed && (run.error || run.log) && (
              <div
                style={{
                  padding: '10px 12px',
                  background: '#2a1a1a',
                  borderTop: '1px solid #444',
                  fontSize: 12,
                  fontFamily: 'monospace',
                  color: '#e88',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: 120,
                  overflowY: 'auto',
                }}
              >
                <strong>Log / error:</strong> {run.error || run.log}
              </div>
            )}
          </div>
        );
      })}
      {pagination && pagination.totalPages > 1 && (
        <div className="workflows-history-pagination" style={{ padding: '12px 0', marginTop: 0, borderTop: 'none' }}>
          <button
            type="button"
            className="pagination-btn"
            onClick={() => onPageChange(pagination.page - 1)}
            disabled={pagination.page <= 1}
          >
            ← Previous
          </button>
          <span className="workflows-history-pagination__info" style={{ fontSize: 13 }}>
            Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
          </span>
          <button
            type="button"
            className="pagination-btn"
            onClick={() => onPageChange(pagination.page + 1)}
            disabled={pagination.page >= pagination.totalPages}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

// Spočíta, koľkokrát sa daná trieda vyskytuje v celom dokumente (všetky class="..." a class='...')
// eslint-disable-next-line no-unused-vars
function countClassInHtml(html, className) {
  if (!html || typeof html !== 'string' || !className) return 0;
  let count = 0;
  const s = html;
  const sLower = s.toLowerCase();
  const needle = 'class=';
  let i = 0;
  while (i < s.length) {
    const classIdx = sLower.indexOf(needle, i);
    if (classIdx === -1) break;
    let j = classIdx + needle.length;
    while (j < s.length && (s[j] === ' ' || s[j] === '\t' || s[j] === '\n' || s[j] === '\r')) j++;
    if (j >= s.length) break;
    const quote = s[j];
    if (quote !== '"' && quote !== "'") { i = classIdx + 1; continue; }
    j += 1;
    const start = j;
    while (j < s.length && s[j] !== quote) j++;
    const value = s.slice(start, j).trim();
    const classes = value.split(/\s+/).filter(Boolean);
    count += classes.filter((c) => c === className).length;
    i = j + 1;
  }
  return count;
}

// Skript pre snapshot inspect (telo ako funkcia, aby sa neparsoval z template literálu a nevznikali escape chyby)
function snapshotInspectScriptBody() {
  var style = document.createElement('style');
  style.textContent = '.snapshot-inspect-highlight { outline: 2px solid #0af !important; outline-offset: -2px; }';
  (document.head || document.documentElement).appendChild(style);
  var last = null;
  document.addEventListener('mousemove', function(e) {
    var el = e.target;
    if (el === last) return;
    if (last) last.classList.remove('snapshot-inspect-highlight');
    last = el;
    el.classList.add('snapshot-inspect-highlight');
  });
  document.addEventListener('click', function(e) {
    e.preventDefault();
    e.stopPropagation();
    var el = e.target;
    var html = el.outerHTML;
    var styleStr = el.getAttribute('style') || '';
    var styles = [];
    styleStr.split(';').forEach(function(s) {
      var i = s.indexOf(':');
      if (i > 0) { styles.push({ name: s.slice(0, i).trim(), value: s.slice(i + 1).trim() }); }
    });
    var attrs = [];
    for (var i = 0; i < el.attributes.length; i++) {
      var a = el.attributes[i];
      attrs.push({ name: a.name, value: a.value });
    }
    var classesWithCounts = [];
    var cls = el.getAttribute('class');
    if (cls) {
      var fullDoc = '';
      try { fullDoc = document.documentElement ? document.documentElement.outerHTML : ''; } catch (e) {}
      function countInDoc(docStr, className) {
        if (!docStr || !className) return 0;
        var count = 0, i = 0, low = docStr.toLowerCase();
        while (i < docStr.length) {
          var idx = low.indexOf('class=', i);
          if (idx === -1) break;
          var j = idx + 6;
          while (j < docStr.length && (docStr[j] === ' ' || docStr[j] === '\t' || docStr[j] === '\n' || docStr[j] === '\r')) j++;
          if (j >= docStr.length) break;
          var q = docStr[j];
          if (q !== '"' && q !== "'") { i = idx + 1; continue; }
          j++;
          var start = j;
          while (j < docStr.length && docStr[j] !== q) j++;
          var val = docStr.slice(start, j).trim();
          var parts = val.split(/\s+/);
          for (var k = 0; k < parts.length; k++) if (parts[k] === className) count++;
          i = j + 1;
        }
        return count;
      }
      cls.split(/\s+/).filter(Boolean).forEach(function(c) {
        classesWithCounts.push({ class: c, count: countInDoc(fullDoc, c) });
      });
    }
    window.parent.postMessage({
      type: 'SNAPSHOT_ELEMENT_CLICK',
      html: html,
      styles: styles,
      attributes: attrs,
      tagName: el.tagName ? el.tagName.toLowerCase() : '',
      classesWithCounts: classesWithCounts
    }, '*');
  }, true);
}

// Vloží do snapshot HTML skript pre inspect (hover highlight + klik → postMessage s elementom a štýlmi)
function injectSnapshotInspectScript(html) {
  if (!html || typeof html !== 'string') return html;
  const fnStr = snapshotInspectScriptBody.toString();
  const scriptBody = fnStr.slice(fnStr.indexOf('{') + 1, fnStr.lastIndexOf('}'));
  // Split '</script>' to avoid closing the script tag in HTML
  // eslint-disable-next-line no-useless-concat
  const tag = '<script>\n(function() {' + scriptBody + '})();\n<' + '/script>';
  return html.includes('</body>') ? html.replace('</body>', tag + '</body>') : html + tag;
}

// --- Panel nastavení fragmentu (dvojklik) ---
// Z hodnoty atributu (názov + hodnota) zostaví CSS selektor pre snapshot kontextové menu
function buildSelectorFromAttr(attrName, attrValue) {
  if (!attrName || attrValue == null) return '';
  const name = String(attrName).trim();
  const val = String(attrValue).trim();
  if (!val) return '';
  const esc = (s) => s.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  if (name === 'id') return '#' + esc(val);
  if (name === 'class') return '.' + esc(val);
  return '[' + name + '="' + esc(val) + '"]';
}

function FragmentSettingsPanel({ node, onSave, onClose, allNodes, onMoveToGroup, selectedNodeId, workflowId }) {
  const [params, setParams] = useState(() => ({ ...(node?.data?.params || {}) }));
  const [workflowsList, setWorkflowsList] = useState([]);
  const [connectorsList, setConnectorsList] = useState([]);
  const [webhooksList, setWebhooksList] = useState([]);
  const [subflowInputVars, setSubflowInputVars] = useState({ query: [], body: [] });
  const [subflowInputVarsLoading, setSubflowInputVarsLoading] = useState(false);
  const aiPromptFileInputRef = useRef(null);
  const fragmentType = FRAGMENT_TYPES.find((t) => t.id === (node?.data?.fragmentType || node?.data?.type));
  const groups = (allNodes || []).filter((n) => n.type === 'group');

  useEffect(() => {
    setParams({ ...(node?.data?.params || fragmentType?.defaultParams || {}) });
  }, [node?.id, node?.data?.params, fragmentType?.defaultParams]);

  useEffect(() => {
    if ((fragmentType?.id) !== 'agent') return;
    let cancelled = false;
    getAiConnectors()
      .then((list) => { if (!cancelled) setConnectorsList(Array.isArray(list) ? list : []); })
      .catch(() => { if (!cancelled) setConnectorsList([]); });
    return () => { cancelled = true; };
  }, [fragmentType?.id]);

  useEffect(() => {
    if ((fragmentType?.id) !== 'subflow') return;
    let cancelled = false;
    getWorkflows()
      .then((list) => { if (!cancelled) setWorkflowsList(list || []); })
      .catch(() => { if (!cancelled) setWorkflowsList([]); });
    return () => { cancelled = true; };
  }, [fragmentType?.id]);

  // When subflow workflow is selected, fetch its nodes and extract required query/body vars
  useEffect(() => {
    if ((fragmentType?.id) !== 'subflow') return;
    const wfId = (params.workflowId || '').trim();
    if (!wfId) {
      setSubflowInputVars({ query: [], body: [] });
      return;
    }
    let cancelled = false;
    setSubflowInputVarsLoading(true);
    getWorkflowById(wfId)
      .then((w) => {
        if (cancelled) return;
        const vars = extractWorkflowVariablesByType(w);
        setSubflowInputVars({ query: vars.query || [], body: vars.body || [] });
      })
      .catch(() => {
        if (!cancelled) setSubflowInputVars({ query: [], body: [] });
      })
      .finally(() => {
        if (!cancelled) setSubflowInputVarsLoading(false);
      });
    return () => { cancelled = true; };
  }, [fragmentType?.id, params.workflowId]);

  useEffect(() => {
    if ((fragmentType?.id) !== 'call_api') return;
    let cancelled = false;
    getUserWebhooks()
      .then((list) => { if (!cancelled) setWebhooksList(Array.isArray(list) ? list : []); })
      .catch(() => { if (!cancelled) setWebhooksList([]); });
    return () => { cancelled = true; };
  }, [fragmentType?.id]);

  if (!node || !fragmentType) {
    return (
      <div className="workflow-settings-panel">
        <div style={{ padding: 16 }}>Vyber fragment (dvojklik).</div>
        <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
      </div>
    );
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ ...node.data, params });
    onClose();
  };

  const renderFields = () => {
    const { id } = fragmentType;
    if (id === 'agent') {
      return (
        <>
          <div className="form-group">
            <label>API kľúč</label>
            <select
              value={params.aiConnectorId || ''}
              onChange={(e) => setParams((p) => ({ ...p, aiConnectorId: e.target.value || '' }))}
              style={{ minWidth: '100%' }}
            >
              <option value="">— vyberte API kľúč —</option>
              {connectorsList.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.name?.trim() ? c.name : `${c.provider || 'API'} #${c._id.slice(-6)}`}
                </option>
              ))}
            </select>
            <small style={{ color: '#888', display: 'block', marginTop: 4 }}>Kľúče pridáte v podstránke API kľúče.</small>
          </div>
          <div className="form-group">
            <label>Model</label>
            <input value={params.modelName || ''} onChange={(e) => setParams((p) => ({ ...p, modelName: e.target.value }))} placeholder="napr. gpt-4o, gpt-4o-mini" />
          </div>
          <div className="form-group">
            <label>Max tokens</label>
            <input type="number" min={1} max={128000} value={params.maxTokens ?? 2048} onChange={(e) => setParams((p) => ({ ...p, maxTokens: parseInt(e.target.value, 10) || 2048 }))} />
            <small style={{ color: '#888', display: 'block', marginTop: 4 }}>Maximálny počet tokenov v odpovedi (1–128000).</small>
          </div>
          <div className="form-group">
            <label>Temperature</label>
            <input type="number" min={0} max={2} step={0.1} value={params.temperature ?? 0.7} onChange={(e) => setParams((p) => ({ ...p, temperature: parseFloat(e.target.value) }))} />
            <small style={{ color: '#888', display: 'block', marginTop: 4 }}>0 = deterministické, 2 = veľmi náhodné (odporúčané 0.3–0.9).</small>
          </div>
          <div className="form-group">
            <label>Timeout (s)</label>
            <input type="number" value={params.timeout ?? 30} onChange={(e) => setParams((p) => ({ ...p, timeout: parseInt(e.target.value, 10) || 30 }))} />
          </div>
          <div className="form-group">
            <label>Jazyk</label>
            <input value={params.language || 'sk'} onChange={(e) => setParams((p) => ({ ...p, language: e.target.value }))} placeholder="sk" />
          </div>
          <div className="form-group" style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #444' }}>
            <strong style={{ display: 'block', marginBottom: 8 }}>Šetrenie dát (menej prenosov pri načítavaní)</strong>
            <label className={`workflow-checkbox-option ${params.lowBandwidthMode === true ? 'is-checked' : ''}`}>
              <input type="checkbox" checked={params.lowBandwidthMode === true} onChange={(e) => {
                const on = e.target.checked;
                setParams((p) => ({ ...p, lowBandwidthMode: on, disableImages: on, disableFonts: on, blockAds: on, blockMedia: on, blockExternalJs: on }));
              }} />
              <span className="workflow-checkbox-box" aria-hidden />
              <span className="workflow-checkbox-label">Režim šetrenia dát (obrázky, fonty, reklamy, médiá, externý JS vypnuté)</span>
            </label>
            <small style={{ color: '#888', display: 'block', marginTop: 4 }}>Odporúčané pri slabom internete alebo obmedzených dátach.</small>
            <label className={`workflow-checkbox-option ${params.disableImages === true ? 'is-checked' : ''}`} style={{ marginTop: 12, display: 'block' }}>
              <input type="checkbox" checked={params.disableImages === true} onChange={(e) => setParams((p) => ({ ...p, disableImages: e.target.checked }))} />
              <span className="workflow-checkbox-box" aria-hidden />
              <span className="workflow-checkbox-label">Načítať web bez obrázkov</span>
            </label>
            <small style={{ color: '#888', display: 'block', marginTop: 4 }}>Rýchlejšie načítanie, obrázky sa nenačítajú.</small>
            <label className={`workflow-checkbox-option ${params.disableFonts === true ? 'is-checked' : ''}`} style={{ marginTop: 12, display: 'block' }}>
              <input type="checkbox" checked={params.disableFonts === true} onChange={(e) => setParams((p) => ({ ...p, disableFonts: e.target.checked }))} />
              <span className="workflow-checkbox-box" aria-hidden />
              <span className="workflow-checkbox-label">Nenačítavať webové fonty</span>
            </label>
            <small style={{ color: '#888', display: 'block', marginTop: 4 }}>Použijú sa systémové fonty, menej sťahovaných dát.</small>
            <label className={`workflow-checkbox-option ${params.blockAds === true ? 'is-checked' : ''}`} style={{ marginTop: 12, display: 'block' }}>
              <input type="checkbox" checked={params.blockAds === true} onChange={(e) => setParams((p) => ({ ...p, blockAds: e.target.checked }))} />
              <span className="workflow-checkbox-box" aria-hidden />
              <span className="workflow-checkbox-label">Nenačítavať reklamy (Google AdSense, DoubleClick)</span>
            </label>
            <small style={{ color: '#888', display: 'block', marginTop: 4 }}>Blokuje reklamné requesty, menej dát a rýchlejšie stránky.</small>
            <label className={`workflow-checkbox-option ${params.blockMedia === true ? 'is-checked' : ''}`} style={{ marginTop: 12, display: 'block' }}>
              <input type="checkbox" checked={params.blockMedia === true} onChange={(e) => setParams((p) => ({ ...p, blockMedia: e.target.checked }))} />
              <span className="workflow-checkbox-box" aria-hidden />
              <span className="workflow-checkbox-label">Nenačítavať médiá (video, audio)</span>
            </label>
            <small style={{ color: '#888', display: 'block', marginTop: 4 }}>Blokuje videá a audio (YouTube, Vimeo, .mp4, .webm…), výrazne menej dát.</small>
            <label className={`workflow-checkbox-option ${params.blockExternalJs === true ? 'is-checked' : ''}`} style={{ marginTop: 12, display: 'block' }}>
              <input type="checkbox" checked={params.blockExternalJs === true} onChange={(e) => setParams((p) => ({ ...p, blockExternalJs: e.target.checked }))} />
              <span className="workflow-checkbox-box" aria-hidden />
              <span className="workflow-checkbox-label">Nenačítavať externý JS (Google, Facebook, analytics, trackery)</span>
            </label>
            <small style={{ color: '#888', display: 'block', marginTop: 4 }}>Blokuje Google (GTM, GA), Facebook (SDK, Pixel), Hotjar, Mixpanel a pod. Menej dát; niektoré stránky môžu stratiť funkcie.</small>
            <label className={`workflow-checkbox-option ${params.blockScriptsOtherThanMainDomain === true ? 'is-checked' : ''}`} style={{ marginTop: 12, display: 'block' }}>
              <input type="checkbox" checked={params.blockScriptsOtherThanMainDomain === true} onChange={(e) => setParams((p) => ({ ...p, blockScriptsOtherThanMainDomain: e.target.checked }))} />
              <span className="workflow-checkbox-box" aria-hidden />
              <span className="workflow-checkbox-label">Blokovať všetky skripty mimo hlavnej domény</span>
            </label>
            <small style={{ color: '#888', display: 'block', marginTop: 4 }}>Povolí len skripty z tej istej domény ako stránka (vrátane subdomén). Výrazne menej dát; vyžaduje Selenium 4 s CDP.</small>
            <div className="form-group" style={{ marginTop: 12 }}>
              <label style={{ display: 'block', marginBottom: 4 }}>Kompresia HTTP (pre sťahovanie CSS pri snapshote)</label>
              <select
                value={params.acceptEncoding === 'br' ? 'br' : 'deflate'}
                onChange={(e) => setParams((p) => ({ ...p, acceptEncoding: e.target.value }))}
                style={{ minWidth: 160 }}
              >
                <option value="deflate">Deflate (gzip, deflate)</option>
                <option value="br">Brotli (br)</option>
              </select>
              <small style={{ color: '#888', display: 'block', marginTop: 4 }}>Accept-Encoding pri requestoch (napr. inline CSS). Brotli často menšie odpovede.</small>
            </div>
          </div>
          <div className="form-group">
            <label className={`workflow-checkbox-option ${params.autoCloseChrome !== false ? 'is-checked' : ''}`}>
              <input type="checkbox" checked={params.autoCloseChrome !== false} onChange={(e) => setParams((p) => ({ ...p, autoCloseChrome: e.target.checked }))} />
              <span className="workflow-checkbox-box" aria-hidden />
              <span className="workflow-checkbox-label">Automaticky zavrieť Chrome po skončení</span>
            </label>
            <small style={{ color: '#888', display: 'block', marginTop: 4 }}>Vypnuté = okno zostane otvorené, zavrieš ho sám.</small>
          </div>
          <div className="form-group" style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #444' }}>
            <strong style={{ display: 'block', marginBottom: 8 }}>Globálne premenné</strong>
            <p style={{ margin: '0 0 12px', fontSize: 12, color: '#aaa', lineHeight: 1.5 }}>
              Premenné dostupné v celom workflow ako <code>{'{{ global.meno }}'}</code>. Hodnota môže obsahovať <code>{'{{ query.xxx }}'}</code> alebo <code>{'{{ body.xxx }}'}</code>. V Python/JS skriptoch môžete premennú prepísať (napr. <code>context['global']['meno'] = 2</code> alebo návrat <code>{'{{ result: ..., globals: { meno: 2 } }}'}</code>).
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(params.globalVariables || []).map((gv, idx) => (
                <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    placeholder="Názov premennej"
                    value={typeof gv === 'object' ? (gv.name || '') : ''}
                    onChange={(e) => {
                      const list = [...(params.globalVariables || [])];
                      list[idx] = { ...(typeof list[idx] === 'object' ? list[idx] : {}), name: e.target.value };
                      setParams((p) => ({ ...p, globalVariables: list }));
                    }}
                    style={{ width: 160, flexShrink: 0 }}
                  />
                  <input
                    placeholder="Hodnota (môže byť {{ query.x }}, {{ body.x }})"
                    value={typeof gv === 'object' ? (gv.value ?? '') : ''}
                    onChange={(e) => {
                      const list = [...(params.globalVariables || [])];
                      list[idx] = { ...(typeof list[idx] === 'object' ? list[idx] : {}), value: e.target.value };
                      setParams((p) => ({ ...p, globalVariables: list }));
                    }}
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setParams((p) => ({ ...p, globalVariables: (p.globalVariables || []).filter((_, i) => i !== idx) }))}
                    title="Odstrániť"
                  >
                    −
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setParams((p) => ({ ...p, globalVariables: [...(p.globalVariables || []), { name: '', value: '' }] }))}
              >
                + Pridať premennú
              </button>
            </div>
          </div>
          <div className="form-group" style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #444' }}>
            <strong style={{ display: 'block', marginBottom: 8 }}>Proxy</strong>
            <label className={`workflow-checkbox-option ${params.useProxy === true ? 'is-checked' : ''}`}>
              <input type="checkbox" checked={params.useProxy === true} onChange={(e) => setParams((p) => ({ ...p, useProxy: e.target.checked }))} />
              <span className="workflow-checkbox-box" aria-hidden />
              <span className="workflow-checkbox-label">Použiť proxy (host, port, meno, heslo)</span>
            </label>
            {params.useProxy === true && (
              <>
                <div className="form-group" style={{ marginTop: 8 }}>
                  <label>Host</label>
                  <input value={params.proxyHost || ''} onChange={(e) => setParams((p) => ({ ...p, proxyHost: e.target.value }))} placeholder="napr. proxy.example.com" />
                </div>
                <div className="form-group">
                  <label>Port</label>
                  <input type="number" value={params.proxyPort || ''} onChange={(e) => setParams((p) => ({ ...p, proxyPort: e.target.value }))} placeholder="8080" />
                </div>
                <div className="form-group">
                  <label>Meno</label>
                  <input value={params.proxyUsername || ''} onChange={(e) => setParams((p) => ({ ...p, proxyUsername: e.target.value }))} placeholder="používateľské meno" />
                </div>
                <div className="form-group">
                  <label>Heslo</label>
                  <input type="text" value={params.proxyPassword || ''} onChange={(e) => setParams((p) => ({ ...p, proxyPassword: e.target.value }))} placeholder="heslo" />
                </div>
              </>
            )}
          </div>
        </>
      );
    }
    if (id === 'open_website' || id === 'redirect') {
      return (
        <div className="form-group">
          <label>URL</label>
          <input value={params.url || ''} onChange={(e) => setParams((p) => ({ ...p, url: e.target.value }))} placeholder="https://..." />
          <small style={{ color: '#888' }}>
            Podporované premenné: <code>{'{{ query.xxx }}'}</code>, <code>{'{{ body.xxx }}'}</code>, <code>{'{{ global.xxx }}'}</code>, <code>{'{{ previous.input }}'}</code>.
          </small>
        </div>
      );
    }
    if (id === 'browser_history_back') {
      return (
        <div className="form-group">
          <label>Poznámka (voliteľné)</label>
          <input
            value={params.description || ''}
            onChange={(e) => setParams((p) => ({ ...p, description: e.target.value }))}
            placeholder="napr. Návrat na predchádzajúcu stránku"
          />
          <small style={{ color: '#888' }}>Prehliadač vykoná akciu „Späť“ (história).</small>
        </div>
      );
    }
    if (id === 'fill_forms') {
      const fields = Array.isArray(params.fields) ? params.fields : [];
      const updateField = (index, key, value) => {
        setParams((p) => {
          const newFields = [...(p.fields || [])];
          newFields[index] = { ...newFields[index], [key]: value };
          return { ...p, fields: newFields };
        });
      };
      const addField = () => {
        setParams((p) => ({ ...p, fields: [...(p.fields || []), { selector: '', value: '', occurrenceIndex: 1 }] }));
      };
      const removeField = (index) => {
        setParams((p) => ({ ...p, fields: (p.fields || []).filter((_, i) => i !== index) }));
      };
      return (
        <div className="form-group">
          <label>Polia formulára</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {fields.map((f, idx) => {
              const field = typeof f === 'object' ? f : { selector: String(f), value: '', occurrenceIndex: 1 };
              return (
                <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: '#1e1e1e', padding: 10, borderRadius: 6 }}>
                  <div style={{ flex: 2, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <label style={{ fontSize: 10, color: '#888' }}>CSS selektor</label>
                    <input
                      value={field.selector || ''}
                      onChange={(e) => updateField(idx, 'selector', e.target.value)}
                      placeholder='input[name="email"], select#country'
                      style={{ fontFamily: 'monospace', fontSize: 12 }}
                    />
                  </div>
                  <div style={{ flex: 2, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <label style={{ fontSize: 10, color: '#888' }}>Hodnota</label>
                    <input
                      value={field.value ?? ''}
                      onChange={(e) => updateField(idx, 'value', e.target.value)}
                      placeholder='user@example.com, {{ body.email }}'
                      style={{ fontFamily: 'monospace', fontSize: 12 }}
                    />
                  </div>
                  <div style={{ width: 80, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <label style={{ fontSize: 10, color: '#888' }}>Ktorý v poradí</label>
                    <input
                      type="text"
                      value={field.occurrenceIndex !== undefined && field.occurrenceIndex !== null ? String(field.occurrenceIndex) : '1'}
                      onChange={(e) => updateField(idx, 'occurrenceIndex', e.target.value)}
                      placeholder="1"
                      style={{ fontFamily: 'monospace', fontSize: 12, textAlign: 'center' }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeField(idx)}
                    style={{ marginTop: 18, padding: '4px 8px', background: '#3a3a3a', border: 'none', borderRadius: 4, color: '#f87171', cursor: 'pointer', fontSize: 14 }}
                    title="Odstrániť pole"
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
          <button
            type="button"
            onClick={addField}
            style={{ marginTop: 10, padding: '8px 16px', background: '#2563eb', border: 'none', borderRadius: 6, color: '#fff', cursor: 'pointer', fontSize: 12 }}
          >
            + Pridať pole
          </button>
          <div style={{ marginTop: 10, padding: 10, background: '#1a1a1a', borderRadius: 6, fontSize: 11, color: '#aaa', lineHeight: 1.5 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Polia formulára</div>
            <div style={{ marginTop: 8, marginBottom: 4 }}><strong>Podporované typy:</strong></div>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              <li><strong>input</strong> (text, email, password, …) – CSS selektor + text na vloženie. Selektor môže použiť ľubovoľný atribút: <code>name</code>, <code>data-bdd</code>, <code>data-testid</code>, <code>id</code>, … Príklad: <code>input[name="email"]</code>, <code>input[data-bdd="email-field"]</code></li>
              <li><strong>select</strong> – CSS selektor + presný text option, ktorý sa má vybrať. Príklad: <code>select#country</code> + <code>Slovensko</code></li>
              <li><strong>radio</strong> – výber podľa hodnoty (atribút <code>value</code>): selektor skupiny (napr. <code>input[name="gender"]</code>) + hodnota, ktorú má mať vybraný radio.</li>
              <li><strong>checkbox</strong> – selektor konkrétneho checkboxu, hodnota <code>1</code>/<code>true</code>/<code>ano</code> = zaškrtnúť, prázdne alebo <code>0</code> = odškrtnúť.</li>
              <li><strong>textarea</strong> – rovnako ako input, selektor + text.</li>
            </ul>
            <div style={{ marginTop: 8 }}><strong>Ktorý v poradí:</strong> Ak je na stránke viac elementov s rovnakým selektorom, zadajte číslo (1 = prvý, 2 = druhý, …). Môže byť aj premenná: <code>{'{{ query.index }}'}</code>, <code>{'{{ global.index }}'}</code>.</div>
            <div style={{ marginTop: 8 }}>Premenné: <code>{'{{ query.názov }}'}</code> (GET), <code>{'{{ body.názov }}'}</code> (POST), <code>{'{{ global.názov }}'}</code> (globálna z Agenta).</div>
          </div>
        </div>
      );
    }
    if (id === 'click_element') {
      const clickBy = params.clickBy || 'selector';
      return (
        <div className="form-group">
          <label>Spôsob výberu</label>
          <div className="workflow-radio-group workflow-radio-group--row">
            <label className={`workflow-radio-option ${clickBy === 'selector' ? 'is-selected' : ''}`}>
              <input type="radio" name="click_element_by" checked={clickBy === 'selector'} onChange={() => setParams((p) => ({ ...p, clickBy: 'selector' }))} />
              <span className="workflow-radio-dot" aria-hidden />
              <span className="workflow-radio-label">Podľa CSS selektora</span>
            </label>
            <label className={`workflow-radio-option ${clickBy === 'text' ? 'is-selected' : ''}`}>
              <input type="radio" name="click_element_by" checked={clickBy === 'text'} onChange={() => setParams((p) => ({ ...p, clickBy: 'text' }))} />
              <span className="workflow-radio-dot" aria-hidden />
              <span className="workflow-radio-label">Podľa textu v elemente</span>
            </label>
          </div>
          {clickBy === 'selector' ? (
            <>
              <label>CSS selektor elementu</label>
              <input value={params.selector || ''} onChange={(e) => setParams((p) => ({ ...p, selector: e.target.value }))} placeholder="button[data-bdd='submit'], #btn-next, a[href='/login'], [alt='Zavrieť']" />
              <small style={{ color: '#888' }}>CSS selektor – môže použiť ľubovoľný atribút (name, data-bdd, data-testid, alt, aria-label, …).</small>
            </>
          ) : (
            <>
              <label>Text v elemente</label>
              <input value={params.text || ''} onChange={(e) => setParams((p) => ({ ...p, text: e.target.value }))} placeholder="Kupiť, Odoslať, Prihlásiť sa…" />
              <small style={{ color: '#888' }}>Element obsahujúci tento text (tlačidlá, odkazy, atď.).</small>
            </>
          )}
          <div className="form-group" style={{ marginTop: 12 }}>
            <label>Ktorý v poradí (1 = prvý, 2 = druhý, …)</label>
            <input
              type="text"
              value={params.occurrenceIndex !== undefined && params.occurrenceIndex !== null ? String(params.occurrenceIndex) : '1'}
              onChange={(e) => setParams((p) => ({ ...p, occurrenceIndex: e.target.value }))}
              placeholder="1 alebo {{ previous.input }}"
            />
            <small style={{ color: '#888' }}>Číslo alebo <code>{'{{ previous.input }}'}</code>, <code>{'{{ query.xxx }}'}</code>, <code>{'{{ global.xxx }}'}</code>.</small>
          </div>
          <div className="form-group" style={{ marginTop: 12 }}>
            <label>Iframe (voliteľné)</label>
            <input
              value={params.iframeSelector ?? ''}
              onChange={(e) => setParams((p) => ({ ...p, iframeSelector: e.target.value }))}
              placeholder="napr. iframe, iframe#gigya-form"
            />
            <small style={{ color: '#888' }}>Ak je tlačidlo/element vo vnútri iframe (napr. Gigya formulár), zadajte CSS selektor iframe.</small>
          </div>
          <div className="form-group" style={{ marginTop: 12 }}>
            <label className={`workflow-checkbox-option ${params.clickMultiple === true ? 'is-checked' : ''}`}>
              <input
                type="checkbox"
                checked={params.clickMultiple === true}
                onChange={(e) => setParams((p) => ({ ...p, clickMultiple: e.target.checked, ...(e.target.checked ? {} : { clickCount: 1 }) }))}
              />
              <span className="workflow-checkbox-box" aria-hidden />
              <span className="workflow-checkbox-label">Chcem kliknúť na element viackrát</span>
            </label>
          </div>
          {params.clickMultiple === true && (
            <>
              <div className="form-group" style={{ marginTop: 8 }}>
                <label>Koľkokrát kliknúť</label>
                <input
                  type="text"
                  value={params.clickCount !== undefined && params.clickCount !== null ? String(params.clickCount) : '2'}
                  onChange={(e) => setParams((p) => ({ ...p, clickCount: e.target.value }))}
                  placeholder="2 alebo {{ query.clicknum }}"
                />
                <small style={{ color: '#888' }}>Počet klikov (2 = dvojklik). Môže byť <code>{'{{ previous.input }}'}</code> alebo <code>{'{{ query.xxx }}'}</code>.</small>
              </div>
              <div className="form-group" style={{ marginTop: 8 }}>
                <label>Medzera medzi klikmi (ms)</label>
                <input
                  type="text"
                  value={params.clickDelayMs !== undefined && params.clickDelayMs !== null ? String(params.clickDelayMs) : '200'}
                  onChange={(e) => setParams((p) => ({ ...p, clickDelayMs: e.target.value }))}
                  placeholder="200"
                />
                <small style={{ color: '#888' }}>Čas v ms medzi klikmi. Môže byť <code>{'{{ query.xxx }}'}</code>, <code>{'{{ global.xxx }}'}</code>.</small>
              </div>
            </>
          )}
          <div className="form-group" style={{ marginTop: 16 }}>
            <label>When element is not found</label>
            <div className="workflow-radio-group workflow-radio-group--row">
              <label className={`workflow-radio-option ${(params.onFailed || 'fail') === 'fail' ? 'is-selected' : ''}`}>
                <input type="radio" name="click_element_on_failed" checked={(params.onFailed || 'fail') === 'fail'} onChange={() => setParams((p) => ({ ...p, onFailed: 'fail' }))} />
                <span className="workflow-radio-dot" aria-hidden />
                <span className="workflow-radio-label">Stop and snapshot</span>
              </label>
              <label className={`workflow-radio-option ${(params.onFailed || '') === 'decide_by_ai' ? 'is-selected' : ''}`}>
                <input type="radio" name="click_element_on_failed" checked={(params.onFailed || '') === 'decide_by_ai'} onChange={() => setParams((p) => ({ ...p, onFailed: 'decide_by_ai' }))} />
                <span className="workflow-radio-dot" aria-hidden />
                <span className="workflow-radio-label">Decide by AI</span>
              </label>
            </div>
            <small style={{ color: '#888' }}>Stop: workflow stops, a snapshot is saved, and an error is reported. Decide by AI: uses the Agent model to pick the element from the page based on your description, then continues.</small>
          </div>
          {(params.onFailed || '') === 'decide_by_ai' && (
            <>
              <div className="form-group" style={{ marginTop: 12 }}>
                <label>Description for AI (which element to click)</label>
                <input
                  value={params.onFailedAiPrompt ?? ''}
                  onChange={(e) => setParams((p) => ({ ...p, onFailedAiPrompt: e.target.value }))}
                  placeholder={params.description ? `e.g. ${params.description}` : 'e.g. Submit button, Login link'}
                />
                <small style={{ color: '#888' }}>Uses the AI configured in the Agent step. If empty, this fragment’s Description is used.</small>
              </div>
              <div className="form-group" style={{ marginTop: 12 }}>
                <label className={`workflow-checkbox-option ${params.onFailedAutorepair === true ? 'is-checked' : ''}`}>
                  <input
                    type="checkbox"
                    checked={params.onFailedAutorepair === true}
                    onChange={(e) => setParams((p) => ({ ...p, onFailedAutorepair: e.target.checked }))}
                  />
                  <span className="workflow-checkbox-box" aria-hidden />
                  <span className="workflow-checkbox-label">Autorepair: update this fragment with the element AI clicked</span>
                </label>
                <small style={{ color: '#888' }}>When AI successfully clicks, the run will suggest updating this fragment’s selector/text. Apply from the run result.</small>
              </div>
            </>
          )}
        </div>
      );
    }
    if (id === 'wait_time') {
      return (
        <div className="form-group">
          <label>Čas čakania (ms)</label>
          <input
            type="number"
            min={0}
            value={params.ms ?? 1000}
            onChange={(e) => setParams((p) => ({ ...p, ms: parseInt(e.target.value, 10) || 0 }))}
            placeholder="1000"
          />
          <small style={{ color: '#888' }}>Všetky ďalšie kroky sa pozastavia na túto dobu (v milisekundách).</small>
        </div>
      );
    }
    if (id === 'breakpoint') {
      return (
        <>
          <div className="form-group">
            <label>Poznámka (voliteľné)</label>
            <input
              value={params.description || ''}
              onChange={(e) => setParams((p) => ({ ...p, description: e.target.value }))}
              placeholder="napr. Kontrola po prihlásení"
            />
          </div>
          <div style={{ marginTop: 12, padding: 10, background: 'rgba(231, 76, 60, 0.1)', borderRadius: 6, fontSize: 12, color: '#e74c3c', lineHeight: 1.5 }}>
            Beh workflow sa na tomto mieste zastaví a uloží sa snapshot aktuálnej stránky (rovnako ako na konci behu). Ďalšie kroky sa nespustia.
          </div>
        </>
      );
    }
    if (id === 'random_delay') {
      return (
        <div className="form-group">
          <label>Rozsah (ms)</label>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <input
                type="number"
                min={0}
                value={params.minMs ?? 500}
                onChange={(e) => setParams((p) => ({ ...p, minMs: parseInt(e.target.value, 10) || 0 }))}
                placeholder="500"
                style={{ width: '100%' }}
              />
              <small style={{ color: '#666', fontSize: 10 }}>Od</small>
            </div>
            <span style={{ color: '#888', fontSize: 14 }}>–</span>
            <div style={{ flex: 1 }}>
              <input
                type="number"
                min={0}
                value={params.maxMs ?? 2000}
                onChange={(e) => setParams((p) => ({ ...p, maxMs: parseInt(e.target.value, 10) || 0 }))}
                placeholder="2000"
                style={{ width: '100%' }}
              />
              <small style={{ color: '#666', fontSize: 10 }}>Do</small>
            </div>
          </div>
          <small style={{ color: '#888', marginTop: 8, display: 'block' }}>Workflow sa pozastaví na náhodný čas v tomto rozsahu (v milisekundách).</small>
        </div>
      );
    }
    if (id === 'wait_for_element') {
      const selectorType = params.selectorType || 'css';
      const waitType = params.waitType || 'appear';
      const visible = params.visible !== false;
      return (
        <>
          <div className="form-group">
            <label>Typ selektora</label>
            <div className="workflow-radio-group workflow-radio-group--row">
              <label className={`workflow-radio-option ${selectorType === 'css' ? 'is-selected' : ''}`}>
                <input type="radio" name="wait_element_selectorType" checked={selectorType === 'css'} onChange={() => setParams((p) => ({ ...p, selectorType: 'css' }))} />
                <span className="workflow-radio-dot" aria-hidden />
                <span className="workflow-radio-label">CSS selektor</span>
              </label>
              <label className={`workflow-radio-option ${selectorType === 'xpath' ? 'is-selected' : ''}`}>
                <input type="radio" name="wait_element_selectorType" checked={selectorType === 'xpath'} onChange={() => setParams((p) => ({ ...p, selectorType: 'xpath' }))} />
                <span className="workflow-radio-dot" aria-hidden />
                <span className="workflow-radio-label">XPath</span>
              </label>
            </div>
          </div>
          <div className="form-group">
            <label>{selectorType === 'css' ? 'CSS selektor' : 'XPath'}</label>
            <input
              value={params.selector || ''}
              onChange={(e) => setParams((p) => ({ ...p, selector: e.target.value }))}
              placeholder={selectorType === 'css' ? '.loading-spinner, #modal, [data-loading="true"]' : '//div[@class="loading"]'}
            />
            <small style={{ color: '#888' }}>Selektor elementu, na ktorý sa má čakať.</small>
          </div>
          <div className="form-group">
            <label>Typ čakania</label>
            <div className="workflow-radio-group">
              <label className={`workflow-radio-option ${waitType === 'appear' ? 'is-selected' : ''}`}>
                <input type="radio" name="wait_element_waitType" checked={waitType === 'appear'} onChange={() => setParams((p) => ({ ...p, waitType: 'appear' }))} />
                <span className="workflow-radio-dot" aria-hidden />
                <span className="workflow-radio-label">Čaká na objavenie sa elementu</span>
              </label>
              <label className={`workflow-radio-option ${waitType === 'disappear' ? 'is-selected' : ''}`}>
                <input type="radio" name="wait_element_waitType" checked={waitType === 'disappear'} onChange={() => setParams((p) => ({ ...p, waitType: 'disappear' }))} />
                <span className="workflow-radio-dot" aria-hidden />
                <span className="workflow-radio-label">Čaká na zmiznutie elementu</span>
              </label>
            </div>
          </div>
          {waitType === 'appear' && (
            <div className="form-group">
              <label className={`workflow-checkbox-option ${visible ? 'is-checked' : ''}`}>
                <input type="checkbox" checked={visible} onChange={(e) => setParams((p) => ({ ...p, visible: e.target.checked }))} />
                <span className="workflow-checkbox-box" aria-hidden />
                <span className="workflow-checkbox-label">Element musí byť viditeľný</span>
              </label>
              <small style={{ color: '#888', display: 'block', marginTop: 4 }}>If checked, waits for a visible element. Otherwise waits only for presence in DOM (may be hidden).</small>
            </div>
          )}
          <div className="form-group">
            <label>Timeout (sekundy)</label>
            <input
              type="number"
              min={1}
              value={params.timeout ?? 10}
              onChange={(e) => setParams((p) => ({ ...p, timeout: parseInt(e.target.value, 10) || 10 }))}
              placeholder="10"
            />
            <small style={{ color: '#888' }}>Maximálny čas čakania. Po uplynutí timeoutu workflow pokračuje ďalej (alebo zlyhá, ak je to kritické).</small>
          </div>
          <div style={{ marginTop: 16, padding: 10, background: '#1a1a1a', borderRadius: 6, fontSize: 11, color: '#aaa', lineHeight: 1.5 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Použitie:</div>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              <li><strong>Čakanie na loading:</strong> Nastav selektor pre loading spinner a typ čakania na "zmiznutie". Workflow pokračuje až keď loading zmizne.</li>
              <li><strong>Čakanie na element:</strong> Nastav selektor pre element a typ čakania na "objavenie sa". Workflow pokračuje až keď sa element objaví.</li>
              <li><strong>CSS selektory:</strong> <code>.trieda</code>, <code>#id</code>, <code>[data-loading="true"]</code>, <code>button[data-testid="submit"]</code></li>
              <li><strong>XPath:</strong> <code>{'//div[@class="loading"]'}</code>, <code>{'//*[contains(@class, "spinner")]'}</code></li>
            </ul>
          </div>
        </>
      );
    }
    if (id === 'console_log') {
      return (
        <>
          <div className="form-group">
            <label>Premenná na zobrazenie</label>
            <input
              value={params.variable || params.var || ''}
              onChange={(e) => setParams((p) => ({ ...p, variable: e.target.value, var: e.target.value }))}
              placeholder="{{ global.xxx }}, {{ query.xxx }}, {{ body.xxx }}, {{ previous.input }}"
            />
            <small style={{ color: '#888' }}>
              Premenná, ktorá sa zobrazí v logu. Podporované: <code>{'{{ global.xxx }}'}</code>, <code>{'{{ query.xxx }}'}</code>, <code>{'{{ body.xxx }}'}</code>, <code>{'{{ previous.input }}'}</code>.
            </small>
          </div>
          <div style={{ marginTop: 16, padding: 10, background: '#1a1a1a', borderRadius: 6, fontSize: 11, color: '#aaa', lineHeight: 1.5 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Použitie:</div>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              <li>Tento fragment nemá výstupnú hodnotu – len zobrazí premennú v logu behu workflow.</li>
              <li>Užitočné na debugovanie – môžeš vidieť hodnoty premenných v priebehu behu.</li>
              <li>Príklady: <code>{'{{ global.userId }}'}</code>, <code>{'{{ query.page }}'}</code>, <code>{'{{ previous.input }}'}</code></li>
            </ul>
          </div>
        </>
      );
    }
    if (id === 'run_python_script') {
      return (
        <>
          <div className="form-group">
            <label>Python skript</label>
            <CodeEditor
              language="python"
              value={params.script || ''}
              onChange={(code) => setParams((p) => ({ ...p, script: code }))}
              placeholder="# Selenium driver je v premennej driver"
              rows={8}
            />
          </div>
          <div className="form-group">
            <label>AI prompt (voliteľné – generovanie skriptu podľa webu)</label>
            <textarea rows={3} value={params.aiPrompt || ''} onChange={(e) => setParams((p) => ({ ...p, aiPrompt: e.target.value }))} placeholder="Napíš čo má skript urobiť, AI vygeneruje kód podľa DOM" />
          </div>
          <small style={{ color: '#888' }}>V tomto fragmente môžete použiť výstup predchádzajúceho kroku ako <code>{'{{ previous.input }}'}</code>. Výstup do ďalšieho kroku: <code>context['_python_result'] = ...</code>. Globálne premenné (prepisovateľné): <code>context['global']['meno'] = hodnota</code> – potom <code>{'{{ global.meno }}'}</code> v ďalších krokoch.</small>
        </>
      );
    }
    if (id === 'run_javascript') {
      return (
        <>
          <div className="form-group">
            <label>JavaScript (v kontexte stránky)</label>
            <CodeEditor
              language="javascript"
              value={params.script || ''}
              onChange={(code) => setParams((p) => ({ ...p, script: code }))}
              placeholder="return document.querySelector('.price')?.innerText;"
              rows={8}
            />
          </div>
          <small style={{ color: '#888', display: 'block', marginBottom: 8 }}>
            Skript sa spustí v prehliadači (Selenium). Zakázané: eval, new Function, import, require, document.write. Maximálna dĺžka 50 000 znakov.
          </small>
          <small style={{ color: '#888' }}>V tomto fragmente môžete použiť výstup predchádzajúceho kroku (API, JS, extract…) ako <code>{'{{ previous.input }}'}</code> v parametroch. Ak chcete nastaviť globálnu premennú, vráťte <code>{'{ result: ..., globals: { meno: hodnota } }'}</code> – <code>{'{{ global.meno }}'}</code> bude v ďalších krokoch.</small>
        </>
      );
    }
    if (id === 'ai_prompt') {
      const attachments = Array.isArray(params.promptImageAttachments) ? params.promptImageAttachments : [];
      const mergeNewImages = async (fileList) => {
        const files = Array.from(fileList || []).filter((f) => f && f.size > 0);
        if (!files.length) return;
        const additions = [];
        for (const f of files) {
          try {
            additions.push(await fileToPromptImageAttachment(f));
          } catch (err) {
            toast.error(err?.message || String(err));
          }
        }
        if (!additions.length) return;
        setParams((p) => {
          const cur = Array.isArray(p.promptImageAttachments) ? p.promptImageAttachments : [];
          return { ...p, promptImageAttachments: [...cur, ...additions] };
        });
      };
      return (
        <>
          <div className="form-group">
            <label>Prompt</label>
            <textarea
              rows={8}
              value={params.prompt || ''}
              onChange={(e) => setParams((p) => ({ ...p, prompt: e.target.value }))}
              placeholder="Čistý text pre model z agenta (bez DOM kontextu stránky, ak ho backend nepridáva sám)."
              style={{ fontFamily: 'inherit', fontSize: 13, width: '100%', boxSizing: 'border-box' }}
            />
            <small style={{ color: '#888', display: 'block', marginTop: 6 }}>
              Rovnaké premenné ako inde: <code>{'{{ previous.input }}'}</code>, <code>{'{{ global.xxx }}'}</code>, <code>{'{{ query.xxx }}'}</code>, <code>{'{{ body.xxx }}'}</code>. Výstup fragmentu = textová odpoveď modelu (podľa implementácie runnera).
            </small>
          </div>
          <div className="form-group">
            <label>Obrázky k promptu (voliteľné)</label>
            <div
              role="presentation"
              onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                mergeNewImages(e.dataTransfer?.files);
              }}
              onClick={() => aiPromptFileInputRef.current?.click()}
              style={{
                border: '2px dashed #555',
                borderRadius: 8,
                padding: '16px 12px',
                textAlign: 'center',
                color: '#aaa',
                fontSize: 13,
                cursor: 'pointer',
                background: '#1e1e1e',
                lineHeight: 1.45,
              }}
            >
              Presuň sem obrázky (drag & drop) alebo klikni pre výber súborov.
              <div style={{ fontSize: 11, color: '#777', marginTop: 6 }}>PNG, JPEG, GIF, WebP · max 5 MB na súbor</div>
            </div>
            <input
              ref={aiPromptFileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp,.png,.jpg,.jpeg,.gif,.webp"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => {
                mergeNewImages(e.target.files);
                e.target.value = '';
              }}
            />
            {attachments.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 12 }} onClick={(e) => e.stopPropagation()}>
                {attachments.map((att, attIdx) => {
                  const src =
                    att.dataUrl ||
                    (att.base64 ? `data:${att.mimeType || 'image/png'};base64,${att.base64}` : '');
                  if (!src) return null;
                  return (
                    <div
                      key={att.id || `${att.name}-${attIdx}`}
                      style={{
                        position: 'relative',
                        width: 72,
                        height: 72,
                        borderRadius: 6,
                        overflow: 'hidden',
                        border: '1px solid #444',
                        flexShrink: 0,
                      }}
                    >
                      <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                      <button
                        type="button"
                        title="Odstrániť"
                        onClick={(e) => {
                          e.stopPropagation();
                          setParams((p) => {
                            const cur = Array.isArray(p.promptImageAttachments) ? p.promptImageAttachments : [];
                            return { ...p, promptImageAttachments: cur.filter((_, i) => i !== attIdx) };
                          });
                        }}
                        style={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          width: 22,
                          height: 22,
                          padding: 0,
                          lineHeight: '20px',
                          fontSize: 16,
                          border: 'none',
                          borderRadius: 4,
                          cursor: 'pointer',
                          background: 'rgba(0,0,0,0.65)',
                          color: '#fff',
                        }}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            <small style={{ color: '#888', display: 'block', marginTop: 10 }}>
              Obrázky sa v workflow ukladajú len ako dáta pre beh; backend ich má pri vyhodnotení uložiť dočasne na disk, poslať modelu a po odpovedi zmazať (nie dlhodobé úložisko).
            </small>
          </div>
        </>
      );
    }
    if (id === 'if_ai') {
      return (
        <>
          <div className="form-group">
            <label>Prompt (podmienka v prirodzenom jazyku)</label>
            <textarea
              rows={4}
              value={params.prompt || ''}
              onChange={(e) => setParams((p) => ({ ...p, prompt: e.target.value }))}
              placeholder="Napríklad: Na stránke je viditeľné tlačidlo „Kupiť“ alebo hláška „Vypredané“."
              style={{ fontFamily: 'inherit', fontSize: 13 }}
            />
          </div>
          <small style={{ color: '#888' }}>Vetva A = podmienka platí (AI vyhodnotí áno), B = neplatí. Vyhodnotenie podľa DOM stránky.</small>
        </>
      );
    }
    if (id === 'if_operator') {
      const operatorType = params.operatorType || 'equals';
      return (
        <>
          <div className="form-group">
            <label>Zdroj hodnoty (čo sa porovnáva)</label>
            <input
              value={params.source || ''}
              onChange={(e) => setParams((p) => ({ ...p, source: e.target.value }))}
              placeholder="previous.input alebo {{ global.xxx }}, {{ query.xxx }}, {{ body.xxx }}"
            />
            <small style={{ color: '#888' }}>Predvolené: <code>{'{{ previous.input }}'}</code> (výstup predchádzajúceho kroku). Môžeš použiť aj <code>{'{{ global.xxx }}'}</code>, <code>{'{{ query.xxx }}'}</code>, <code>{'{{ body.xxx }}'}</code>.</small>
          </div>
          <div className="form-group">
            <label>Operátor</label>
            <div className="workflow-radio-group">
              <label className={`workflow-radio-option ${operatorType === 'equals' ? 'is-selected' : ''}`}>
                <input type="radio" name="if_operator_type" checked={operatorType === 'equals'} onChange={() => setParams((p) => ({ ...p, operatorType: 'equals' }))} />
                <span className="workflow-radio-dot" aria-hidden />
                <span className="workflow-radio-label">Rovná sa (presne)</span>
              </label>
              <label className={`workflow-radio-option ${operatorType === 'not_equals' ? 'is-selected' : ''}`}>
                <input type="radio" name="if_operator_type" checked={operatorType === 'not_equals'} onChange={() => setParams((p) => ({ ...p, operatorType: 'not_equals' }))} />
                <span className="workflow-radio-dot" aria-hidden />
                <span className="workflow-radio-label">Nerovná sa</span>
              </label>
              <label className={`workflow-radio-option ${operatorType === 'contains' ? 'is-selected' : ''}`}>
                <input type="radio" name="if_operator_type" checked={operatorType === 'contains'} onChange={() => setParams((p) => ({ ...p, operatorType: 'contains' }))} />
                <span className="workflow-radio-dot" aria-hidden />
                <span className="workflow-radio-label">Obsahuje</span>
              </label>
              <label className={`workflow-radio-option ${operatorType === 'in_list' ? 'is-selected' : ''}`}>
                <input type="radio" name="if_operator_type" checked={operatorType === 'in_list'} onChange={() => setParams((p) => ({ ...p, operatorType: 'in_list' }))} />
                <span className="workflow-radio-dot" aria-hidden />
                <span className="workflow-radio-label">Je v zozname (oddelené čiarkou)</span>
              </label>
            </div>
          </div>
          {operatorType === 'in_list' ? (
            <div className="form-group">
              <label>Hodnoty (oddelené čiarkou)</label>
              <input
                value={params.values || ''}
                onChange={(e) => setParams((p) => ({ ...p, values: e.target.value }))}
                placeholder="toto, iné, foo"
              />
              <small style={{ color: '#888' }}>Vetva A = zdroj hodnoty sa (po orezaní) rovná jednej z hodnôt, B = inak. Hodnoty môžu obsahovať <code>{'{{ global.xxx }}'}</code>, <code>{'{{ query.xxx }}'}</code>, <code>{'{{ body.xxx }}'}</code>.</small>
            </div>
          ) : (
            <div className="form-group">
              <label>Hodnota na porovnanie</label>
              <input
                value={params.value || ''}
                onChange={(e) => setParams((p) => ({ ...p, value: e.target.value }))}
                placeholder={operatorType === 'contains' ? 'napr. toto alebo {{ global.xxx }}' : 'toto alebo {{ global.xxx }}'}
              />
              <small style={{ color: '#888' }}>Vetva A = podmienka platí (zdroj hodnoty {operatorType === 'equals' ? '==' : operatorType === 'not_equals' ? '!=' : 'obsahuje'} túto hodnotu), B = neplatí. Hodnota môže obsahovať <code>{'{{ global.xxx }}'}</code>, <code>{'{{ query.xxx }}'}</code>, <code>{'{{ body.xxx }}'}</code>.</small>
            </div>
          )}
          <small style={{ color: '#888', display: 'block', marginTop: 8 }}>Vyhodnotenie podľa výstupu predchádzajúceho kroku (Call API / Webhook, Run JavaScript, Extract data, …).</small>
        </>
      );
    }
    if (id === 'if_fixed') {
      const checkType = params.checkType || 'element';
      return (
        <>
          <div className="form-group">
            <label>Typ kontroly</label>
            <div className="workflow-radio-group">
              <label className={`workflow-radio-option ${checkType === 'element' ? 'is-selected' : ''}`}>
                <input type="radio" name="if_fixed_checkType" checked={checkType === 'element'} onChange={() => setParams((p) => ({ ...p, checkType: 'element' }))} />
                <span className="workflow-radio-dot" aria-hidden />
                <span className="workflow-radio-label">Existencia elementu (element selektor s možnosťou atribútov: .trieda, #id, [name=""], [data-bdd=""], [alt=""], …)</span>
              </label>
              <label className={`workflow-radio-option ${checkType === 'text_in_source' ? 'is-selected' : ''}`}>
                <input type="radio" name="if_fixed_checkType" checked={checkType === 'text_in_source'} onChange={() => setParams((p) => ({ ...p, checkType: 'text_in_source' }))} />
                <span className="workflow-radio-dot" aria-hidden />
                <span className="workflow-radio-label">Existencia textu v zdrojovom kóde stránky</span>
              </label>
              <label className={`workflow-radio-option ${checkType === 'text_in_element' ? 'is-selected' : ''}`}>
                <input type="radio" name="if_fixed_checkType" checked={checkType === 'text_in_element'} onChange={() => setParams((p) => ({ ...p, checkType: 'text_in_element' }))} />
                <span className="workflow-radio-dot" aria-hidden />
                <span className="workflow-radio-label">Existencia textu v konkrétnom elemente</span>
              </label>
            </div>
          </div>
          {(checkType === 'element' || checkType === 'text_in_element') && (
            <div className="form-group">
              <label>{checkType === 'element' ? 'Element selektor (s možnosťou atribútov)' : 'Element selektor (v ktorom hľadať text, s možnosťou atribútov)'}</label>
              <input
                value={params.selector || ''}
                onChange={(e) => setParams((p) => ({ ...p, selector: e.target.value }))}
                placeholder={checkType === 'element' ? '.btn-primary, #modal, [data-bdd="submit"], button[name="send"]' : '.message, [data-bdd="status"]'}
              />
            </div>
          )}
          {(checkType === 'text_in_source' || checkType === 'text_in_element') && (
            <div className="form-group">
              <label>{checkType === 'text_in_source' ? 'Text v zdrojovom kóde' : 'Text v elemente'}</label>
              <input
                value={params.text || ''}
                onChange={(e) => setParams((p) => ({ ...p, text: e.target.value }))}
                placeholder={checkType === 'text_in_source' ? 'Vypredané' : 'Úspešne odoslané'}
              />
            </div>
          )}
          <small style={{ color: '#888' }}>Vetva A = podmienka splnená, B = nesplnená. Kontrola na stránke (DOM).</small>
        </>
      );
    }
    if (id === 'if_multi_dom') {
      const conditions = Array.isArray(params.conditions) ? params.conditions : [];
      const addCondition = () => setParams((p) => ({ ...p, conditions: [...(p.conditions || []), { checkType: 'element', selector: '', text: '' }] }));
      const removeCondition = (i) => setParams((p) => ({ ...p, conditions: (p.conditions || []).filter((_, j) => j !== i) }));
      const moveCondition = (fromIndex, toIndex) => {
        if (fromIndex === toIndex) return;
        setParams((p) => {
          const arr = [...(p.conditions || [])];
          const [item] = arr.splice(fromIndex, 1);
          arr.splice(toIndex, 0, item);
          return { ...p, conditions: arr };
        });
      };
      const setCondition = (i, field, val) => setParams((p) => {
        const arr = [...(p.conditions || [])];
        if (!arr[i]) arr[i] = { checkType: 'element', selector: '', text: '' };
        arr[i] = { ...arr[i], [field]: val };
        return { ...p, conditions: arr };
      });
      const onDragStartCond = (e, idx) => { e.dataTransfer.setData('text/plain', String(idx)); e.dataTransfer.effectAllowed = 'move'; e.target.closest('.condition-card')?.classList.add('condition-dragging'); };
      const onDragEndCond = () => { document.querySelectorAll('.condition-card.condition-dragging').forEach((el) => el.classList.remove('condition-dragging')); };
      const onDragOverCond = (e, toIndex) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; e.currentTarget.classList.add('condition-drop-target'); };
      const onDragLeaveCond = (e) => { e.currentTarget.classList.remove('condition-drop-target'); };
      const onDropCond = (e, toIndex) => {
        e.preventDefault();
        e.currentTarget.classList.remove('condition-drop-target');
        const fromIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
        if (Number.isNaN(fromIndex)) return;
        moveCondition(fromIndex, toIndex);
      };
      return (
        <>
          <div style={{ marginBottom: 8, fontSize: 12, color: '#aaa' }}>Podmienky v poradí (IF, else IF, …). Posledný výstup = ELSE, ak žiadna neplatí. Presuňte za pomoci úchopu na zmenu poradia.</div>
          {conditions.map((c, i) => (
            <div
              key={i}
              className="condition-card"
              style={{ marginBottom: 16, padding: 12, background: '#252525', borderRadius: 8, border: '1px solid #333' }}
              onDragOver={(e) => onDragOverCond(e, i)}
              onDragLeave={onDragLeaveCond}
              onDrop={(e) => onDropCond(e, i)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span
                    draggable
                    onDragStart={(e) => onDragStartCond(e, i)}
                    onDragEnd={onDragEndCond}
                    style={{ cursor: 'grab', color: '#888', padding: '2px 4px', userSelect: 'none' }}
                    title="Presuňte na zmenu poradia"
                  >⋮⋮</span>
                  <strong>Podmienka {i + 1}</strong>
                </span>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => removeCondition(i)} disabled={conditions.length <= 1}>−</button>
              </div>
              <div className="form-group">
                <label>Typ</label>
                <div className="workflow-radio-group">
                  {['element', 'text_in_source', 'text_in_element'].map((ct) => (
                    <label key={ct} className={`workflow-radio-option ${(c.checkType || 'element') === ct ? 'is-selected' : ''}`}>
                      <input type="radio" name={`if_multi_dom_${i}_ct`} checked={(c.checkType || 'element') === ct} onChange={() => setCondition(i, 'checkType', ct)} />
                      <span className="workflow-radio-dot" aria-hidden />
                      <span className="workflow-radio-label">{ct === 'element' ? 'Element' : ct === 'text_in_source' ? 'Text v zdroji' : 'Text v elemente'}</span>
                    </label>
                  ))}
                </div>
              </div>
              {((c.checkType || 'element') === 'element' || (c.checkType || 'element') === 'text_in_element') && (
                <div className="form-group">
                  <label>Selektor</label>
                  <input value={c.selector || ''} onChange={(e) => setCondition(i, 'selector', e.target.value)} placeholder=".btn, #id, [data-x]" />
                </div>
              )}
              {((c.checkType || 'element') === 'text_in_source' || (c.checkType || 'element') === 'text_in_element') && (
                <div className="form-group">
                  <label>Text</label>
                  <input value={c.text || ''} onChange={(e) => setCondition(i, 'text', e.target.value)} placeholder="Hľadaný text" />
                </div>
              )}
            </div>
          ))}
          <button type="button" className="btn btn-secondary btn-sm" onClick={addCondition}>+ Pridať podmienku</button>
          <small style={{ color: '#888', display: 'block', marginTop: 8 }}>Výstupy: 0 = 1. IF, 1 = 2. IF, … , posledný = ELSE.</small>
        </>
      );
    }
    if (id === 'if_multi_operator') {
      const conditions = Array.isArray(params.conditions) ? params.conditions : [];
      const addCondition = () => setParams((p) => ({ ...p, conditions: [...(p.conditions || []), { operatorType: 'equals', value: '', values: '', source: '' }] }));
      const removeCondition = (i) => setParams((p) => ({ ...p, conditions: (p.conditions || []).filter((_, j) => j !== i) }));
      const moveCondition = (fromIndex, toIndex) => {
        if (fromIndex === toIndex) return;
        setParams((p) => {
          const arr = [...(p.conditions || [])];
          const [item] = arr.splice(fromIndex, 1);
          arr.splice(toIndex, 0, item);
          return { ...p, conditions: arr };
        });
      };
      const setCondition = (i, field, val) => setParams((p) => {
        const arr = [...(p.conditions || [])];
        if (!arr[i]) arr[i] = { operatorType: 'equals', value: '', values: '', source: '' };
        arr[i] = { ...arr[i], [field]: val };
        return { ...p, conditions: arr };
      });
      const onDragStartCond = (e, idx) => { e.dataTransfer.setData('text/plain', String(idx)); e.dataTransfer.effectAllowed = 'move'; e.target.closest('.condition-card')?.classList.add('condition-dragging'); };
      const onDragEndCond = () => { document.querySelectorAll('.condition-card.condition-dragging').forEach((el) => el.classList.remove('condition-dragging')); };
      const onDragOverCond = (e, toIndex) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; e.currentTarget.classList.add('condition-drop-target'); };
      const onDragLeaveCond = (e) => { e.currentTarget.classList.remove('condition-drop-target'); };
      const onDropCond = (e, toIndex) => {
        e.preventDefault();
        e.currentTarget.classList.remove('condition-drop-target');
        const fromIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
        if (Number.isNaN(fromIndex)) return;
        moveCondition(fromIndex, toIndex);
      };
      return (
        <>
          <div style={{ marginBottom: 8, fontSize: 12, color: '#aaa' }}>Podmienky v poradí (IF, else IF, …). Posledný výstup = ELSE. Presuňte za pomoci úchopu na zmenu poradia.</div>
          {conditions.map((c, i) => (
            <div
              key={i}
              className="condition-card"
              style={{ marginBottom: 16, padding: 12, background: '#252525', borderRadius: 8, border: '1px solid #333' }}
              onDragOver={(e) => onDragOverCond(e, i)}
              onDragLeave={onDragLeaveCond}
              onDrop={(e) => onDropCond(e, i)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span
                    draggable
                    onDragStart={(e) => onDragStartCond(e, i)}
                    onDragEnd={onDragEndCond}
                    style={{ cursor: 'grab', color: '#888', padding: '2px 4px', userSelect: 'none' }}
                    title="Presuňte na zmenu poradia"
                  >⋮⋮</span>
                  <strong>Podmienka {i + 1}</strong>
                </span>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => removeCondition(i)} disabled={conditions.length <= 1}>−</button>
              </div>
              <div className="form-group">
                <label>Zdroj</label>
                <input value={c.source || ''} onChange={(e) => setCondition(i, 'source', e.target.value)} placeholder="previous.input alebo {{ global.xxx }}" />
              </div>
              <div className="form-group">
                <label>Operátor</label>
                <div className="workflow-radio-group">
                  {['equals', 'not_equals', 'contains', 'in_list'].map((op) => (
                    <label key={op} className={`workflow-radio-option ${(c.operatorType || 'equals') === op ? 'is-selected' : ''}`}>
                      <input type="radio" name={`if_multi_op_${i}`} checked={(c.operatorType || 'equals') === op} onChange={() => setCondition(i, 'operatorType', op)} />
                      <span className="workflow-radio-dot" aria-hidden />
                      <span className="workflow-radio-label">{op === 'equals' ? 'Rovná sa' : op === 'not_equals' ? 'Nerovná sa' : op === 'contains' ? 'Obsahuje' : 'V zozname'}</span>
                    </label>
                  ))}
                </div>
              </div>
              {(c.operatorType || 'equals') === 'in_list' ? (
                <div className="form-group">
                  <label>Hodnoty (oddelené čiarkou)</label>
                  <input value={c.values || ''} onChange={(e) => setCondition(i, 'values', e.target.value)} placeholder="a, b, c" />
                </div>
              ) : (
                <div className="form-group">
                  <label>Hodnota</label>
                  <input value={c.value || ''} onChange={(e) => setCondition(i, 'value', e.target.value)} placeholder="hodnota alebo {{ global.xxx }}" />
                </div>
              )}
            </div>
          ))}
          <button type="button" className="btn btn-secondary btn-sm" onClick={addCondition}>+ Pridať podmienku</button>
          <small style={{ color: '#888', display: 'block', marginTop: 8 }}>Výstupy: 0 = 1. IF, 1 = 2. IF, … , posledný = ELSE. Zdroj môže byť prázdny = previous.input.</small>
        </>
      );
    }
    if (id === 'extract_data') {
      const matchMode = params.matchMode || 'one';
      return (
        <>
          <div className="form-group">
            <label>Počet elementov</label>
            <div className="workflow-radio-group workflow-radio-group--row">
              <label className={`workflow-radio-option ${matchMode === 'one' ? 'is-selected' : ''}`}>
                <input type="radio" name="extract_data_matchMode" checked={matchMode === 'one'} onChange={() => setParams((p) => ({ ...p, matchMode: 'one' }))} />
                <span className="workflow-radio-dot" aria-hidden />
                <span className="workflow-radio-label">Jeden (vybraný v poradí)</span>
              </label>
              <label className={`workflow-radio-option ${matchMode === 'all' ? 'is-selected' : ''}`}>
                <input type="radio" name="extract_data_matchMode" checked={matchMode === 'all'} onChange={() => setParams((p) => ({ ...p, matchMode: 'all' }))} />
                <span className="workflow-radio-dot" aria-hidden />
                <span className="workflow-radio-label">Všetky (všetky s danou triedou/selektorom)</span>
              </label>
            </div>
          </div>
          {matchMode === 'one' && (
            <div className="form-group">
              <label>Ktorý v poradí (1 = prvý, 2 = druhý, …)</label>
              <input
                type="text"
                value={params.occurrenceIndex !== undefined && params.occurrenceIndex !== null ? String(params.occurrenceIndex) : '1'}
                onChange={(e) => setParams((p) => ({ ...p, occurrenceIndex: e.target.value }))}
                placeholder="1 alebo {{ previous.input }}"
              />
              <small style={{ color: '#888' }}>Číslo alebo <code>{'{{ previous.input }}'}</code>, <code>{'{{ query.xxx }}'}</code>, <code>{'{{ global.xxx }}'}</code>.</small>
            </div>
          )}
          {matchMode === 'all' && (
            <div className="form-group">
              <label>Selektor kontajnera (všetky elementy)</label>
              <input value={params.containerSelector || ''} onChange={(e) => setParams((p) => ({ ...p, containerSelector: e.target.value }))} placeholder=".product-card, .row-item, [data-item]" />
              <small style={{ color: '#888' }}>CSS selektor – nájde sa všetky zodpovedajúce elementy; z každého sa potom extrahujú polia podľa štruktúry (relatívne vo vnútri kontajnera).</small>
            </div>
          )}
          <div className="form-group">
            <label>Štruktúra výstupných dát (JSON)</label>
            <textarea rows={6} value={params.structure || ''} onChange={(e) => setParams((p) => ({ ...p, structure: e.target.value }))} placeholder='{"title": ".product-name", "price": ".price"}' style={{ fontFamily: 'monospace', fontSize: 12 }} />
          </div>
          <small style={{ color: '#888' }}>V tomto fragmente môžete použiť výstup predchádzajúceho kroku ako <code>{'{{ previous.input }}'}</code> (napr. v štruktúre). Pri „Všetky“ sú selektory v štruktúre relatívne k každému kontajneru.</small>
        </>
      );
    }
    if (id === 'get_html') {
      return (
        <>
          <div className="form-group">
            <label>CSS selector</label>
            <input
              type="text"
              value={params.selector || ''}
              onChange={(e) => setParams((p) => ({ ...p, selector: e.target.value }))}
              placeholder="body, div#main, .content, [data-section]"
            />
            <small style={{ color: '#888' }}>Same CSS selectors as elsewhere (e.g. body, div[data-id=&apos;2&apos;], .content). Output is the inner HTML of the matched element.</small>
          </div>
          <div className="form-group">
            <label>Occurrence (1 = first, 2 = second, …)</label>
            <input
              type="text"
              value={params.occurrenceIndex !== undefined && params.occurrenceIndex !== null ? String(params.occurrenceIndex) : '1'}
              onChange={(e) => setParams((p) => ({ ...p, occurrenceIndex: e.target.value }))}
              placeholder="1 or {{ previous.input }}"
            />
            <small style={{ color: '#888' }}>When multiple elements match, which one to use. Supports <code>{'{{ previous.input }}'}</code>, <code>{'{{ query.xxx }}'}</code>, <code>{'{{ global.xxx }}'}</code>.</small>
          </div>
        </>
      );
    }
    if (id === 'call_api') {
      const queryParams = Array.isArray(params.queryParams) ? params.queryParams : [];
      const bodyParams = Array.isArray(params.bodyParams) ? params.bodyParams : [];
      const headerParams = Array.isArray(params.headerParams) ? params.headerParams : [];
      const urlSource = params.urlSource || (params.webhookId && params.webhookId.trim() ? 'webhook' : 'url');
      const useWebhook = urlSource === 'webhook';
      const addQuery = () => setParams((p) => ({ ...p, queryParams: [...(p.queryParams || []), { key: '', value: '' }] }));
      const addBody = () => setParams((p) => ({ ...p, bodyParams: [...(p.bodyParams || []), { key: '', value: '' }] }));
      const addHeader = () => setParams((p) => ({ ...p, headerParams: [...(p.headerParams || []), { key: '', value: '' }] }));
      const setQueryAt = (i, field, val) => setParams((p) => {
        const arr = [...(p.queryParams || [])];
        if (!arr[i]) arr[i] = { key: '', value: '' };
        arr[i] = { ...arr[i], [field]: val };
        return { ...p, queryParams: arr };
      });
      const setBodyAt = (i, field, val) => setParams((p) => {
        const arr = [...(p.bodyParams || [])];
        if (!arr[i]) arr[i] = { key: '', value: '' };
        arr[i] = { ...arr[i], [field]: val };
        return { ...p, bodyParams: arr };
      });
      const setHeaderAt = (i, field, val) => setParams((p) => {
        const arr = [...(p.headerParams || [])];
        if (!arr[i]) arr[i] = { key: '', value: '' };
        arr[i] = { ...arr[i], [field]: val };
        return { ...p, headerParams: arr };
      });
      const removeQuery = (i) => setParams((p) => ({ ...p, queryParams: (p.queryParams || []).filter((_, j) => j !== i) }));
      const removeBody = (i) => setParams((p) => ({ ...p, bodyParams: (p.bodyParams || []).filter((_, j) => j !== i) }));
      const removeHeader = (i) => setParams((p) => ({ ...p, headerParams: (p.headerParams || []).filter((_, j) => j !== i) }));

      const auth = params.auth && typeof params.auth === 'object' ? params.auth : { type: 'none' };
      const authType = auth.type || 'none';
      const setAuth = (patch) => setParams((p) => ({ ...p, auth: { ...(p.auth && typeof p.auth === 'object' ? p.auth : { type: 'none' }), ...patch } }));

      return (
        <>
          <div className="form-group">
            <label>Zdroj URL</label>
            <div className="call-api-source-toggle" role="group" aria-label="Zdroj URL">
              <label className={`call-api-source-option ${!useWebhook ? 'active' : ''}`}>
                <input
                  type="radio"
                  name={`call_api_source_${node?.id || 'default'}`}
                  checked={!useWebhook}
                  onChange={() => setParams((p) => ({ ...p, urlSource: 'url', webhookId: '' }))}
                />
                <span className="call-api-source-label">Zadaj URL</span>
              </label>
              <label className={`call-api-source-option ${useWebhook ? 'active' : ''}`}>
                <input
                  type="radio"
                  name={`call_api_source_${node?.id || 'default'}`}
                  checked={useWebhook}
                  onChange={() => setParams((p) => ({
                    ...p,
                    urlSource: 'webhook',
                    webhookId: '',
                    /* url ponecháme – pri prepnutí späť na „Zadaj URL“ sa zobrazí */
                  }))}
                />
                <span className="call-api-source-label">Vyber webhook</span>
              </label>
            </div>
            {!useWebhook ? (
              <>
                <label style={{ display: 'block', marginTop: 8 }}>URL</label>
                <input value={params.url || ''} onChange={(e) => setParams((p) => ({ ...p, url: e.target.value }))} placeholder="https://api.example.com/endpoint" />
                <small style={{ color: '#888' }}>Môže obsahovať {'{{ query.xxx }}'}, {'{{ body.xxx }}'}, {'{{ global.xxx }}'} alebo {'{{ previous.input }}'} (výstup predchádzajúceho kroku).</small>
              </>
            ) : (
              <>
                <label style={{ display: 'block', marginTop: 8 }}>Webhook</label>
                <select
                  value={params.webhookId || ''}
                  onChange={(e) => {
                    const id = e.target.value;
                    const wh = webhooksList.find((w) => w._id === id);
                    setParams((p) => ({ ...p, webhookId: id, url: wh ? (wh.url || '') : '' }));
                  }}
                  style={{ width: '100%', padding: '8px 12px', background: '#2a2a2a', border: '1px solid #444', borderRadius: 6, color: '#fff' }}
                >
                  <option value="">— vyberte webhook —</option>
                  {webhooksList.map((w) => (
                    <option key={w._id} value={w._id}>{w.name || w.description || w.url?.slice(0, 50) || w._id}</option>
                  ))}
                </select>
                {webhooksList.length === 0 && <small style={{ color: '#888' }}>Nemáte žiadne webhooks. Pridajte ich v sekcii Webhooks.</small>}
              </>
            )}
          </div>
          <div className="form-group">
            <label>Metóda</label>
            <select value={params.method || 'GET'} onChange={(e) => setParams((p) => ({ ...p, method: e.target.value }))}>
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="PATCH">PATCH</option>
              <option value="DELETE">DELETE</option>
            </select>
          </div>

          <div className="form-group" style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #444' }}>
            <label>Autorizácia</label>
            <select
              value={authType}
              onChange={(e) => setAuth({ type: e.target.value })}
              style={{ width: '100%', padding: '8px 12px', background: '#2a2a2a', border: '1px solid #444', borderRadius: 6, color: '#fff' }}
            >
              <option value="none">No auth</option>
              <option value="api_key">API key</option>
              <option value="bearer">Bearer token</option>
              <option value="jwt_bearer">JWT bearer</option>
              <option value="basic">Basic auth</option>
            </select>

            {authType === 'api_key' && (
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 4 }}>Názov headeru</label>
                  <input
                    value={auth.apiKeyHeaderName || 'X-API-Key'}
                    onChange={(e) => setAuth({ apiKeyHeaderName: e.target.value })}
                    placeholder="X-API-Key"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 4 }}>API key</label>
                  <input
                    value={auth.apiKey || ''}
                    onChange={(e) => setAuth({ apiKey: e.target.value })}
                    placeholder="hodnota alebo {{ global.xxx }}"
                  />
                </div>
              </div>
            )}

            {(authType === 'bearer' || authType === 'jwt_bearer') && (
              <div style={{ marginTop: 8 }}>
                <label style={{ display: 'block', marginBottom: 4 }}>Token</label>
                <input
                  value={auth.token || ''}
                  onChange={(e) => setAuth({ token: e.target.value })}
                  placeholder="hodnota alebo {{ global.xxx }}"
                />
                <small style={{ color: '#888' }}>Posiela sa v headri <code>Authorization</code> ako <code>Bearer token</code>.</small>
              </div>
            )}

            {authType === 'basic' && (
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 4 }}>Username</label>
                  <input
                    value={auth.username || ''}
                    onChange={(e) => setAuth({ username: e.target.value })}
                    placeholder="hodnota alebo {{ global.xxx }}"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 4 }}>Password</label>
                  <input
                    type="password"
                    value={auth.password || ''}
                    onChange={(e) => setAuth({ password: e.target.value })}
                    placeholder="hodnota alebo {{ global.xxx }}"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Headers</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {headerParams.map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input placeholder="názov" value={item.key || ''} onChange={(e) => setHeaderAt(i, 'key', e.target.value)} style={{ width: 160, flexShrink: 0 }} />
                  <input placeholder="hodnota" value={item.value ?? ''} onChange={(e) => setHeaderAt(i, 'value', e.target.value)} style={{ flex: 1 }} />
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => removeHeader(i)} title="Odstrániť">−</button>
                </div>
              ))}
              <button type="button" className="btn btn-secondary btn-sm" onClick={addHeader}>+ Pridať header</button>
            </div>
            <small style={{ color: '#888' }}>Hodnoty môžu obsahovať {'{{ query.xxx }}'}, {'{{ body.xxx }}'}, {'{{ global.xxx }}'} alebo {'{{ previous.input }}'}.</small>
          </div>

          <div className="form-group">
            <label>Query parametre (GET)</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {queryParams.map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input placeholder="názov" value={item.key || ''} onChange={(e) => setQueryAt(i, 'key', e.target.value)} style={{ width: 120, flexShrink: 0 }} />
                  <input placeholder="hodnota" value={item.value ?? ''} onChange={(e) => setQueryAt(i, 'value', e.target.value)} style={{ flex: 1 }} />
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => removeQuery(i)} title="Odstrániť">−</button>
                </div>
              ))}
              <button type="button" className="btn btn-secondary btn-sm" onClick={addQuery}>+ Pridať query parameter</button>
            </div>
          </div>
          <div className="form-group">
            <label>Body parametre (POST/PUT/PATCH – JSON)</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {bodyParams.map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input placeholder="kľúč" value={item.key || ''} onChange={(e) => setBodyAt(i, 'key', e.target.value)} style={{ width: 120, flexShrink: 0 }} />
                  <input placeholder="hodnota" value={item.value ?? ''} onChange={(e) => setBodyAt(i, 'value', e.target.value)} style={{ flex: 1 }} />
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => removeBody(i)} title="Odstrániť">−</button>
                </div>
              ))}
              <button type="button" className="btn btn-secondary btn-sm" onClick={addBody}>+ Pridať body parameter</button>
            </div>
            <small style={{ color: '#888' }}>Hodnoty môžu obsahovať {'{{ query.xxx }}'}, {'{{ body.xxx }}'}, {'{{ global.xxx }}'} alebo {'{{ previous.input }}'} (výstup predchádzajúceho kroku).</small>
          </div>
        </>
      );
    }
    if (id === 'subflow') {
      const currentId = (params.workflowId || '').trim();
      const queryVars = subflowInputVars.query || [];
      const bodyVars = subflowInputVars.body || [];
      const hasInputVars = queryVars.length > 0 || bodyVars.length > 0;
      const subflowQueryParams = params.subflowQueryParams || {};
      const subflowBodyParams = params.subflowBodyParams || {};
      const varPlaceholder = '{{ previous.input }}, {{ global.xxx }}, {{ query.xxx }}, {{ body.xxx }}';
      return (
        <>
          <div className="form-group">
            <label>Workflow (subflow)</label>
            <select
              value={currentId}
              onChange={(e) => {
                const id = e.target.value;
                const w = workflowsList.find((x) => x._id === id);
                setParams((p) => ({
                  ...p,
                  workflowId: id,
                  workflowName: w ? (w.name || '') : '',
                }));
              }}
              style={{ width: '100%', padding: '8px 12px', background: '#2a2a2a', border: '1px solid #444', borderRadius: 6, color: '#fff' }}
            >
              <option value="">— select workflow —</option>
              {workflowsList.map((w) => (
                <option key={w._id} value={w._id}>{w.name || w._id}</option>
              ))}
            </select>
            <small style={{ color: '#888', display: 'block', marginTop: 6 }}>
              The selected workflow runs in the same context (same browser, variables). Do not use circular references (A → B → A).
            </small>
          </div>
          {subflowInputVarsLoading && (
            <div className="form-group" style={{ color: '#888', fontSize: 12 }}>Checking workflow for required inputs…</div>
          )}
          {!subflowInputVarsLoading && hasInputVars && (
            <>
              <div className="form-group" style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #444' }}>
                <label style={{ fontWeight: 600 }}>Inputs for subflow</label>
                <small style={{ color: '#888', display: 'block', marginBottom: 8 }}>
                  The called workflow uses these query/body variables. You can pass values or use variables (e.g. {'{{ previous.input }}'}, {'{{ global.xxx }}'}).
                </small>
              </div>
              {queryVars.length > 0 && (
                <div className="form-group">
                  <label>Query parameters</label>
                  {queryVars.map((varName) => (
                    <div key={`q-${varName}`} style={{ marginBottom: 8 }}>
                      <input
                        type="text"
                        value={subflowQueryParams[varName] ?? ''}
                        onChange={(e) => setParams((p) => ({
                          ...p,
                          subflowQueryParams: { ...(p.subflowQueryParams || {}), [varName]: e.target.value },
                        }))}
                        placeholder={varPlaceholder}
                        style={{ width: '100%', padding: '6px 10px', background: '#2a2a2a', border: '1px solid #444', borderRadius: 4, color: '#fff', fontSize: 13 }}
                      />
                      <span style={{ fontSize: 11, color: '#888' }}>query.{varName}</span>
                    </div>
                  ))}
                </div>
              )}
              {bodyVars.length > 0 && (
                <div className="form-group">
                  <label>Body parameters</label>
                  {bodyVars.map((varName) => (
                    <div key={`b-${varName}`} style={{ marginBottom: 8 }}>
                      <input
                        type="text"
                        value={subflowBodyParams[varName] ?? ''}
                        onChange={(e) => setParams((p) => ({
                          ...p,
                          subflowBodyParams: { ...(p.subflowBodyParams || {}), [varName]: e.target.value },
                        }))}
                        placeholder={varPlaceholder}
                        style={{ width: '100%', padding: '6px 10px', background: '#2a2a2a', border: '1px solid #444', borderRadius: 4, color: '#fff', fontSize: 13 }}
                      />
                      <span style={{ fontSize: 11, color: '#888' }}>body.{varName}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      );
    }
    return null;
  };

  return (
    <div className="workflow-settings-panel">
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ padding: 16, borderBottom: '1px solid #444', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <h3 style={{ margin: 0 }}>Nastavenia: {fragmentType.label}</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" className="btn btn-primary btn-sm">Save</button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
          </div>
        </div>
        <div style={{ padding: 16, flex: 1, overflowY: 'auto' }}>
          <div className="form-group">
            <label>Popis (voliteľný)</label>
            <input
              value={params.description || ''}
              onChange={(e) => setParams((p) => ({ ...p, description: e.target.value }))}
              placeholder="Krátky popis kroku – zobrazí sa na karte fragmentu"
              style={{ width: '100%' }}
            />
          </div>
          {renderFields()}
          {groups.length > 0 && typeof onMoveToGroup === 'function' && selectedNodeId && (
            <div className="form-group" style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #444' }}>
              <label style={{ fontSize: 13, marginBottom: 8 }}>Visual subflow</label>
              <div className="workflow-radio-group" style={{ gap: 4 }}>
                <label className={`workflow-radio-option ${!node?.parentId ? 'is-selected' : ''}`} style={{ padding: '6px 8px', fontSize: 12 }}>
                  <input
                    type="radio"
                    name="visual_subflow_group"
                    checked={!node?.parentId}
                    onChange={() => {
                      onMoveToGroup(selectedNodeId, null);
                    }}
                  />
                  <span className="workflow-radio-dot" aria-hidden />
                  <span className="workflow-radio-label">— žiadna (mimo skupiny) —</span>
                </label>
                {groups.map((g) => (
                  <label
                    key={g.id}
                    className={`workflow-radio-option ${node?.parentId === g.id ? 'is-selected' : ''}`}
                    style={{ padding: '6px 8px' }}
                  >
                    <input
                      type="radio"
                      name="visual_subflow_group"
                      checked={node?.parentId === g.id}
                      onChange={() => {
                        onMoveToGroup(selectedNodeId, g.id);
                      }}
                    />
                    <span className="workflow-radio-dot" aria-hidden />
                    <span className="workflow-radio-label">{g.data?.label || g.id}</span>
                  </label>
                ))}
              </div>
              <small style={{ color: '#888', display: 'block', marginTop: 6, fontSize: 11 }}>
                Fragment bude vizuálne vnútri vybranej skupiny (parentId + extent: parent).
              </small>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}

// --- Plátno workflow + React Flow ---
const initialNodes = [{ id: 'agent-0', type: 'fragment', position: { x: 0, y: 0 }, data: { fragmentType: 'agent', label: 'Agent', params: FRAGMENT_TYPES[0].defaultParams }, className: getOutlineClassNameForNode({ type: 'fragment', data: { fragmentType: 'agent' } }) }];
const initialEdges = [];

// Payload pre beh: len fragment uzly (skupiny sa nevykonávajú)
function buildRunPayload(nodes, edges) {
  const fragmentNodes = (nodes || []).filter((n) => n.type === 'fragment');
  return {
    nodes: fragmentNodes.map((n) => {
      const ft = n.data?.fragmentType || n.type || 'default';
      const defaultParams = FRAGMENT_TYPES.find((f) => f.id === ft)?.defaultParams || {};
      const params = { ...defaultParams, ...(n.data?.params || {}) };
      return {
        id: n.id,
        type: ft,
        position: n.position,
        data: { fragmentType: n.data?.fragmentType || ft, label: n.data?.label, params },
      };
    }),
    edges: (edges || []).map((e) => ({ id: e.id, source: e.source, target: e.target, sourceHandle: e.sourceHandle, targetHandle: e.targetHandle })),
  };
}

// Normalizácia uzla pre snapshot (iba polia ktoré ukladáme – bez React Flow internál)
function normalizeNodeForSnapshot(n) {
  // Pre skupiny normalizuj style - iba width a height
  // NodeResizer aktualizuje width a height priamo na uzle, nie v style
  let normalizedStyle = undefined;
  if (n.type === 'group') {
    // Skontroluj, či má uzel width a height vlastnosti (z NodeResizer)
    const width = typeof n.width === 'number' ? n.width : 
                 (typeof n.width === 'string' ? parseFloat(n.width) : null);
    const height = typeof n.height === 'number' ? n.height : 
                  (typeof n.height === 'string' ? parseFloat(n.height) : null);
    
    // Ak má width/height priamo na uzle, použij ich
    if (width !== null && !isNaN(width) && width > 0 && 
        height !== null && !isNaN(height) && height > 0) {
      normalizedStyle = { width, height };
    } else if (n.style && typeof n.style === 'object' && !Array.isArray(n.style)) {
      // Ak nemá width/height priamo, skús style
      if ('width' in n.style && 'height' in n.style && 
          typeof n.style.width === 'number' && typeof n.style.height === 'number') {
        normalizedStyle = { width: n.style.width, height: n.style.height };
      }
    }
  } else if (n.style && typeof n.style === 'object' && Object.keys(n.style).length > 0) {
    // Pre ostatné uzly zachovaj style, ak existuje
    normalizedStyle = n.style;
  }
  
  return {
    id: n.id,
    type: n.type,
    position: n.position || { x: 0, y: 0 },
    data: n.data,
    style: normalizedStyle,
    parentId: n.parentId,
    extent: n.extent,
    className: n.className,
  };
}
function normalizeEdgeForSnapshot(e) {
  return { id: e.id, source: e.source, target: e.target, sourceHandle: e.sourceHandle, targetHandle: e.targetHandle };
}

// Snapshot celého stavu (vrátane skupín) pre porovnanie uložených zmien
function payloadSnapshot(name, nodes, edges) {
  const sortById = (a, b) => (a.id || '').localeCompare(b.id || '');
  const normNodes = (nodes || []).map(normalizeNodeForSnapshot).sort(sortById);
  const normEdges = (edges || []).map(normalizeEdgeForSnapshot).sort((a, b) => (a.id || '').localeCompare(b.id || ''));
  return JSON.stringify({ name: name || '', nodes: normNodes, edges: normEdges });
}

// Vráti fragment, pod ktorý sa doplní phantom „Last snapshot“ (sink alebo najnižší fragment)
function findOwnerFragmentForSnapshot(nodes, edges) {
  const fragments = (nodes || []).filter((n) => n.type === 'fragment');
  if (fragments.length === 0) return null;
  const sourceIds = new Set((edges || []).map((e) => e.source));
  const sinks = fragments.filter((n) => !sourceIds.has(n.id));
  const candidates = sinks.length > 0 ? sinks : fragments;
  return candidates.reduce((a, b) => ((a.position?.y ?? 0) >= (b.position?.y ?? 0) ? a : b));
}

// Absolútna pozícia uzla vo flow (ak má parentId, sčíta pozície po reťazci parentov)
function getFlowPosition(nodes, nodeId) {
  const node = (nodes || []).find((n) => n.id === nodeId);
  if (!node) return { x: 0, y: 0 };
  const pos = node.position || { x: 0, y: 0 };
  if (!node.parentId) return { x: pos.x ?? 0, y: pos.y ?? 0 };
  const parentPos = getFlowPosition(nodes, node.parentId);
  return { x: (pos.x ?? 0) + parentPos.x, y: (pos.y ?? 0) + parentPos.y };
}

function FlowCanvas({ workflowId, workflow, onSave, onClose, onPayloadChange, runWorkflowForBuilder, setRunModal, setRunForm, runningRunId, runningRunData, onOpenRunDetail, onPublish, onUnpublish }) {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [name, setName] = useState(workflow?.name || 'New Workflow');
  const [saving, setSaving] = useState(false);
  const [savedJustNow, setSavedJustNow] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [undoStackLength, setUndoStackLength] = useState(0);
  const [redoStackLength, setRedoStackLength] = useState(0);
  const [snapshotModalOpen, setSnapshotModalOpen] = useState(false);
  const [snapshotHtml, setSnapshotHtml] = useState(null);
  const [, setSnapshotLoading] = useState(false);
  const [, setSnapshotError] = useState(null);
  const [snapshotSelectedElement, setSnapshotSelectedElement] = useState(null);
  const [snapshotPanelHtmlExpanded, setSnapshotPanelHtmlExpanded] = useState(false);
  const [snapshotContextMenu, setSnapshotContextMenu] = useState(null);
  const [snapshotModalSlideIn, setSnapshotModalSlideIn] = useState(false);
  const [snapshotModalExiting, setSnapshotModalExiting] = useState(false);
  const [lastSnapshotMeta, setLastSnapshotMeta] = useState(null);
  const [runStepDisplayLabel, setRunStepDisplayLabel] = useState('');
  const [runStepExitingLabel, setRunStepExitingLabel] = useState(null);
  const runStepExitTimeoutRef = useRef(null);
  const runStepPendingLabelsRef = useRef([]);
  const runStepLastShownCountRef = useRef(0);
  const runStepDisplayLabelRef = useRef('');
  const [fragmentsPanelCollapsed, setFragmentsPanelCollapsed] = useState(false);
  const [fragmentsPanelTooltip, setFragmentsPanelTooltip] = useState(null); // { text, left, top } – okamžitý tooltip v collapsed režime
  const store = useStoreApi();
  const { fitView, screenToFlowPosition } = useReactFlow();
  const flowWrapperRef = useRef(null);
  const lastSyncedRef = useRef(null);
  const lastSavedSnapshotRef = useRef(null);
  const expectedSnapshotAfterLoadRef = useRef(null);
  const nodesChangeInProgressRef = useRef(false);
  // Undo/Redo história
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const isUndoRedoRef = useRef(false);
  const dragStartTrackingRef = useRef(new Set());
  const resizeStartTrackingRef = useRef(new Set());

  useEffect(() => {
    lastSyncedRef.current = null;
    lastSavedSnapshotRef.current = null;
    expectedSnapshotAfterLoadRef.current = null;
  }, [workflowId]);

  useEffect(() => {
    if (!workflowId) {
      setLastSnapshotMeta(null);
      return;
    }
    let cancelled = false;
    getLastSnapshot(workflowId)
      .then((meta) => { if (!cancelled) setLastSnapshotMeta(meta); })
      .catch(() => { if (!cancelled) setLastSnapshotMeta(null); });
    return () => { cancelled = true; };
  }, [workflowId]);

  // Keď beh spustený z editora skončí (runningRunId prejde na null), znovu načítame last snapshot
  // aby sa ikona posunula k fragmentu, pri ktorom beh skončil
  const prevRunningRunIdRef = useRef(null);
  useEffect(() => {
    const prev = prevRunningRunIdRef.current;
    prevRunningRunIdRef.current = runningRunId;
    if (workflowId && prev != null && runningRunId === null) {
      getLastSnapshot(workflowId)
        .then((meta) => setLastSnapshotMeta(meta))
        .catch(() => setLastSnapshotMeta(null));
    }
  }, [workflowId, runningRunId]);

  // Ref s aktuálnym zobrazeným textom (pre drain)
  runStepDisplayLabelRef.current = runStepDisplayLabel;

  // Text v tlačidle „Prebieha”: postupne všetky kroky (stepsDone + currentStepId), každý s animáciou
  useEffect(() => {
    if (!runningRunId) {
      setRunStepDisplayLabel('');
      setRunStepExitingLabel(null);
      runStepPendingLabelsRef.current = [];
      runStepLastShownCountRef.current = 0;
      if (runStepExitTimeoutRef.current) {
        clearTimeout(runStepExitTimeoutRef.current);
        runStepExitTimeoutRef.current = null;
      }
      return;
    }

    const stepsDone = runningRunData?.stepsDone ?? [];
    const currentStepId = runningRunData?.currentStepId;
    const path = [...stepsDone, currentStepId].filter(Boolean);
    const pathLen = path.length;

    // Do fronty pridáme len nové kroky (ktoré sme ešte nezařadili)
    if (pathLen > runStepLastShownCountRef.current) {
      for (let i = runStepLastShownCountRef.current; i < pathLen; i++) {
        const label = getStepLabel(workflow, path[i]);
        runStepPendingLabelsRef.current.push(label);
      }
      runStepLastShownCountRef.current = pathLen;
    }

    // Ak ešte nemáme žiadny zobrazený text: buď "Spúšťa sa...", alebo prvý z fronty (potom drain zobrazí zvyšok)
    if (runStepDisplayLabel === '') {
      if (runStepPendingLabelsRef.current.length === 0) {
        setRunStepDisplayLabel(pathLen ? getStepLabel(workflow, path[pathLen - 1]) : 'Spúšťa sa...');
        if (pathLen) runStepLastShownCountRef.current = pathLen;
        return;
      }
      const first = runStepPendingLabelsRef.current.shift();
      setRunStepDisplayLabel(first);
    }

    const drain = (afterTimeout = false) => {
      if (!afterTimeout && runStepExitingLabel != null) return;
      const queue = runStepPendingLabelsRef.current;
      if (queue.length === 0) return;
      const nextLabel = queue.shift();
      const currentDisplay = runStepDisplayLabelRef.current;
      if (runStepExitTimeoutRef.current) clearTimeout(runStepExitTimeoutRef.current);
      setRunStepExitingLabel(currentDisplay);
      setRunStepDisplayLabel(nextLabel);
      runStepExitTimeoutRef.current = setTimeout(() => {
        setRunStepExitingLabel(null);
        runStepExitTimeoutRef.current = null;
        drain(true);
      }, 320);
    };

    drain();

    return () => {
      if (runStepExitTimeoutRef.current) {
        clearTimeout(runStepExitTimeoutRef.current);
        runStepExitTimeoutRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- runStepDisplayLabel/runStepExitingLabel updated in same effect
  }, [runningRunId, runningRunData?.currentStepId, runningRunData?.stepsDone, workflow]);

  // Last snapshot ostáva na fragmente, ktorému patrí (fragmentId z API); pri zmene grafu sa nepresúva
  const ownerForSnapshot = useMemo(() => {
    const fragments = (nodes || []).filter((n) => n.type === 'fragment');
    if (fragments.length === 0) return null;
    const fragmentId = lastSnapshotMeta?.fragmentId;
    if (fragmentId) {
      const pinned = fragments.find((n) => n.id === fragmentId);
      if (pinned) return pinned;
    }
    return findOwnerFragmentForSnapshot(nodes, edges);
  }, [nodes, edges, lastSnapshotMeta?.fragmentId]);
  const [flowTransform, setFlowTransform] = useState(() => store.getState().transform || [0, 0, 1]);
  useEffect(() => {
    return store.subscribe((state) => setFlowTransform(state.transform || [0, 0, 1]));
  }, [store]);

  useEffect(() => {
    if (!fragmentsPanelCollapsed) setFragmentsPanelTooltip(null);
  }, [fragmentsPanelCollapsed]);

  useEffect(() => {
    setName(workflow?.name || 'New Workflow');
    if (!workflow?._id) {
      // Nový workflow – východzie uzly
      if (lastSyncedRef.current === 'new') return;
      lastSyncedRef.current = 'new';
      setNodes(initialNodes);
      setEdges(initialEdges);
      lastSavedSnapshotRef.current = null;
      expectedSnapshotAfterLoadRef.current = null;
      return;
    }
    if (lastSyncedRef.current === workflow._id) return;
    lastSyncedRef.current = workflow._id;
    const nodesFromApi = workflow.nodes?.length
      ? workflow.nodes.map((n) => {
          const isGroup = n.type === 'group';
          if (isGroup) {
            // Skontroluj, či n.style má width a height, inak použij default
            // MongoDB môže vracať čísla ako stringy alebo null, takže konvertujeme na čísla
            let groupStyle = { width: 280, height: 200 };
            if (n.style !== null && n.style !== undefined && typeof n.style === 'object') {
              const width = typeof n.style.width === 'number' ? n.style.width : 
                           (typeof n.style.width === 'string' ? parseFloat(n.style.width) : null);
              const height = typeof n.style.height === 'number' ? n.style.height : 
                            (typeof n.style.height === 'string' ? parseFloat(n.style.height) : null);
              if (width !== null && !isNaN(width) && width > 0 && 
                  height !== null && !isNaN(height) && height > 0) {
                groupStyle = { width, height };
              }
            }
            return {
              id: n.id,
              type: 'group',
              position: n.position || { x: 0, y: 0 },
              style: groupStyle,
              data: { label: n.data?.label || 'Subflow' },
              className: n.className || getOutlineClassNameForNode({ type: 'group' }),
            };
          }
          return {
            id: n.id,
            type: 'fragment',
            position: n.position || { x: 0, y: 0 },
            parentId: n.parentId || undefined,
            extent: n.extent || undefined,
            data: {
              fragmentType: n.data?.fragmentType || n.type || n.data?.type,
              label: n.data?.label || n.data?.type || n.type,
              params: n.data?.params || {},
            },
            className: n.className || getOutlineClassNameForNode({ type: 'fragment', data: { fragmentType: n.data?.fragmentType || n.type || n.data?.type } }),
          };
        })
      : initialNodes;
    const edgesFromApi = workflow.edges?.length ? workflow.edges : initialEdges;
    // Zorad nodes: parent nodes musia byť pred child nodes (React Flow requirement)
    const sortedNodes = (() => {
      const parents = nodesFromApi.filter((n) => !n.parentId);
      const children = nodesFromApi.filter((n) => n.parentId);
      return [...parents, ...children];
    })();
    expectedSnapshotAfterLoadRef.current = payloadSnapshot(workflow?.name || 'New Workflow', sortedNodes, edgesFromApi);
    isUndoRedoRef.current = true; // Zabráň ukladaniu do histórie pri načítaní
    setNodes(sortedNodes);
    setEdges(edgesFromApi);
    // Reset undo/redo histórie pri načítaní nového workflow
    undoStackRef.current = [];
    redoStackRef.current = [];
    setUndoStackLength(0);
    setRedoStackLength(0);
    setTimeout(() => {
      isUndoRedoRef.current = false;
    }, 100);
    // Po načítaní uzlov vycentruj diagram do zorného poľa
    const t = setTimeout(() => {
      fitView({ padding: 0.2, maxZoom: 1, minZoom: 0.1 });
    }, 50);
    return () => clearTimeout(t);
  }, [workflow?._id, workflow?.nodes, workflow?.edges, workflow?.name, setNodes, setEdges, fitView]);

  // Po načítaní workflow nastavíme „uložený“ snapshot až keď state (nodes/edges) zodpovedá načítaným dátam
  useEffect(() => {
    const expected = expectedSnapshotAfterLoadRef.current;
    if (expected == null || !workflow?._id || lastSyncedRef.current !== workflow._id) return;
    const current = payloadSnapshot(name, nodes, edges);
    if (current === expected) {
      lastSavedSnapshotRef.current = expected;
      expectedSnapshotAfterLoadRef.current = null;
    }
  }, [workflow?._id, name, nodes, edges]);

  useEffect(() => {
    if (typeof onPayloadChange === 'function') {
      onPayloadChange(buildRunPayload(nodes, edges));
    }
  }, [nodes, edges, onPayloadChange]);

  const handleLoadLastSnapshot = useCallback(async () => {
    if (!workflowId) return;
    setSnapshotLoading(true);
    setSnapshotError(null);
    try {
      const meta = await getLastSnapshot(workflowId);
      const html = await getSnapshotHtml(meta.id);
      setSnapshotHtml(injectSnapshotInspectScript(html));
      setSnapshotSelectedElement(null);
      setSnapshotModalSlideIn(true);
      setSnapshotModalExiting(false);
      setSnapshotModalOpen(true);
    } catch (err) {
      setSnapshotError(err.response?.data?.error || err.message || 'Error loading');
      toast.error('Snapshot sa nepodarilo načítať');
    } finally {
      setSnapshotLoading(false);
    }
  }, [workflowId]);

  useEffect(() => {
    if (!snapshotModalOpen || !snapshotHtml) return;
    if (snapshotModalSlideIn) {
      const id = requestAnimationFrame(() => setSnapshotModalSlideIn(false));
      return () => cancelAnimationFrame(id);
    }
  }, [snapshotModalOpen, snapshotHtml, snapshotModalSlideIn]);

  const closeSnapshotModal = useCallback(() => {
    setSnapshotModalExiting(true);
    setTimeout(() => {
      setSnapshotModalOpen(false);
      setSnapshotHtml(null);
      setSnapshotSelectedElement(null);
      setSnapshotModalExiting(false);
    }, 320);
  }, []);

  useEffect(() => {
    if (!snapshotModalOpen) return;
    const onMessage = (e) => {
      if (e.data?.type === 'SNAPSHOT_ELEMENT_CLICK' && e.data.html != null) {
        setSnapshotSelectedElement({
          html: e.data.html,
          styles: e.data.styles || [],
          attributes: e.data.attributes || [],
          tagName: e.data.tagName || '',
          classesWithCounts: e.data.classesWithCounts || [],
        });
        setSnapshotPanelHtmlExpanded(false);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [snapshotModalOpen]);

  useEffect(() => {
    if (!snapshotContextMenu) return;
    const close = () => setSnapshotContextMenu(null);
    const onMouseDown = (e) => {
      if (e.target.closest('.workflow-snapshot-context-menu')) return;
      close();
    };
    window.addEventListener('mousedown', onMouseDown);
    return () => window.removeEventListener('mousedown', onMouseDown);
  }, [snapshotContextMenu]);

  // Undo funkcia
  const handleUndo = useCallback(() => {
    if (undoStackRef.current.length === 0) return;
    const currentSnapshot = { nodes: JSON.parse(JSON.stringify(nodes)), edges: JSON.parse(JSON.stringify(edges)), name };
    redoStackRef.current.push(currentSnapshot);
    const previousSnapshot = undoStackRef.current.pop();
    isUndoRedoRef.current = true;
    setName(previousSnapshot.name);
    // Použijeme applyNodeChanges na správne obnovenie pozícií
    const restoredNodes = previousSnapshot.nodes.map((n) => ({
      ...n,
      position: n.position || { x: 0, y: 0 },
    }));
    setNodes(restoredNodes);
    setEdges(previousSnapshot.edges);
    setUndoStackLength(undoStackRef.current.length);
    setRedoStackLength(redoStackRef.current.length);
    setTimeout(() => {
      isUndoRedoRef.current = false;
    }, 0);
  }, [nodes, edges, name, setNodes, setEdges]);

  // Redo funkcia
  const handleRedo = useCallback(() => {
    if (redoStackRef.current.length === 0) return;
    const currentSnapshot = { nodes: JSON.parse(JSON.stringify(nodes)), edges: JSON.parse(JSON.stringify(edges)), name };
    undoStackRef.current.push(currentSnapshot);
    const nextSnapshot = redoStackRef.current.pop();
    isUndoRedoRef.current = true;
    setName(nextSnapshot.name);
    // Použijeme applyNodeChanges na správne obnovenie pozícií
    const restoredNodes = nextSnapshot.nodes.map((n) => ({
      ...n,
      position: n.position || { x: 0, y: 0 },
    }));
    setNodes(restoredNodes);
    setEdges(nextSnapshot.edges);
    setUndoStackLength(undoStackRef.current.length);
    setRedoStackLength(redoStackRef.current.length);
    setTimeout(() => {
      isUndoRedoRef.current = false;
    }, 0);
  }, [nodes, edges, name, setNodes, setEdges]);

  // Klávesové skratky pre undo/redo
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey) {
        if (undoStackLength > 0) {
          event.preventDefault();
          handleUndo();
        }
      } else if ((event.ctrlKey || event.metaKey) && (event.key === 'y' || (event.key === 'z' && event.shiftKey))) {
        if (redoStackLength > 0) {
          event.preventDefault();
          handleRedo();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo, undoStackLength, redoStackLength]);

  // Ulož snapshot do histórie pred zmenou (používa sa v handleroch)
  const saveToHistoryBeforeChange = useCallback(() => {
    if (isUndoRedoRef.current) return;
    const snapshot = { nodes: JSON.parse(JSON.stringify(nodes)), edges: JSON.parse(JSON.stringify(edges)), name };
    undoStackRef.current.push(snapshot);
    // Obmedzíme veľkosť histórie na 50 záznamov
    if (undoStackRef.current.length > 50) {
      undoStackRef.current.shift();
    }
    // Po uložení do undo histórie vymaž redo históriu
    redoStackRef.current = [];
    setUndoStackLength(undoStackRef.current.length);
    setRedoStackLength(0);
  }, [nodes, edges, name]);

  // Zoradenie nodes: parent nodes musia byť pred child nodes (React Flow requirement)
  const sortNodesWithParentsFirst = useCallback((nds) => {
    const parents = nds.filter((n) => !n.parentId);
    const children = nds.filter((n) => n.parentId);
    return [...parents, ...children];
  }, []);

  const onConnect = useCallback((params) => {
    saveToHistoryBeforeChange();
    setEdges((eds) => addEdge(params, eds));
  }, [setEdges, saveToHistoryBeforeChange]);

  const addFragment = useCallback(
    (fragmentType, position, initialParams) => {
      saveToHistoryBeforeChange();
      let flowPosition = position;
      if (!flowPosition && flowWrapperRef.current && typeof screenToFlowPosition === 'function') {
        const rect = flowWrapperRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        flowPosition = screenToFlowPosition({ x: centerX, y: centerY });
      }
      if (!flowPosition) {
        flowPosition = { x: 200 + (nodes.length % 3) * 180, y: 80 + nodes.length * 70 };
      }
      const id = `${fragmentType.id}-${Date.now()}`;
      const params = { ...fragmentType.defaultParams, ...(initialParams || {}) };
      setNodes((nds) => {
        const newNodes = nds.concat({
          id,
          type: 'fragment',
          position: flowPosition,
          data: {
            fragmentType: fragmentType.id,
            label: fragmentType.label,
            params,
          },
          className: getOutlineClassNameForNode({ type: 'fragment', data: { fragmentType: fragmentType.id } }),
        });
        return sortNodesWithParentsFirst(newNodes);
      });
      toast.success(`Pridaný: ${fragmentType.label}`);
    },
    [setNodes, screenToFlowPosition, nodes.length, sortNodesWithParentsFirst, saveToHistoryBeforeChange]
  );

  const addGroup = useCallback(
    (position) => {
      saveToHistoryBeforeChange();
      let flowPosition = position;
      if (!flowPosition && flowWrapperRef.current && typeof screenToFlowPosition === 'function') {
        const rect = flowWrapperRef.current.getBoundingClientRect();
        flowPosition = screenToFlowPosition({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
      }
      if (!flowPosition) flowPosition = { x: 220, y: 100 };
      const id = `group-${Date.now()}`;
      setNodes((nds) => {
        const newNodes = nds.concat({
          id,
          type: 'group',
          position: flowPosition,
          data: { label: 'Subflow' },
          style: { width: 280, height: 200 },
          className: getOutlineClassNameForNode({ type: 'group' }),
        });
        return sortNodesWithParentsFirst(newNodes);
      });
      toast.success('Pridaná skupina (subflow)');
    },
    [setNodes, screenToFlowPosition, sortNodesWithParentsFirst, saveToHistoryBeforeChange]
  );

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      const groupDrop = event.dataTransfer.getData('application/group');
      if (groupDrop === 'true') {
        let position;
        try {
          const rect = flowWrapperRef.current?.getBoundingClientRect();
          const transform = store.getState().transform;
          if (rect && transform && Array.isArray(transform) && transform.length >= 3) {
            const [tx, ty, zoom] = transform;
            position = { x: (event.clientX - rect.left - tx) / zoom, y: (event.clientY - rect.top - ty) / zoom };
          } else {
            const toFlow = store.getState().screenToFlowPosition;
            position = typeof toFlow === 'function' ? toFlow({ x: event.clientX, y: event.clientY }) : { x: event.clientX - 200, y: event.clientY - 80 };
          }
        } catch (_) {
          position = { x: event.clientX - 200, y: event.clientY - 80 };
        }
        addGroup(position);
        return;
      }
      const typeId = event.dataTransfer.getData('application/fragment');
      const fragment = FRAGMENT_TYPES.find((f) => f.id === typeId);
      if (!fragment) return;
      let position;
      try {
        const rect = flowWrapperRef.current?.getBoundingClientRect();
        const transform = store.getState().transform;
        if (rect && transform && Array.isArray(transform) && transform.length >= 3) {
          const [tx, ty, zoom] = transform;
          position = {
            x: (event.clientX - rect.left - tx) / zoom,
            y: (event.clientY - rect.top - ty) / zoom,
          };
        } else {
          const toFlow = store.getState().screenToFlowPosition;
          if (typeof toFlow === 'function') {
            position = toFlow({ x: event.clientX, y: event.clientY });
          } else {
            position = { x: event.clientX - 200, y: event.clientY - 80 };
          }
        }
      } catch (_) {
        position = { x: event.clientX - 200, y: event.clientY - 80 };
      }
      addFragment(fragment, position);
    },
    [store, addFragment, addGroup]
  );
  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  // Wrapper pre onNodesChange: aplikuje zmeny (vrátane style z NodeResizer) a zoradí nodes (parent pred child)
  const handleNodesChange = useCallback(
    (changes) => {
      // Ak práve prebieha undo/redo alebo načítanie, neukladaj snapshot
      if (isUndoRedoRef.current) {
        onNodesChange(changes);
        setTimeout(() => {
          setNodes((nds) => {
            const sorted = sortNodesWithParentsFirst(nds);
            const needsReorder = sorted.some((n, i) => nds[i]?.id !== n.id);
            return needsReorder ? sorted : nds;
          });
        }, 0);
        return;
      }
      
      // Identifikuj významné zmeny, ktoré si zaslúžia snapshot v histórii
      const hasSignificantChange = changes.some((change) => {
        // Ulož snapshot pri: add, remove
        if (change.type === 'add' || change.type === 'remove') {
          return true;
        }
        // Pri začiatku dragging (dragging: true) - označíme, že drag začal
        if (change.type === 'position' && change.dragging === true && !dragStartTrackingRef.current.has(change.id)) {
          dragStartTrackingRef.current.add(change.id);
          // Uložíme snapshot PRED začiatkom dragovania (pozícia ešte nie je zmenená)
          return true;
        }
        // Po dokončení dragging (dragging: false) - odstráň z tracking setu, neukladaj snapshot
        if (change.type === 'position' && change.dragging === false) {
          dragStartTrackingRef.current.delete(change.id);
          return false;
        }
        // Pri začiatku resizing (resizing: true) - uložíme snapshot pred zmenou veľkosti (len raz)
        if (change.type === 'dimensions' && change.resizing === true && !resizeStartTrackingRef.current.has(change.id)) {
          resizeStartTrackingRef.current.add(change.id);
          return true; // Uložíme snapshot PRED zmenou veľkosti
        }
        // Po dokončení resizing (resizing: false) - odstráň z tracking setu, neukladaj snapshot
        if (change.type === 'dimensions' && change.resizing === false) {
          resizeStartTrackingRef.current.delete(change.id);
          return false;
        }
        return false;
      });
      
      // Ulož snapshot pred významnou zmenou
      if (hasSignificantChange) {
        saveToHistoryBeforeChange();
      }
      
      // Označ, že práve prebieha zmena uzlov (aby sme zabránili nekonečnej slučke v useEffect)
      nodesChangeInProgressRef.current = true;
      // Najprv aplikuj zmeny cez onNodesChange (React Flow interné aktualizácie)
      onNodesChange(changes);
      // Potom zorad nodes tak, aby parent nodes boli pred child nodes
      // Použijeme setTimeout, aby sme počkali na aktualizáciu stavu z onNodesChange
      setTimeout(() => {
        setNodes((nds) => {
          const sorted = sortNodesWithParentsFirst(nds);
          const needsReorder = sorted.some((n, i) => nds[i]?.id !== n.id);
          nodesChangeInProgressRef.current = false;
          return needsReorder ? sorted : nds;
        });
      }, 0);
    },
    [onNodesChange, setNodes, sortNodesWithParentsFirst, saveToHistoryBeforeChange]
  );

  const onNodeClick = useCallback((_, node) => setSelectedNodeId(node.id), []);
  const onNodeDoubleClick = useCallback((_, node) => setSelectedNodeId(node.id), []);
  const selectedNode = nodes.find((n) => n.id === selectedNodeId);

  const updateNodeData = useCallback(
    (nodeId, newData) => {
      saveToHistoryBeforeChange();
      setNodes((nds) => nds.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, ...newData } } : n)));
    },
    [setNodes, saveToHistoryBeforeChange]
  );

  // eslint-disable-next-line no-unused-vars
  const updateNode = useCallback(
    (nodeId, patch) => {
      setNodes((nds) => nds.map((n) => (n.id === nodeId ? { ...n, ...patch } : n)));
    },
    [setNodes]
  );

  // Presun fragmentu do skupiny alebo von (parentId + extent podľa React Flow Sub Flows)
  // Dôležité: parent nodes musia byť pred child nodes v nodes array!
  const moveFragmentToGroup = useCallback(
    (fragmentId, groupId) => {
      saveToHistoryBeforeChange();
      const fragment = nodes.find((n) => n.id === fragmentId);
      const group = groupId ? nodes.find((n) => n.id === groupId && n.type === 'group') : null;
      if (!fragment || fragment.type !== 'fragment') return;
      
      if (group) {
        // Preveď na relatívnu pozíciu vnútri skupiny (s paddingom)
        const pad = 20;
        const relX = pad;
        const relY = pad;
        
        // Aktualizuj node a zorad nodes (parent pred child)
        setNodes((nds) => {
          const updated = nds.map((n) =>
            n.id === fragmentId
              ? { ...n, parentId: groupId, extent: 'parent', position: { x: relX, y: relY } }
              : n
          );
          return sortNodesWithParentsFirst(updated);
        });
      } else {
        // Odstránenie zo skupiny: previesť relatívnu pozíciu na flow
        let flowX = fragment.position?.x ?? 0;
        let flowY = fragment.position?.y ?? 0;
        if (fragment.parentId) {
          const currentParent = nodes.find((n) => n.id === fragment.parentId);
          if (currentParent) {
            flowX = (currentParent.position?.x ?? 0) + flowX;
            flowY = (currentParent.position?.y ?? 0) + flowY;
          }
        }
        setNodes((nds) => {
          const updated = nds.map((n) =>
            n.id === fragmentId
              ? { ...n, parentId: undefined, extent: undefined, position: { x: flowX, y: flowY } }
              : n
          );
          return sortNodesWithParentsFirst(updated);
        });
      }
    },
    [nodes, setNodes, sortNodesWithParentsFirst, saveToHistoryBeforeChange]
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      // Normalizuj nodes pred ukladaním - pre skupiny iba width a height v style
      const normalizedNodes = nodes.map((n) => {
        if (n.type === 'group') {
          // Pre skupiny ulož width a height do style
          // NodeResizer aktualizuje width a height priamo na uzle, nie v style
          let styleToSave = null;
          // Skontroluj, či má uzel width a height vlastnosti (z NodeResizer)
          const width = typeof n.width === 'number' ? n.width : 
                       (typeof n.width === 'string' ? parseFloat(n.width) : null);
          const height = typeof n.height === 'number' ? n.height : 
                        (typeof n.height === 'string' ? parseFloat(n.height) : null);
          
          // Ak nemá width/height priamo, skús style
          const finalWidth = (width !== null && !isNaN(width) && width > 0) ? width :
                            (n.style && typeof n.style.width === 'number' ? n.style.width : null);
          const finalHeight = (height !== null && !isNaN(height) && height > 0) ? height :
                             (n.style && typeof n.style.height === 'number' ? n.style.height : null);
          
          if (finalWidth !== null && finalHeight !== null) {
            styleToSave = { width: finalWidth, height: finalHeight };
          }
          
          // Vytvor nový objekt bez React Flow interných vlastností
          const nodeToSave = {
            id: n.id,
            type: n.type,
            position: n.position,
            data: n.data,
            style: styleToSave,
            className: n.className,
            parentId: n.parentId,
            extent: n.extent,
          };
          return nodeToSave;
        }
        // Pre fragmenty odstráň React Flow interné vlastnosti
        const { measured, selected, width, height, resizing, dragging, ...fragmentNode } = n;
        return fragmentNode;
      });
      
      // Ukladáme celý stav (vrátane skupín a parentId) – beh používa buildRunPayload
      const payload = {
        name,
        nodes: normalizedNodes,
        edges,
      };
      if (workflowId) {
        await updateWorkflow(workflowId, payload);
        toast.success('Workflow uložený');
      } else {
        const created = await createWorkflow(payload);
        toast.success('Workflow vytvorený');
        onSave(created._id);
      }
      lastSavedSnapshotRef.current = payloadSnapshot(name, nodes, edges);
      setSavedJustNow(true);
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!savedJustNow) return;
    const t = setTimeout(() => setSavedJustNow(false), 2000);
    return () => clearTimeout(t);
  }, [savedJustNow]);

  const currentSnapshot = payloadSnapshot(name, nodes, edges);
  const hasUnsavedChanges = lastSavedSnapshotRef.current === null || lastSavedSnapshotRef.current !== currentSnapshot;

  const publishedUrl = workflow?.published && workflow?.workflowId ? `${API_BASE_URL}/workflows/run/${workflow.workflowId}` : null;

  const [tx, ty, zoom] = Array.isArray(flowTransform) && flowTransform.length >= 3 ? flowTransform : [0, 0, 1];
  const scaledSnapshotSize = Math.round(LAST_SNAPSHOT_BUTTON_SIZE * zoom);
  const ownerFlowPos = ownerForSnapshot ? getFlowPosition(nodes, ownerForSnapshot.id) : null;
  const ownerWidth = ownerForSnapshot ? getNodeWidth(ownerForSnapshot) : 0;
  const stickySnapshotStyle = lastSnapshotMeta && ownerForSnapshot && ownerFlowPos
    ? {
        position: 'absolute',
        left: (ownerFlowPos.x + ownerWidth + LAST_SNAPSHOT_GAP) * zoom + tx,
        top: ownerFlowPos.y * zoom + ty,
        width: scaledSnapshotSize,
        height: scaledSnapshotSize,
        zIndex: 10,
        pointerEvents: 'auto',
      }
    : null;

  const FLOAT_GAP = 12;
  const PANEL_TOP_EXTRA = 12; /* extra odsadenie panelov od horného menu */
  const TOP_BAR_OFFSET = 56 + FLOAT_GAP * 2 + PANEL_TOP_EXTRA; /* top + bar height + gap + extra */

  return (
    <div className="workflow-builder workflow-builder--floating" style={{ position: 'relative', height: '100%', overflow: 'hidden' }}>
      {/* Plátno – na celú oblasť, pod plávajúcimi panelmi */}
      <div className="workflow-builder__canvas" style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
        <div ref={flowWrapperRef} style={{ width: '100%', height: '100%', position: 'relative' }} className="react-flow-wrapper" onDrop={onDrop} onDragOver={onDragOver}>
          <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={handleNodesChange}
              onEdgesChange={(changes) => {
                const hasSignificantChange = changes.some((change) => change.type === 'add' || change.type === 'remove');
                if (hasSignificantChange) {
                  saveToHistoryBeforeChange();
                }
                onEdgesChange(changes);
              }}
              onConnect={onConnect}
              onNodeClick={onNodeClick}
              onNodeDoubleClick={onNodeDoubleClick}
              nodeTypes={nodeTypes}
              fitView
              fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
              minZoom={0.1}
              maxZoom={2}
              colorMode="dark"
              className="workflow-react-flow-dark"
              elementsSelectable={true}
            >
              <Controls className="workflow-controls-dark" />
              <Background variant={BackgroundVariant.Dots} gap={12} size={1} color="#555" />
            </ReactFlow>
            {stickySnapshotStyle && (
              <div
                role="button"
                tabIndex={0}
                onClick={handleLoadLastSnapshot}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleLoadLastSnapshot(); } }}
                style={{
                  ...stickySnapshotStyle,
                  borderRadius: '50%',
                  border: '1px solid rgba(125, 60, 152, 0.4)',
                  background: 'linear-gradient(135deg, rgba(125, 60, 152, 0.28) 0%, rgba(125, 60, 152, 0.08) 60%, rgba(30, 30, 30, 0.5) 100%)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.04)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'border-color 0.15s, box-shadow 0.15s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(125, 60, 152, 0.7)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.25), 0 0 0 1px rgba(125, 60, 152, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(125, 60, 152, 0.4)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.04)';
                }}
                title="Last snapshot (klik otvorí)"
              >
                <MaterialIcon icon="photo_library" size={Math.round(22 * zoom)} style={{ color: LAST_SNAPSHOT_COLOR }} />
              </div>
            )}
        </div>
      </div>

      {/* Horné menu – plávajúce, neopiera sa o okraj */}
      <div
        className="workflow-builder__topbar"
        style={{
          position: 'absolute',
          top: FLOAT_GAP,
          left: FLOAT_GAP,
          right: FLOAT_GAP,
          zIndex: 2,
          minHeight: 56,
          padding: '12px 20px',
          background: '#1e1e1e',
          borderRadius: 12,
          boxShadow: '0 4px 24px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.06)',
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        {/* Späť tlačidlo */}
        <button 
          type="button" 
          onClick={onClose}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 14px',
            background: '#2a2a2a',
            border: '1px solid #444',
            borderRadius: 8,
            color: '#e0e0e0',
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: 500,
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#333';
            e.currentTarget.style.borderColor = '#555';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#2a2a2a';
            e.currentTarget.style.borderColor = '#444';
          }}
        >
          <MaterialIcon icon="arrow_back" size={18} />
          Späť
        </button>

        {/* Názov workflow */}
        <div style={{ flex: 1, minWidth: 200, maxWidth: 400 }}>
          <input 
            type="text" 
            value={name} 
            onChange={(e) => {
              saveToHistoryBeforeChange();
              setName(e.target.value);
            }} 
            placeholder="Názov workflow" 
            style={{ 
              width: '100%',
              padding: '10px 14px', 
              fontSize: 15, 
              background: '#2a2a2a', 
              border: '1px solid #444', 
              borderRadius: 8, 
              color: '#fff',
              transition: 'all 0.2s'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#555';
              e.currentTarget.style.background = '#333';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = '#444';
              e.currentTarget.style.background = '#2a2a2a';
            }}
          />
        </div>

        {/* Undo/Redo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            onClick={handleUndo}
            disabled={undoStackLength === 0}
            style={{ 
              padding: '8px 12px', 
              fontSize: 14, 
              display: 'flex', 
              alignItems: 'center', 
              gap: 6,
              background: undoStackLength === 0 ? '#1a1a1a' : '#2a2a2a',
              border: '1px solid #444',
              borderRadius: 8,
              color: undoStackLength === 0 ? '#666' : '#e0e0e0',
              cursor: undoStackLength === 0 ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              if (undoStackLength > 0) {
                e.currentTarget.style.background = '#333';
                e.currentTarget.style.borderColor = '#555';
              }
            }}
            onMouseLeave={(e) => {
              if (undoStackLength > 0) {
                e.currentTarget.style.background = '#2a2a2a';
                e.currentTarget.style.borderColor = '#444';
              }
            }}
            title="Undo (Ctrl+Z)"
          >
            <MaterialIcon icon="undo" size={18} />
            Undo
          </button>
          <button
            onClick={handleRedo}
            disabled={redoStackLength === 0}
            style={{ 
              padding: '8px 12px', 
              fontSize: 14, 
              display: 'flex', 
              alignItems: 'center', 
              gap: 6,
              background: redoStackLength === 0 ? '#1a1a1a' : '#2a2a2a',
              border: '1px solid #444',
              borderRadius: 8,
              color: redoStackLength === 0 ? '#666' : '#e0e0e0',
              cursor: redoStackLength === 0 ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              if (redoStackLength > 0) {
                e.currentTarget.style.background = '#333';
                e.currentTarget.style.borderColor = '#555';
              }
            }}
            onMouseLeave={(e) => {
              if (redoStackLength > 0) {
                e.currentTarget.style.background = '#2a2a2a';
                e.currentTarget.style.borderColor = '#444';
              }
            }}
            title="Redo (Ctrl+Shift+Z alebo Ctrl+Y)"
          >
            <MaterialIcon icon="redo" size={18} />
            Redo
          </button>
        </div>

        {/* Save / Saved status */}
        {hasUnsavedChanges ? (
          <button 
            onClick={handleSave} 
            disabled={saving} 
            style={{
              padding: '8px 16px',
              background: '#3498db',
              border: 'none',
              borderRadius: 8,
              color: '#fff',
              fontSize: 14,
              fontWeight: 500,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1,
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
            onMouseEnter={(e) => {
              if (!saving) {
                e.currentTarget.style.background = '#2980b9';
              }
            }}
            onMouseLeave={(e) => {
              if (!saving) {
                e.currentTarget.style.background = '#3498db';
              }
            }}
          >
            {saving ? (
              <>
                <MaterialIcon icon="hourglass_empty" size={18} />
                Ukladám...
              </>
            ) : (
              <>
                <MaterialIcon icon="save" size={18} />
                Save
              </>
            )}
          </button>
        ) : (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 6,
            padding: '8px 14px',
            color: savedJustNow ? '#4caf50' : '#888', 
            fontSize: 14,
            fontWeight: 500
          }}>
            <MaterialIcon icon={savedJustNow ? "check_circle" : "done"} size={18} />
            {savedJustNow ? 'Uložené' : 'Uložené'}
          </div>
        )}

        {/* Run workflow / Prebieha – zobrazuje aktuálny fragment, klik otvorí detail behu */}
        {runWorkflowForBuilder && (
          runningRunId ? (
            <button
              type="button"
              className="workflow-run-btn workflow-run-btn--running"
              onClick={() => onOpenRunDetail?.(runningRunId)}
              style={{
                padding: '8px 16px',
                background: '#374151',
                border: '1px solid #4b5563',
                borderRadius: 8,
                color: '#e5e7eb',
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
              title="Klik otvorí detail behu"
            >
              <span className="workflow-run-rec" aria-hidden />
              <span className="workflow-run-step-text-wrap">
                {runStepExitingLabel != null && (
                  <span className="workflow-run-step-exit" key="exiting">
                    {runStepExitingLabel}
                  </span>
                )}
                <span className="workflow-run-step-enter" key={runStepDisplayLabel || 'current'}>
                  {runStepDisplayLabel || 'Spúšťa sa...'}
                </span>
              </span>
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                if (setRunModal && setRunForm) {
                  setRunModal(runWorkflowForBuilder);
                  const nodesToScan = nodes || runWorkflowForBuilder?.nodes || [];
                  const vars = extractWorkflowVariables(nodesToScan);
                  const initQuery = {};
                  const initBody = {};
                  vars.query.forEach((v) => { initQuery[v] = ''; });
                  vars.body.forEach((v) => { initBody[v] = ''; });
                  setRunForm({ eventId: '', accountId: '', useProxy: false, headless: false, recordingMode: false, queryParams: initQuery, bodyParams: initBody, _extractedVars: vars });
                }
              }}
              style={{
                padding: '8px 16px',
                background: '#27ae60',
                border: 'none',
                borderRadius: 8,
                color: '#fff',
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#229954';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#27ae60';
              }}
            >
              <MaterialIcon icon="play_arrow" size={18} />
              Run
            </button>
          )
        )}

        {/* Publish / Unpublish tlačidlo */}
        {workflow && (
          workflow.published ? (
            <button
              type="button"
              onClick={(e) => onUnpublish(workflow._id, e)}
              style={{
                padding: 8,
                background: '#2a2a2a',
                border: '1px solid #27ae60',
                borderRadius: 8,
                color: '#27ae60',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#2a3a2a';
                e.currentTarget.style.borderColor = '#229954';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#2a2a2a';
                e.currentTarget.style.borderColor = '#27ae60';
              }}
              title="Zrušiť publikovanie workflow"
            >
              <MaterialIcon icon="cloud" size={20} />
            </button>
          ) : (
            <button
              type="button"
              onClick={(e) => onPublish(workflow._id, e)}
              style={{
                padding: 8,
                background: '#2a2a2a',
                border: '1px solid #444',
                borderRadius: 8,
                color: '#888',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#333';
                e.currentTarget.style.borderColor = '#555';
                e.currentTarget.style.color = '#aaa';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#2a2a2a';
                e.currentTarget.style.borderColor = '#444';
                e.currentTarget.style.color = '#888';
              }}
              title="Publikovať workflow a získať webhook URL"
            >
              <MaterialIcon icon="cloud_off" size={20} />
            </button>
          )
        )}

        {/* Published URL */}
        {publishedUrl && (
          <div
            role="button"
            tabIndex={0}
            onClick={() => { navigator.clipboard.writeText(publishedUrl); toast.success('URL skopírovaná'); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigator.clipboard.writeText(publishedUrl); toast.success('URL skopírovaná'); } }}
            style={{ 
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12, 
              color: '#9cdcfe', 
              cursor: 'pointer', 
              padding: '8px 12px', 
              borderRadius: 8,
              background: '#2a2a2a',
              border: '1px solid #444',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#333';
              e.currentTarget.style.borderColor = '#555';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#2a2a2a';
              e.currentTarget.style.borderColor = '#444';
            }}
            title="Klikni pre skopírovanie"
          >
            <MaterialIcon icon="link" size={16} />
            <code style={{ background: 'transparent', color: 'inherit', fontSize: 'inherit' }}>
              {publishedUrl.length > 40 ? publishedUrl.slice(0, 37) + '...' : publishedUrl}
            </code>
          </div>
        )}
      </div>

      {/* Ľavý panel – paleta fragmentov, plávajúci */}
      <div
        className="workflow-fragments-panel workflow-builder__left-panel"
        style={{
          position: 'absolute',
          left: FLOAT_GAP,
          top: TOP_BAR_OFFSET,
          bottom: FLOAT_GAP,
          width: fragmentsPanelCollapsed ? 56 : 360,
          zIndex: 2,
          padding: fragmentsPanelCollapsed ? '10px 8px' : '16px',
          overflowY: 'auto',
          background: '#1a1a1a',
          borderRadius: 12,
          boxShadow: '0 4px 24px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.06)',
          transition: 'width 0.2s ease, padding 0.2s ease',
        }}
      >
            <div style={{ 
              fontWeight: 600, 
              fontSize: fragmentsPanelCollapsed ? 14 : 16, 
              marginBottom: fragmentsPanelCollapsed ? 8 : 4,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: fragmentsPanelCollapsed ? 'center' : 'flex-start',
              gap: 8
            }}>
              {!fragmentsPanelCollapsed && <MaterialIcon icon="widgets" size={20} />}
              {fragmentsPanelCollapsed ? (
                <button
                  type="button"
                  onClick={() => setFragmentsPanelCollapsed(false)}
                  title="Rozbaliť panel fragmentov"
                  style={{ 
                    width: 36, 
                    height: 36, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    background: 'transparent',
                    border: '1px solid #444',
                    borderRadius: 8,
                    color: '#aaa',
                    cursor: 'pointer'
                  }}
                >
                  <MaterialIcon icon="chevron_right" size={20} />
                </button>
              ) : (
                <>
                  Tools
                  <button
                    type="button"
                    onClick={() => setFragmentsPanelCollapsed(true)}
                    title="Zbaliť panel fragmentov"
                    style={{ 
                      marginLeft: 'auto',
                      width: 28, 
                      height: 28, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: 6,
                      color: '#888',
                      cursor: 'pointer'
                    }}
                  >
                    <MaterialIcon icon="chevron_left" size={20} />
                  </button>
                </>
              )}
            </div>
            {!fragmentsPanelCollapsed && (
              <div style={{ 
                fontSize: 12, 
                color: '#888', 
                marginBottom: 16,
                lineHeight: 1.4
              }}>
                Drag to canvas or click
              </div>
            )}
            {fragmentsPanelCollapsed ? (
              <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center' }}>
                <div
                  draggable
                  onDragStart={(e) => { e.dataTransfer.setData('application/group', 'true'); e.dataTransfer.effectAllowed = 'move'; }}
                  onClick={() => addGroup()}
                  onMouseEnter={(e) => {
                    const r = e.currentTarget.getBoundingClientRect();
                    setFragmentsPanelTooltip({ text: 'Visual subflow – vizuálny blok pre fragmenty', left: r.right + 8, top: r.top + r.height / 2 });
                  }}
                  onMouseLeave={() => setFragmentsPanelTooltip(null)}
                  style={{ 
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(155, 89, 182, 0.7)',
                    borderRadius: 8,
                    cursor: 'grab',
                    border: '1px solid rgba(155, 89, 182, 0.5)',
                    flexShrink: 0
                  }}
                >
                  <FragmentIcon type="group" size={20} />
                </div>
                {FRAGMENT_TYPES.map((f) => {
                  const categoryColor = FRAGMENT_CATEGORY_OUTLINES[f.category] || FRAGMENT_CATEGORY_OUTLINES.default;
                  const categoryBg = FRAGMENT_CATEGORY_BG[f.category] || FRAGMENT_CATEGORY_BG.default;
                  const tooltipText = f.description ? `${f.label} – ${f.description}` : f.label;
                  return (
                    <div
                      key={f.id}
                      draggable
                      onDragStart={(e) => { e.dataTransfer.setData('application/fragment', f.id); e.dataTransfer.effectAllowed = 'move'; }}
                      onClick={() => addFragment(f)}
                      onMouseEnter={(e) => {
                        const r = e.currentTarget.getBoundingClientRect();
                        setFragmentsPanelTooltip({ text: tooltipText, left: r.right + 8, top: r.top + r.height / 2 });
                      }}
                      onMouseLeave={() => setFragmentsPanelTooltip(null)}
                      style={{ 
                        width: 40,
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: categoryBg,
                        borderRadius: 8,
                        cursor: 'grab',
                        border: '1px solid #444',
                        borderLeft: `3px solid ${categoryColor}`,
                        flexShrink: 0
                      }}
                    >
                      <FragmentIcon type={f.id} size={20} />
                    </div>
                  );
                })}
              </div>
              {fragmentsPanelTooltip && (
                <div
                  role="tooltip"
                  style={{
                    position: 'fixed',
                    left: fragmentsPanelTooltip.left,
                    top: fragmentsPanelTooltip.top,
                    transform: 'translateY(-50%)',
                    padding: '6px 10px',
                    background: '#2a2a2a',
                    color: '#eee',
                    fontSize: 12,
                    lineHeight: 1.35,
                    borderRadius: 6,
                    border: '1px solid #555',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                    maxWidth: 280,
                    whiteSpace: 'normal',
                    zIndex: 10000,
                    pointerEvents: 'none'
                  }}
                >
                  {fragmentsPanelTooltip.text}
                </div>
              )}
              </>
            ) : (
            FRAGMENT_CATEGORIES.map((cat) => {
              const typesInCat = FRAGMENT_TYPES.filter((f) => f.category === cat.id);
              const isSubflowCat = cat.id === 'subflow';
              if (typesInCat.length === 0 && !isSubflowCat) return null;
              return (
                <div key={cat.id} style={{ marginBottom: 20 }}>
                  <div style={{ 
                    fontSize: 11, 
                    color: '#aaa', 
                    marginBottom: 10, 
                    textTransform: 'uppercase', 
                    letterSpacing: '0.08em',
                    fontWeight: 600,
                    paddingBottom: 6,
                    borderBottom: '1px solid #333'
                  }}>
                    {cat.label}
                  </div>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr', 
                    gap: 8
                  }}>
                    {isSubflowCat && (() => {
                      const subflowColor = FRAGMENT_CATEGORY_OUTLINES.subflow; // #9b59b6
                      return (
                      <div
                        draggable
                        onDragStart={(e) => { e.dataTransfer.setData('application/group', 'true'); e.dataTransfer.effectAllowed = 'move'; }}
                        onClick={() => addGroup()}
                        style={{ 
                          padding: '10px', 
                          background: 'rgba(155, 89, 182, 0.7)', 
                          borderRadius: 8, 
                          cursor: 'grab', 
                          border: '1px dashed rgba(155, 89, 182, 0.5)',
                          borderLeft: `3px solid ${subflowColor}`,
                          display: 'flex', 
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          gap: 6,
                          transition: 'all 0.2s',
                          minHeight: 60
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(155, 89, 182, 0.85)';
                          e.currentTarget.style.borderColor = 'rgba(155, 89, 182, 0.7)';
                          e.currentTarget.style.borderLeftColor = subflowColor;
                          e.currentTarget.style.transform = 'translateY(-1px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(155, 89, 182, 0.7)';
                          e.currentTarget.style.borderColor = 'rgba(155, 89, 182, 0.5)';
                          e.currentTarget.style.borderLeftColor = subflowColor;
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                        title="Pridaj visual subflow – vizuálny blok pre fragmenty"
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                          <FragmentIcon type="group" size={18} />
                          <span style={{ fontSize: 12, fontWeight: 500, color: '#fff', flex: 1, lineHeight: 1.2 }}>
                            Visual subflow
                          </span>
                        </div>
                        <div style={{ 
                          fontSize: 10, 
                          color: '#888', 
                          lineHeight: 1.3,
                          marginTop: 2
                        }}>
                          Vizuálny blok pre fragmenty
                        </div>
                      </div>
                      );
                    })()}
                    {typesInCat.map((f) => {
                      const categoryColor = FRAGMENT_CATEGORY_OUTLINES[f.category] || FRAGMENT_CATEGORY_OUTLINES.default;
                      const categoryBg = FRAGMENT_CATEGORY_BG[f.category] || FRAGMENT_CATEGORY_BG.default;
                      return (
                      <div
                        key={f.id}
                        draggable
                        onDragStart={(e) => { e.dataTransfer.setData('application/fragment', f.id); e.dataTransfer.effectAllowed = 'move'; }}
                        onClick={() => addFragment(f)}
                        style={{ 
                          padding: '10px', 
                          background: `linear-gradient(135deg, ${categoryBg} 0%, #2a2a2a 35%)`, 
                          borderRadius: 8, 
                          cursor: 'grab', 
                          border: '1px solid #444',
                          borderLeft: `3px solid ${categoryColor}`,
                          display: 'flex', 
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          gap: 6,
                          transition: 'all 0.2s',
                          minHeight: 60
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = `linear-gradient(135deg, ${FRAGMENT_CATEGORY_BG_HOVER[f.category] || FRAGMENT_CATEGORY_BG_HOVER.default} 0%, #333 35%)`;
                          e.currentTarget.style.borderColor = categoryColor;
                          e.currentTarget.style.borderLeftColor = categoryColor;
                          e.currentTarget.style.transform = 'translateY(-1px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = `linear-gradient(135deg, ${categoryBg} 0%, #2a2a2a 35%)`;
                          e.currentTarget.style.borderColor = '#444';
                          e.currentTarget.style.borderLeftColor = categoryColor;
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                        title={`Pridaj: ${f.label}`}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                          <FragmentIcon type={f.id} size={18} />
                          <span style={{ fontSize: 12, fontWeight: 500, color: '#fff', flex: 1, lineHeight: 1.2 }}>
                            {f.label}
                          </span>
                        </div>
                        {f.description && (
                          <div style={{ 
                            fontSize: 10, 
                            color: '#888', 
                            lineHeight: 1.3,
                            marginTop: 2
                          }}>
                            {f.description}
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
            )}
          </div>
        {/* Pravý panel – nastavenia fragmentu/skupiny, bez animácie */}
        {selectedNodeId && selectedNode?.type === 'fragment' && (
          <div
            className="workflow-builder__right-panel"
            style={{
              position: 'absolute',
              right: FLOAT_GAP,
              top: TOP_BAR_OFFSET,
              bottom: FLOAT_GAP,
              width: 420,
              zIndex: 2,
              display: 'flex',
              flexDirection: 'column',
              background: '#1e1e1e',
              borderRadius: 12,
              boxShadow: '0 4px 24px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.06)',
              overflow: 'hidden',
            }}
          >
            <FragmentSettingsPanel
              node={selectedNode}
              allNodes={nodes}
              workflowId={workflowId}
              onSave={(newData) => { updateNodeData(selectedNodeId, newData); setSelectedNodeId(null); }}
              onClose={() => setSelectedNodeId(null)}
              onMoveToGroup={moveFragmentToGroup}
              selectedNodeId={selectedNodeId}
            />
          </div>
        )}
        {selectedNodeId && selectedNode?.type === 'group' && (
          <div
            className="workflow-builder__right-panel"
            style={{
              position: 'absolute',
              right: FLOAT_GAP,
              top: TOP_BAR_OFFSET,
              bottom: FLOAT_GAP,
              width: 420,
              zIndex: 2,
              display: 'flex',
              flexDirection: 'column',
              background: '#1e1e1e',
              borderRadius: 12,
              boxShadow: '0 4px 24px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.06)',
              overflow: 'hidden',
            }}
          >
            <GroupSettingsPanel
              node={selectedNode}
              onSave={(newData) => { updateNodeData(selectedNodeId, newData); setSelectedNodeId(null); }}
              onClose={() => setSelectedNodeId(null)}
            />
          </div>
        )}

      {/* Bottom sheet: Posledný snapshot stránky (last run) – výška 2/5 okna, slide ease */}
      {snapshotModalOpen && snapshotHtml != null && (
        <div
          className="workflow-snapshot-modal-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            background: 'rgba(0,0,0,0.5)',
            transition: 'opacity 0.32s ease',
            opacity: snapshotModalExiting ? 0 : 1,
          }}
          onClick={(e) => e.target === e.currentTarget && closeSnapshotModal()}
        >
          <div
            style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              height: '40vh',
              maxHeight: '40vh',
              background: '#1e1e1e',
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
              boxShadow: '0 -4px 24px rgba(0,0,0,0.4)',
              display: 'flex',
              flexDirection: 'column',
              transform: snapshotModalSlideIn || snapshotModalExiting ? 'translateY(100%)' : 'translateY(0)',
              transition: 'transform 0.32s ease',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ flexShrink: 0, padding: '12px 16px', borderBottom: '1px solid #444', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 600 }}>Snapshot stránky (last run)</span>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={closeSnapshotModal}
              >
                Zavrieť
              </button>
            </div>
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'row', overflow: 'hidden' }}>
              <iframe
                title="Snapshot HTML"
                srcDoc={snapshotHtml}
                sandbox="allow-same-origin allow-scripts"
                style={{
                  flex: 1,
                  minWidth: 0,
                  minHeight: 0,
                  border: 'none',
                  background: '#fff',
                }}
              />
              <div
                style={{
                  width: 400,
                  flexShrink: 0,
                  borderLeft: '1px solid #444',
                  background: '#252526',
                  overflow: 'auto',
                  padding: 16,
                  fontSize: 12,
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 12, color: '#ccc' }}>Element (klikni v snapshote)</div>
                {!snapshotSelectedElement ? (
                  <p style={{ color: '#888', margin: 0 }}>Prejdi kurzorom nad element a klikni pre zobrazenie HTML a CSS.</p>
                ) : (
                  <>
                    {snapshotSelectedElement.tagName && (
                      <div style={{ marginBottom: 12, fontSize: 13 }}>
                        <span style={{ color: '#888' }}>Element: </span>
                        <span style={{ color: '#4ec9b0', fontWeight: 600 }}>{snapshotSelectedElement.tagName}</span>
                      </div>
                    )}
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => setSnapshotPanelHtmlExpanded((v) => !v)}
                      onKeyDown={(e) => e.key === 'Enter' && setSnapshotPanelHtmlExpanded((v) => !v)}
                      style={{
                        marginBottom: 8,
                        color: '#9cdcfe',
                        fontSize: 13,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        userSelect: 'none',
                      }}
                    >
                      <span style={{ fontSize: 10 }}>{snapshotPanelHtmlExpanded ? '▼' : '▶'}</span>
                      HTML
                    </div>
                    {snapshotPanelHtmlExpanded && (
                      <pre
                        style={{
                          margin: '0 0 16px',
                          padding: 10,
                          background: '#1e1e1e',
                          borderRadius: 4,
                          overflow: 'auto',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                          fontSize: 13,
                          color: '#d4d4d4',
                          maxHeight: 200,
                        }}
                      >
                        {snapshotSelectedElement.html}
                      </pre>
                    )}
                    <div style={{ marginBottom: 8, color: '#9cdcfe', fontSize: 13 }}>Atribúty (id, class, data-*, …)</div>
                    <div style={{ background: '#1e1e1e', borderRadius: 4, overflow: 'auto', maxHeight: 260, marginBottom: 16 }}>
                      {(snapshotSelectedElement.attributes || []).filter((a) => a.name !== 'style').length === 0 ? (
                        <div style={{ padding: 10, color: '#888', fontSize: 13 }}>žiadne ostatné atribúty</div>
                      ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                          <tbody>
                            {(snapshotSelectedElement.attributes || []).filter((a) => a.name !== 'style').map((a, i) => (
                              <tr key={i} style={{ borderBottom: '1px solid #333' }}>
                                <td style={{ padding: '5px 8px', color: '#9cdcfe', verticalAlign: 'top', width: 140 }}>{a.name}</td>
                                <td
                                  style={{ padding: '5px 8px', color: '#ce9178', wordBreak: 'break-all', fontSize: 13, cursor: 'context-menu' }}
                                  onContextMenu={(e) => {
                                    if (a.name === 'class' && (snapshotSelectedElement.classesWithCounts || []).length > 0) return;
                                    const sel = buildSelectorFromAttr(a.name, a.value);
                                    if (!sel) return;
                                    e.preventDefault();
                                    setSnapshotContextMenu({ left: e.clientX, top: e.clientY, selector: sel });
                                  }}
                                >
                                  {a.name === 'class' && (snapshotSelectedElement.classesWithCounts || []).length > 0 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                      {(snapshotSelectedElement.classesWithCounts || []).map((cw, j) => {
                                        const selClass = buildSelectorFromAttr('class', cw.class);
                                        return (
                                          <div
                                            key={j}
                                            style={{ cursor: 'context-menu' }}
                                            onContextMenu={(e) => {
                                              if (!selClass) return;
                                              e.preventDefault();
                                              e.stopPropagation();
                                              setSnapshotContextMenu({ left: e.clientX, top: e.clientY, selector: selClass });
                                            }}
                                          >
                                            <span>.{cw.class}</span>
                                            <span style={{ color: '#888', marginLeft: 6 }}>(v dokumente {cw.count}×)</span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    a.value
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                    <div style={{ marginBottom: 8, color: '#9cdcfe', fontSize: 13 }}>CSS (style)</div>
                    <div style={{ background: '#1e1e1e', borderRadius: 4, overflow: 'auto', maxHeight: 320 }}>
                      {snapshotSelectedElement.styles.length === 0 ? (
                        <div style={{ padding: 10, color: '#888', fontSize: 13 }}>žiadne inline štýly</div>
                      ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                          <tbody>
                            {snapshotSelectedElement.styles.map((s, i) => (
                              <tr key={i} style={{ borderBottom: '1px solid #333' }}>
                                <td style={{ padding: '5px 8px', color: '#9cdcfe', verticalAlign: 'top', width: 140 }}>{s.name}</td>
                                <td style={{ padding: '5px 8px', color: '#ce9178', wordBreak: 'break-all' }}>{s.value}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {snapshotContextMenu && (
        <div
          className="workflow-snapshot-context-menu"
          style={{
            position: 'fixed',
            left: snapshotContextMenu.left,
            top: snapshotContextMenu.top,
            zIndex: 10001,
            background: '#2a2a2a',
            border: '1px solid #555',
            borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
            padding: '6px 0',
            minWidth: 200,
          }}
        >
          <div style={{ padding: '6px 12px', fontSize: 11, color: '#888', borderBottom: '1px solid #444' }}>Vytvoriť fragment</div>
          <button
            type="button"
            className="workflow-snapshot-ctx-item"
            style={{ display: 'block', width: '100%', padding: '8px 12px', textAlign: 'left', background: 'none', border: 'none', color: '#e0e0e0', fontSize: 13, cursor: 'pointer' }}
            onClick={() => {
              const ft = FRAGMENT_TYPES.find((f) => f.id === 'wait_for_element');
              if (ft) addFragment(ft, undefined, { selector: snapshotContextMenu.selector, selectorType: 'css' });
              setSnapshotContextMenu(null);
            }}
          >
            Čakať na element
          </button>
          <button
            type="button"
            className="workflow-snapshot-ctx-item"
            style={{ display: 'block', width: '100%', padding: '8px 12px', textAlign: 'left', background: 'none', border: 'none', color: '#e0e0e0', fontSize: 13, cursor: 'pointer' }}
            onClick={() => {
              const ft = FRAGMENT_TYPES.find((f) => f.id === 'click_element');
              if (ft) addFragment(ft, undefined, { selector: snapshotContextMenu.selector, clickBy: 'selector' });
              setSnapshotContextMenu(null);
            }}
          >
            Klik na element
          </button>
        </div>
      )}
    </div>
  );
}

// --- Detail behu: len log a metadáta (bez editora workflow) ---
function RunDetailView({ runId, onBack, getStepLabel, setIsAuthenticated, darkMode, toggleDarkMode }) {
  const [run, setRun] = useState(null);
  const [workflow, setWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [networkPanelOpen, setNetworkPanelOpen] = useState(true);
  const [applyingRepair, setApplyingRepair] = useState(false);
  const autoApplyTriggeredForRunIdRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await getWorkflowRunById(runId);
        if (cancelled) return;
        setRun(r);
        if (r?.workflowId) {
          const w = await getWorkflowById(r.workflowId);
          if (!cancelled) setWorkflow(w);
        }
      } catch {
        if (!cancelled) setRun(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [runId]);

  // Keď je nastavený autorepair (beh má repairedFragments), rovno aplikovať zmenu bez pýtania
  useEffect(() => {
    if (!run?.repairedFragments?.length || applyingRepair) return;
    if (autoApplyTriggeredForRunIdRef.current === runId) return;
    autoApplyTriggeredForRunIdRef.current = runId;
    setApplyingRepair(true);
    (async () => {
      try {
        await applyWorkflowRunRepair(runId);
        toast.success('Oprava bola automaticky aplikovaná do workflow');
        const r = await getWorkflowRunById(runId);
        setRun(r);
        if (run?.workflowId) {
          const w = await getWorkflowById(run.workflowId);
          setWorkflow(w);
        }
      } catch (err) {
        autoApplyTriggeredForRunIdRef.current = null;
        toast.error(err?.response?.data?.error || err?.message || 'Aplikovanie zlyhalo');
      } finally {
        setApplyingRepair(false);
      }
    })();
  }, [runId, run?.repairedFragments, applyingRepair]);

  useEffect(() => {
    if (!run || run.status !== 'running') return;
    setPolling(true);
    const t = setInterval(async () => {
      try {
        const r = await getWorkflowRunById(runId);
        setRun(r);
        if (r.status !== 'running') setPolling(false);
      } catch {}
    }, 2000);
    return () => { clearInterval(t); };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run used for status check only
  }, [runId, run?.status]);

  if (loading) {
    return (
      <Layout setIsAuthenticated={setIsAuthenticated} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
        <div className="loading">Loading run...</div>
      </Layout>
    );
  }
  if (!run) {
    return (
      <Layout setIsAuthenticated={setIsAuthenticated} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
        <div style={{ padding: 24 }}>Run not found.</div>
        <button type="button" className="btn btn-secondary" onClick={() => onBack()}>Späť</button>
      </Layout>
    );
  }

  const stepsDone = run.stepsDone || [];
  const logText = run.log || (run.error ? `Error: ${run.error}` : '') || '';

  return (
    <Layout setIsAuthenticated={setIsAuthenticated} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
      <div
        style={{
          padding: 24,
          width: '100%',
          height: '100%',
          maxHeight: 'calc(100vh - 60px)',
          minHeight: 0,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <div className="run-detail-header">
          <button type="button" className="run-detail-header__back-btn" onClick={() => onBack()}>
            <MaterialIcon icon="arrow_back" size={18} />
            Späť
          </button>
          {workflow && (
            <>
              <span className="run-detail-header__workflow-name">Workflow: {workflow.name}</span>
              <button
                type="button"
                className="run-detail-header__edit-btn"
                onClick={() => onBack({ openWorkflow: run.workflowId })}
              >
                <MaterialIcon icon="edit" size={16} />
                Otvor na úpravu
              </button>
            </>
          )}
          <span className="run-detail-header__dates">
            {run.startedAt ? new Date(run.startedAt).toLocaleString() : ''}
            {run.finishedAt && ` → ${new Date(run.finishedAt).toLocaleString()}`}
          </span>
          <span className="run-detail-header__status">
            {run.status === 'running' && <span className="run-detail-header__status--running">Running</span>}
            {run.status === 'success' && <span className="run-detail-header__status--success">OK</span>}
            {(run.status === 'failed' || run.status === 'error') && <span className="run-detail-header__status--failed">Error</span>}
          </span>
          <span className="run-detail-header__proxy">
            Proxy: {run.usedProxy ? (run.proxyDescription ? `yes – ${run.proxyDescription}` : 'yes') : 'no'}
          </span>
          {polling && <span className="run-detail-header__polling"> (refreshing...)</span>}
        </div>

        {Array.isArray(run.repairedFragments) && run.repairedFragments.length > 0 && (
          <div
            style={{
              padding: 12,
              background: 'rgba(45, 90, 45, 0.2)',
              border: '1px solid rgba(45, 90, 45, 0.5)',
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <MaterialIcon icon="build_circle" size={20} />
            <span style={{ flex: 1 }}>
              {applyingRepair
                ? 'Aplikuje sa oprava do workflow...'
                : 'Autorepair: AI klikol na element. Aplikovať túto zmenu do workflow (fragment bude mať rovnaký selektor/text).'}
            </span>
            {!applyingRepair && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={async () => {
                  setApplyingRepair(true);
                  try {
                    await applyWorkflowRunRepair(runId);
                    toast.success('Fragment bol aktualizovaný vo workflow');
                    const r = await getWorkflowRunById(runId);
                    setRun(r);
                    if (workflow?.nodes) {
                      const w = await getWorkflowById(run.workflowId);
                      setWorkflow(w);
                    }
                  } catch (err) {
                    toast.error(err?.response?.data?.error || err?.message || 'Aplikovanie zlyhalo');
                  } finally {
                    setApplyingRepair(false);
                  }
                }}
              >
                Aplikovať opravu do workflow
              </button>
            )}
          </div>
        )}

        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: 16,
            alignItems: 'stretch',
          }}
        >
          {workflow && (
            <div
              style={{
                flex: '1 1 400px',
                minWidth: 0,
                minHeight: 200,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <div style={{ flexShrink: 0, fontSize: 14, fontWeight: 600 }}>Workflow (read-only)</div>
              <div style={{ flexShrink: 0, fontSize: 12, color: '#888', display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                <span><span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 2, background: '#555', marginRight: 4, verticalAlign: 'middle' }} /> waiting</span>
                <span><span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 2, background: '#d4a84b', marginRight: 4, verticalAlign: 'middle' }} /> running</span>
                <span><span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 2, background: '#2d5a2d', marginRight: 4, verticalAlign: 'middle' }} /> OK</span>
                <span><span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 2, background: '#c44', marginRight: 4, verticalAlign: 'middle' }} /> error</span>
              </div>
              <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <ReactFlowProvider>
                  <ReadOnlyWorkflowFlow workflow={workflow} run={run} />
                </ReactFlowProvider>
              </div>
            </div>
          )}

          <div
            style={{
              flex: '1 1 400px',
              minWidth: 0,
              minHeight: 200,
              maxHeight: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              gap: 12,
            }}
          >
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <RunLogViewer
                logText={logText}
                error={run.error}
                isFailed={run.status === 'failed' || run.status === 'error'}
                onCopy={() => toast.success('Log copied')}
              />
            </div>
            <div style={{ flex: networkPanelOpen ? 1 : '0 0 auto', minHeight: networkPanelOpen ? 0 : undefined, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <NetworkLogPanel entries={run.networkLog || run.result?.networkLog || []} open={networkPanelOpen} onOpenChange={setNetworkPanelOpen} />
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

// Tabuľka network requestov (URL, status, veľkosť v KB) – vždy zobrazená, pri prázdnom zozname „Žiadne záznamy“
function NetworkLogPanel({ entries, open: controlledOpen, onOpenChange }) {
  const [internalOpen, setInternalOpen] = useState(true);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (value) => {
    if (isControlled) onOpenChange?.(value);
    else setInternalOpen(value);
  };
  const list = entries || [];
  const totalKb = list.reduce((s, e) => s + (e.sizeKb || 0), 0);
  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', border: '1px solid #444', borderRadius: 8, overflow: 'hidden', background: '#1e1e1e' }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          flexShrink: 0,
          width: '100%',
          padding: '10px 12px',
          textAlign: 'left',
          background: '#2a2a2a',
          border: 'none',
          color: '#eee',
          fontSize: 14,
          fontWeight: 600,
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span>Network (requests) – {list.length} items, {totalKb.toFixed(1)} KB total</span>
        <span style={{ fontSize: 18 }}>{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div className="workflow-dark-scrollbar" style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          {list.length === 0 ? (
            <div style={{ padding: 12, color: '#888', fontSize: 13 }}>No entries (CDP network log)</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#252525' }}>
                  <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #444' }}>URL</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right', width: 70, borderBottom: '1px solid #444' }}>Status</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right', width: 80, borderBottom: '1px solid #444' }}>KB</th>
                </tr>
              </thead>
              <tbody>
                {list.map((e, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #333' }}>
                    <td style={{ padding: '6px 8px', color: '#ccc', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.url}>{e.url || '—'}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: '#aaa' }}>{e.status != null ? e.status : '—'}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: '#aaa' }}>{e.sizeKb != null ? e.sizeKb.toFixed(2) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

// Modál s detailom behu (pre builder – po spustení workflow)
function RunDetailModal({ runId, onClose }) {
  const [run, setRun] = useState(null);
  const [workflow, setWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [networkPanelOpen, setNetworkPanelOpen] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await getWorkflowRunById(runId);
        if (cancelled) return;
        setRun(r);
        if (r?.workflowId) {
          const w = await getWorkflowById(r.workflowId);
          if (!cancelled) setWorkflow(w);
        }
      } catch {
        if (!cancelled) setRun(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [runId]);

  useEffect(() => {
    if (!run || run.status !== 'running') return;
    const t = setInterval(async () => {
      try {
        const r = await getWorkflowRunById(runId);
        setRun(r);
      } catch {}
    }, 2000);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run used for status check only
  }, [runId, run?.status]);

  const logText = run?.log || (run?.error ? `Error: ${run.error}` : '') || '';

  return (
    <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1002 }} onClick={onClose}>
      <div
        className="modal"
        style={{
          background: '#252525',
          borderRadius: 12,
          maxWidth: '95vw',
          maxHeight: '90vh',
          width: 1000,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ flexShrink: 0, padding: 16, borderBottom: '1px solid #444', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, fontSize: 16 }}>Run detail</span>
            {workflow && <span style={{ color: '#888', fontSize: 14 }}>{workflow.name}</span>}
            {run && (
              <>
                <span style={{ fontSize: 13, color: '#888' }}>
                  {run.startedAt ? new Date(run.startedAt).toLocaleString() : ''}
                  {run.finishedAt && ` → ${new Date(run.finishedAt).toLocaleString()}`}
                </span>
                <span style={{ fontWeight: 600 }}>
                  {run.status === 'running' && <span style={{ color: '#d4a84b' }}>Running</span>}
                  {run.status === 'success' && <span style={{ color: '#4a9b4a' }}>OK</span>}
                  {(run.status === 'failed' || run.status === 'error') && <span style={{ color: '#c44' }}>Error</span>}
                </span>
              </>
            )}
          </div>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 16 }}>
          {loading ? (
            <div className="loading">Loading run...</div>
          ) : !run ? (
            <div>Run not found.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 16, alignItems: 'stretch' }}>
              {workflow && (
                <div style={{ flex: '1 1 400px', minWidth: 0, minHeight: 200, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ flexShrink: 0, fontSize: 14, fontWeight: 600 }}>Workflow</div>
                  <div style={{ flex: 1, minHeight: 200, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <ReactFlowProvider>
                      <ReadOnlyWorkflowFlow workflow={workflow} run={run} />
                    </ReactFlowProvider>
                  </div>
                </div>
              )}
              <div style={{ flex: '1 1 400px', minWidth: 0, minHeight: 200, maxHeight: 400, display: 'flex', flexDirection: 'column', overflow: 'hidden', gap: 12 }}>
                <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  <RunLogViewer
                    logText={logText}
                    error={run.error}
                    isFailed={run.status === 'failed' || run.status === 'error'}
                    onCopy={() => toast.success('Log copied')}
                  />
                </div>
                <div style={{ flex: networkPanelOpen ? 1 : '0 0 auto', minHeight: networkPanelOpen ? 0 : undefined, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  <NetworkLogPanel entries={run.networkLog || []} open={networkPanelOpen} onOpenChange={setNetworkPanelOpen} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Workflows({ setIsAuthenticated, darkMode, toggleDarkMode }) {
  const { id: urlId, runId } = useParams();
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [workflow, setWorkflow] = useState(null);
  const [runModal, setRunModal] = useState(null);
  const [runForm, setRunForm] = useState({ eventId: '', accountId: '', useProxy: false, headless: false, recordingMode: false, queryParams: {}, bodyParams: {} });
  const [events, setEvents] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [, setRuns] = useState([]);
  const [, setRunsLoading] = useState(false);
  const [, setRunsPagination] = useState(null);
  const [runsPage, setRunsPage] = useState(1);
  const [recentRuns, setRecentRuns] = useState([]);
  const [recentRunsLoading, setRecentRunsLoading] = useState(false);
  const [recentRunsPagination, setRecentRunsPagination] = useState(null);
  const [recentRunsPage, setRecentRunsPage] = useState(1);
  const [listTab, setListTab] = useState('workflows');
  const [kebabOpenId, setKebabOpenId] = useState(null);
  const [lastStartedRunId, setLastStartedRunId] = useState(null);
  const [runningRunData, setRunningRunData] = useState(null); // aktuálny stav behu (currentStepId, stepsDone) pre tlačidlo „Prebieha”
  const [runDetailModalRunId, setRunDetailModalRunId] = useState(null);
  const builderPayloadRef = useRef(null);
  const pollRunIdRef = useRef(null);
  const [newWorkflowModalOpen, setNewWorkflowModalOpen] = useState(false);
  const [workflowTypeHover, setWorkflowTypeHover] = useState(null);
  const [creatingN8n, setCreatingN8n] = useState(false);
  const [deleteConfirmWorkflow, setDeleteConfirmWorkflow] = useState(null);

  useEffect(() => {
    if (!kebabOpenId) return;
    const close = () => setKebabOpenId(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [kebabOpenId]);

  useEffect(() => {
    getWorkflows()
      .then(setWorkflows)
      .catch(() => toast.error('Nepodarilo sa načítať workflows'))
      .finally(() => setLoading(false));
  }, []);

  // Sync selectedId from URL (pri otvorení linku /workflows/:id)
  useEffect(() => {
    if (urlId === undefined) return;
    if (urlId === 'new' || urlId === '') {
      setSelectedId('new');
      return;
    }
    setSelectedId(urlId);
  }, [urlId]);

  useEffect(() => {
    if (!selectedId) setWorkflow(null);
    else if (selectedId === 'new') setWorkflow({ name: 'New Workflow', nodes: [], edges: [] });
    else getWorkflowById(selectedId).then(setWorkflow).catch(() => toast.error('Workflow sa nenačítal'));
  }, [selectedId]);

  const fetchRuns = useCallback(() => {
    if (!selectedId || selectedId === 'new') return;
    getWorkflowRuns(selectedId, runsPage, 10)
      .then((data) => {
        if (data.runs) {
          setRuns(Array.isArray(data.runs) ? data.runs : []);
          setRunsPagination(data.pagination || null);
        } else {
          // Fallback pre starý formát (ak API vráti len pole)
          setRuns(Array.isArray(data) ? data : []);
          setRunsPagination(null);
        }
      })
      .catch(() => {
        setRuns([]);
        setRunsPagination(null);
      });
  }, [selectedId, runsPage]);

  useEffect(() => {
    if (!selectedId || selectedId === 'new') {
      setRuns([]);
      setRunsPagination(null);
      setRunsPage(1);
      return;
    }
    setRunsLoading(true);
    getWorkflowRuns(selectedId, runsPage, 10)
      .then((data) => {
        if (data.runs) {
          setRuns(Array.isArray(data.runs) ? data.runs : []);
          setRunsPagination(data.pagination || null);
        } else {
          // Fallback pre starý formát (ak API vráti len pole)
          setRuns(Array.isArray(data) ? data : []);
          setRunsPagination(null);
        }
      })
      .catch(() => {
        setRuns([]);
        setRunsPagination(null);
      })
      .finally(() => setRunsLoading(false));
  }, [selectedId, runsPage]);

  // Automatické obnovovanie histórie behov vybraného workflow
  useEffect(() => {
    if (!selectedId || selectedId === 'new') return;
    const interval = setInterval(fetchRuns, 4000);
    return () => clearInterval(interval);
  }, [selectedId, fetchRuns]);

  const openWorkflow = (id) => {
    setSelectedId(id);
    navigate(id ? `/workflows/${id}` : '/workflows');
  };

  const handleNewWokkioWorkflow = () => {
    setNewWorkflowModalOpen(false);
    setWorkflow({ name: 'New Workflow', nodes: [], edges: [] });
    openWorkflow('new');
  };

  const handleNewN8nWorkflow = async () => {
    setCreatingN8n(true);
    try {
      const triggerNode = {
        id: 'trigger-1',
        type: 'trigger',
        position: { x: 0, y: 0 },
        data: { label: 'Webhook' },
        config: { triggerType: 'webhook', webhookMethod: 'POST' },
      };
      const created = await createWorkflow({
        name: 'New n8n workflow',
        workflowType: 'n8n',
        nodes: [triggerNode],
        edges: [],
      });
      setNewWorkflowModalOpen(false);
      setWorkflows((prev) => [created, ...prev]);
      openWorkflow(created._id);
      toast.success('n8n workflow vytvorený');
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Error creating');
    } finally {
      setCreatingN8n(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const getWorkflowPageUrl = (id) => {
    const basePath = (window.location.pathname || '').replace(/\/workflows\/?.*$/, '') || '';
    return `${window.location.origin}${basePath}/workflows/${id}`;
  };

  useEffect(() => {
    getEvents().then((d) => setEvents(d?.events ?? [])).catch(() => setEvents([]));
    getAccounts().then((d) => setAccounts(d?.accounts ?? [])).catch(() => setAccounts([]));
  }, []);

  useEffect(() => {
    if (selectedId == null) {
      setLastStartedRunId(null);
      setRunningRunData(null);
    }
  }, [selectedId]);

  const fetchRecentRuns = useCallback(() => {
    getRecentWorkflowRuns(recentRunsPage, 10)
      .then((data) => {
        if (data.runs) {
          setRecentRuns(Array.isArray(data.runs) ? data.runs : []);
          setRecentRunsPagination(data.pagination || null);
        } else {
          // Fallback pre starý formát (ak API vráti len pole)
          setRecentRuns(Array.isArray(data) ? data : []);
          setRecentRunsPagination(null);
        }
      })
      .catch(() => {
        setRecentRuns([]);
        setRecentRunsPagination(null);
      });
  }, [recentRunsPage]);

  useEffect(() => {
    if (selectedId != null) return;
    setRecentRunsLoading(true);
    getRecentWorkflowRuns(recentRunsPage, 10)
      .then((data) => {
        if (data.runs) {
          setRecentRuns(Array.isArray(data.runs) ? data.runs : []);
          setRecentRunsPagination(data.pagination || null);
        } else {
          // Fallback pre starý formát (ak API vráti len pole)
          setRecentRuns(Array.isArray(data) ? data : []);
          setRecentRunsPagination(null);
        }
      })
      .catch(() => {
        setRecentRuns([]);
        setRecentRunsPagination(null);
      })
      .finally(() => setRecentRunsLoading(false));
  }, [selectedId, recentRunsPage]);

  // Automatické obnovovanie zoznamu „posledné behy“ na hlavnej stránke
  useEffect(() => {
    if (selectedId != null) return;
    const interval = setInterval(fetchRecentRuns, 4000);
    return () => clearInterval(interval);
  }, [selectedId, fetchRecentRuns]);

  const handleDeleteClick = (id, e) => {
    e?.stopPropagation();
    const w = workflows.find((x) => x._id === id);
    setDeleteConfirmWorkflow(w || { _id: id, name: 'Workflow' });
  };

  const handleDeleteConfirm = async () => {
    const w = deleteConfirmWorkflow;
    if (!w?._id) return;
    const id = w._id;
    setDeleteConfirmWorkflow(null);
    try {
      await deleteWorkflow(id);
      setWorkflows((x) => x.filter((item) => item._id !== id));
      if (selectedId === id) openWorkflow(null);
      toast.success('Workflow zmazaný');
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
    }
  };

  const handlePublish = async (id, e) => {
    e?.stopPropagation();
    try {
      const updated = await publishWorkflow(id);
      setWorkflows((w) => w.map((x) => (x._id === id ? { ...x, published: true, workflowId: updated.workflowId } : x)));
      setWorkflow((w) => (w && w._id === id ? { ...w, published: true, workflowId: updated.workflowId } : w));
      toast.success('Workflow publikovaný');
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
    }
  };

  const handleUnpublish = async (id, e) => {
    e?.stopPropagation();
    try {
      await unpublishWorkflow(id);
      setWorkflows((w) => w.map((x) => (x._id === id ? { ...x, published: false, workflowId: null } : x)));
      setWorkflow((w) => (w && w._id === id ? { ...w, published: false, workflowId: null } : w));
      toast.success('Workflow od publikácie odstránený');
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
    }
  };

  // Otvorenie run modalu s extrahovanými query/body premennými (pre Wokkio aj n8n). Musí byť pred akýmkoľvek conditional return.
  const openRunModalForWorkflow = useCallback((w) => {
    if (!w) return;
    setRunModal(w);
    const vars = extractWorkflowVariablesByType(w);
    const initQuery = {};
    (vars.query || []).forEach((v) => { initQuery[v] = ''; });
    const initBody = {};
    (vars.body || []).forEach((v) => { initBody[v] = ''; });
    setRunForm({ eventId: '', accountId: '', useProxy: false, headless: false, recordingMode: false, queryParams: initQuery, bodyParams: initBody, _extractedVars: vars });
  }, []);

  const handleRun = async () => {
    const id = runModal?._id;
    if (!id) return;
    
    // Validácia povinných parametrov
    const requiredQuery = runForm._extractedVars?.query || [];
    const requiredBody = runForm._extractedVars?.body || [];
    const queryParams = runForm.queryParams || {};
    const bodyParams = runForm.bodyParams || {};
    const allQueryFilled = requiredQuery.every((v) => (queryParams[v] || '').trim() !== '');
    const allBodyFilled = requiredBody.every((v) => (bodyParams[v] || '').trim() !== '');
    
    if (!allQueryFilled || !allBodyFilled) {
      setRunForm((f) => ({ ...f, _attemptedRun: true }));
      toast.error('Vyplňte všetky povinné parametre');
      return;
    }
    
    try {
      const isN8nRun = runModal?.workflowType === 'n8n';
      let result;
      if (isN8nRun) {
        result = await runN8nWorkflow(id, { query: queryParams, body: bodyParams });
      } else {
        const runOpts = {
          eventId: runForm.eventId || undefined,
          accountId: runForm.accountId || undefined,
          useProxy: runForm.useProxy,
          headless: runForm.recordingMode ? false : runForm.headless,
          fresh: runForm.fresh,
          recordingMode: Boolean(runForm.recordingMode),
        };
        if (selectedId != null && builderPayloadRef.current?.nodes) {
          runOpts.nodes = builderPayloadRef.current.nodes;
          runOpts.edges = builderPayloadRef.current.edges;
        }
        const hasQuery = Object.values(queryParams).some((v) => v !== '');
        const hasBody = Object.values(bodyParams).some((v) => v !== '');
        if (hasQuery) runOpts.query = queryParams;
        if (hasBody) runOpts.body = bodyParams;
        result = await runWorkflow(id, runOpts);
      }
      toast.success('Workflow spustený');
      setRunModal(null);
      const runId = result?.runId;
      if (runId) {
        if (selectedId != null) {
          setLastStartedRunId(runId);
          setRunningRunData({
            _id: runId,
            workflowId: id,
            status: 'running',
            startedAt: new Date().toISOString(),
            stepsDone: [],
            currentStepId: null,
          });
        }
        // Reset stránky na 1, aby sa nový run zobrazil hneď
        if (selectedId === id) {
          setRunsPage(1);
        }
        setRecentRunsPage(1);
        
        // Obnoviť dáta aby sa nový run zobrazil
        if (selectedId === id) {
          fetchRuns();
        }
        fetchRecentRuns();
        
        const poll = async () => {
          if (pollRunIdRef.current !== runId) return;
          try {
            const updated = await getWorkflowRunById(runId);
            if (selectedId != null) setRunningRunData(updated);
            // Obnoviť dáta po aktualizácii
            if (selectedId === id) {
              fetchRuns();
            }
            fetchRecentRuns();
            if (updated.status === 'running') {
              setTimeout(poll, 2000);
            } else {
              pollRunIdRef.current = null;
              setLastStartedRunId((prev) => (prev === runId ? null : prev));
              if (selectedId != null) setRunningRunData(null);
            }
          } catch {
            setTimeout(poll, 3000);
          }
        };
        pollRunIdRef.current = runId;
        setTimeout(poll, 1500);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
    }
  };

  // Po všetkých hookoch: ak sme na URL behu, zobraziť detail logu
  if (runId) {
    return (
      <RunDetailView
        runId={runId}
        onBack={(opts) => {
          if (opts?.openWorkflow) navigate(`/workflows/${opts.openWorkflow}`);
          else navigate('/workflows');
        }}
        getStepLabel={getStepLabel}
        setIsAuthenticated={setIsAuthenticated}
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
      />
    );
  }

  if (loading) {
    return (
      <Layout setIsAuthenticated={setIsAuthenticated} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
        <div className="loading">Načítava sa...</div>
      </Layout>
    );
  }

  // Builder na celú obrazovku – bez ľavého menu, s tlačidlom Späť a Run
  if (selectedId != null) {
    const runWorkflowForBuilder = selectedId !== 'new' ? (workflow || { _id: selectedId, name: workflow?.name || 'Workflow' }) : null;
    const isN8n = workflow?.workflowType === 'n8n';
    return (
      <>
        <div className="workflow-builder-fullscreen" style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', flexDirection: 'column', background: '#1a1a1a' }}>
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {isN8n ? (
              <N8nWorkflowEditor
                workflowId={selectedId}
                workflow={workflow}
                onClose={() => openWorkflow(null)}
                onOpenRunModal={openRunModalForWorkflow}
              />
            ) : (
            <ReactFlowProvider>
              <FlowCanvas
                workflowId={selectedId === 'new' ? null : selectedId}
                workflow={workflow}
                onSave={(id) => openWorkflow(id)}
                onClose={() => openWorkflow(null)}
                onPayloadChange={(payload) => { builderPayloadRef.current = payload; }}
                runWorkflowForBuilder={runWorkflowForBuilder}
                setRunModal={setRunModal}
                setRunForm={setRunForm}
                runningRunId={lastStartedRunId}
                runningRunData={runningRunData}
                onOpenRunDetail={setRunDetailModalRunId}
                onPublish={handlePublish}
                onUnpublish={handleUnpublish}
              />
            </ReactFlowProvider>
            )}
          </div>
        </div>
        {deleteConfirmWorkflow && (
          <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001 }} onClick={() => setDeleteConfirmWorkflow(null)}>
            <div className="modal" style={{ background: '#252525', padding: 24, borderRadius: 8, minWidth: 360 }} onClick={(e) => e.stopPropagation()}>
              <h3>Naozaj zmazať workflow?</h3>
              <p style={{ marginTop: 8, color: '#aaa' }}>
                Workflow „{deleteConfirmWorkflow.name}“ bude natrvalo zmazaný. Túto akciu nemožno vrátiť späť.
              </p>
              <div style={{ marginTop: 24, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setDeleteConfirmWorkflow(null)}>Zrušiť</button>
                <button type="button" className="btn btn-danger" onClick={handleDeleteConfirm}>Zmazať</button>
              </div>
            </div>
          </div>
        )}
        {runModal && (() => {
          const showValidation = runForm._attemptedRun;
          const isQueryEmpty = (varName) => showValidation && !(runForm.queryParams?.[varName] || '').trim();
          const isBodyEmpty = (varName) => showValidation && !(runForm.bodyParams?.[varName] || '').trim();
          const hasParams = runForm._extractedVars?.query?.length > 0 || runForm._extractedVars?.body?.length > 0;
          return (
          <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001 }} onClick={() => setRunModal(null)}>
            <div className="modal" style={{ background: '#252525', padding: 24, borderRadius: 8, minWidth: 400, maxWidth: 500, maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
              <h3>Run workflow: {runModal.name}</h3>
              
              {/* Workflow input parameters intro */}
              {hasParams && (
                <p style={{ marginTop: 12, fontSize: 13, color: '#999', lineHeight: 1.5 }}>
                  This workflow requires input parameters to run. Please provide values for each field below.
                </p>
              )}
              
              {/* Query parameters */}
              {runForm._extractedVars?.query?.length > 0 && (
                <div style={{ marginTop: 16, padding: 12, background: '#1a1a1a', borderRadius: 6, border: '1px solid #333' }}>
                  <div style={{ fontSize: 12, color: '#888', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <MaterialIcon icon="link" size={14} />
                    <span>Query parameters</span>
                  </div>
                  {runForm._extractedVars.query.map((varName) => (
                    <div key={`query-${varName}`} style={{ marginTop: 8 }}>
                      <label style={{ fontSize: 13, color: '#ccc' }}>{varName} <span style={{ color: '#e74c3c' }}>*</span></label>
                      <input
                        type="text"
                        value={runForm.queryParams?.[varName] || ''}
                        onChange={(e) => setRunForm((f) => ({ ...f, queryParams: { ...f.queryParams, [varName]: e.target.value } }))}
                        placeholder={varName}
                        style={{ width: '100%', padding: 8, marginTop: 4, background: '#252525', border: `1px solid ${isQueryEmpty(varName) ? '#e74c3c' : '#444'}`, borderRadius: 4, color: '#fff' }}
                      />
                    </div>
                  ))}
                </div>
              )}
              
              {/* Body parameters */}
              {runForm._extractedVars?.body?.length > 0 && (
                <div style={{ marginTop: 12, padding: 12, background: '#1a1a1a', borderRadius: 6, border: '1px solid #333' }}>
                  <div style={{ fontSize: 12, color: '#888', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <MaterialIcon icon="data_object" size={14} />
                    <span>Body parameters</span>
                  </div>
                  {runForm._extractedVars.body.map((varName) => (
                    <div key={`body-${varName}`} style={{ marginTop: 8 }}>
                      <label style={{ fontSize: 13, color: '#ccc' }}>{varName} <span style={{ color: '#e74c3c' }}>*</span></label>
                      <input
                        type="text"
                        value={runForm.bodyParams?.[varName] || ''}
                        onChange={(e) => setRunForm((f) => ({ ...f, bodyParams: { ...f.bodyParams, [varName]: e.target.value } }))}
                        placeholder={varName}
                        style={{ width: '100%', padding: 8, marginTop: 4, background: '#252525', border: `1px solid ${isBodyEmpty(varName) ? '#e74c3c' : '#444'}`, borderRadius: 4, color: '#fff' }}
                      />
                    </div>
                  ))}
                </div>
              )}

              <div style={{ marginTop: 16 }}>
                <label className={`workflow-checkbox-option ${runForm.useProxy ? 'is-checked' : ''}`}>
                  <input type="checkbox" checked={runForm.useProxy} onChange={(e) => setRunForm((f) => ({ ...f, useProxy: e.target.checked }))} />
                  <span className="workflow-checkbox-box" aria-hidden />
                  <span className="workflow-checkbox-label">Proxy</span>
                </label>
              </div>
              <div style={{ marginTop: 12 }}>
                <label className={`workflow-checkbox-option ${runForm.headless ? 'is-checked' : ''} ${runForm.recordingMode ? 'opacity-60' : ''}`}>
                  <input type="checkbox" checked={runForm.headless} disabled={runForm.recordingMode} onChange={(e) => setRunForm((f) => ({ ...f, headless: e.target.checked }))} />
                  <span className="workflow-checkbox-box" aria-hidden />
                  <span className="workflow-checkbox-label">Headless</span>
                </label>
                {runForm.recordingMode && <p style={{ margin: '2px 0 0 24px', fontSize: 11, color: '#888' }}>Headless cannot be enabled in recording mode.</p>}
              </div>
              <div style={{ marginTop: 12 }}>
                <label className={`workflow-checkbox-option ${runForm.fresh ? 'is-checked' : ''}`}>
                  <input type="checkbox" checked={runForm.fresh || false} onChange={(e) => setRunForm((f) => ({ ...f, fresh: e.target.checked }))} />
                  <span className="workflow-checkbox-box" aria-hidden />
                  <span className="workflow-checkbox-label">Clean session</span>
                </label>
                <p style={{ margin: '4px 0 0 24px', fontSize: 11, color: '#777', lineHeight: 1.4 }}>Start with a fresh browser profile — no cookies, cache, or saved logins</p>
              </div>
              <div
                style={{
                  marginTop: 16,
                  padding: '14px 16px',
                  borderRadius: 12,
                  background: runForm.recordingMode ? 'rgba(56, 189, 248, 0.08)' : 'rgba(15, 23, 42, 0.85)',
                  border: runForm.recordingMode
                    ? '1px solid rgba(56, 189, 248, 0.35)'
                    : '1px solid rgba(148, 163, 184, 0.4)',
                  boxShadow: runForm.recordingMode
                    ? '0 0 0 1px rgba(56, 189, 248, 0.12), 0 4px 12px rgba(15, 23, 42, 0.4)'
                    : '0 8px 18px rgba(15, 23, 42, 0.6)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                }}
              >
                <label className={`workflow-checkbox-option ${runForm.recordingMode ? 'is-checked' : ''}`} style={{ margin: 0, alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={runForm.recordingMode || false}
                    onChange={(e) =>
                      setRunForm((f) => ({
                        ...f,
                        recordingMode: e.target.checked,
                        ...(e.target.checked ? { headless: false } : {}),
                      }))
                    }
                  />
                  <span className="workflow-checkbox-box" aria-hidden />
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flex: 1 }}>
                    <MaterialIcon icon="fiber_manual_record" size={18} style={{ color: runForm.recordingMode ? '#38bdf8' : '#64748b' }} />
                    <span className="workflow-checkbox-label">
                      Recording mode
                      <span
                        style={{
                          marginLeft: 8,
                          padding: '2px 6px',
                          borderRadius: 999,
                          fontSize: 10,
                          background: 'rgba(56, 189, 248, 0.15)',
                          color: '#38bdf8',
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          fontWeight: 600,
                        }}
                      >
                        Beta
                      </span>
                    </span>
                  </span>
                </label>
                <p
                  style={{
                    margin: 0,
                    paddingLeft: 30,
                    fontSize: 12,
                    color: '#94a3b8',
                    lineHeight: 1.45,
                  }}
                >
                  Keep the browser open after the last step and add more clicks, extractions, or steps using the recording toolbar in Chrome.
                </p>
              </div>
              <div style={{ marginTop: 24, display: 'flex', gap: 8 }}>
                <button type="button" className="btn btn-primary" onClick={handleRun}>Run</button>
                <button type="button" className="btn btn-secondary" onClick={() => setRunModal(null)}>Cancel</button>
              </div>
            </div>
          </div>
          );
        })()}
        {runDetailModalRunId && (
          <RunDetailModal runId={runDetailModalRunId} onClose={() => setRunDetailModalRunId(null)} />
        )}
      </>
    );
  }

  return (
    <Layout setIsAuthenticated={setIsAuthenticated} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
      <div className="workflows-page workflows-page--list">
        <header className="workflows-page__header">
          <div>
            <h1 className="workflows-page__title">Workflow Builder</h1>
            <p className="workflows-page__subtitle">
              Double-click a fragment to open settings. Add fragments from the palette on the left (drag onto the canvas or click).
            </p>
          </div>
          <button
            type="button"
            className="workflows-page__new-btn"
            onClick={() => setNewWorkflowModalOpen(true)}
          >
            <MaterialIcon icon="add" size={18} />
            <span>New workflow</span>
          </button>
          {newWorkflowModalOpen && (
            <div className="modal-overlay" style={{ zIndex: 1001 }} onClick={() => { setNewWorkflowModalOpen(false); setWorkflowTypeHover(null); }}>
              <div className="modal-content pro-modal new-workflow-modal" onClick={(e) => e.stopPropagation()}>
                <div className="pro-modal__header">
                  <h2 className="pro-modal__title">Create new workflow</h2>
                  <button type="button" className="pro-modal__close" onClick={() => { setNewWorkflowModalOpen(false); setWorkflowTypeHover(null); }} aria-label="Zavrieť">
                    <MaterialIcon icon="close" size={20} />
                  </button>
                </div>
                <div className="pro-modal__body">
                  <div className="new-workflow-modal__grid">
                    <button
                      type="button"
                      onClick={handleNewWokkioWorkflow}
                      className={`new-workflow-type-btn new-workflow-type-btn--wokkio ${workflowTypeHover === 'n8n' ? 'new-workflow-type-btn--has-preview' : ''}`}
                      onMouseEnter={() => setWorkflowTypeHover('wokkio')}
                      onMouseLeave={() => setWorkflowTypeHover(null)}
                    >
                      <span className="new-workflow-type-btn__title">
                        <MaterialIcon icon="graph_1" size={24} />
                        Browser automation
                      </span>
                      <span className="new-workflow-type-btn__desc">DOM-driven automation for navigation, actions, waits, extraction, and assertions.</span>
                      <span className="new-workflow-type-btn__usecases-title">Typical use cases</span>
                      <ul className="new-workflow-type-btn__usecases">
                        <li>Web scraping & data extraction from sites without a reliable API</li>
                        <li>Automated form submissions (lead capture, onboarding, uploads)</li>
                        <li>End-to-end UI testing (regression checks, smoke tests)</li>
                        <li>Monitoring UI changes (broken buttons, layout shifts, missing elements)</li>
                        <li>Automating internal web tools and legacy portals</li>
                        <li>Session-based workflows (logins, multi-step flows, downloads)</li>
                      </ul>
                      {workflowTypeHover === 'n8n' && (
                        <div className="new-workflow-type-btn__preview new-workflow-type-btn__preview--n8n">
                          <svg viewBox="0 0 340 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                            {/* Webhook trigger */}
                            <g className="preview-node preview-node--1">
                              <rect x="10" y="80" width="52" height="34" rx="5" fill="#3498db" fillOpacity="0.3" stroke="#3498db" strokeWidth="1.5"/>
                              <text x="36" y="101" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="500">Webhook</text>
                            </g>
                            {/* Line to Filter */}
                            <path className="preview-line preview-line--1" d="M62 97 L82 97" stroke="#555" strokeWidth="1.5"/>
                            {/* Agent node */}
                            <g className="preview-node preview-node--2">
                              <rect x="82" y="75" width="44" height="44" rx="5" fill="#f39c12" fillOpacity="0.3" stroke="#f39c12" strokeWidth="1.5"/>
                              <text x="104" y="100" textAnchor="middle" fill="#fff" fontSize="7" fontWeight="500">Agent</text>
                            </g>
                            {/* Branch 1 - top: HTTP → Slack */}
                            <path className="preview-line preview-line--2" d="M130 90 L150 90 L150 50 L170 50" stroke="#555" strokeWidth="1.5"/>
                            <g className="preview-node preview-node--3">
                              <rect x="170" y="33" width="48" height="34" rx="5" fill="#9b59b6" fillOpacity="0.3" stroke="#9b59b6" strokeWidth="1.5"/>
                              <text x="194" y="54" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="500">HTTP</text>
                            </g>
                            <path className="preview-line preview-line--3" d="M218 50 L238 50" stroke="#555" strokeWidth="1.5"/>
                            <g className="preview-node preview-node--4">
                              <rect x="238" y="33" width="48" height="34" rx="5" fill="#27ae60" fillOpacity="0.3" stroke="#27ae60" strokeWidth="1.5"/>
                              <text x="262" y="54" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="500">Slack</text>
                            </g>
                            {/* Branch 2 - middle: AI → Email */}
                            <path className="preview-line preview-line--4" d="M130 97 L170 97" stroke="#555" strokeWidth="1.5"/>
                            <g className="preview-node preview-node--5">
                              <rect x="170" y="80" width="48" height="34" rx="5" fill="#e67e22" fillOpacity="0.3" stroke="#e67e22" strokeWidth="1.5"/>
                              <text x="194" y="101" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="500">AI</text>
                            </g>
                            <path className="preview-line preview-line--5" d="M218 97 L238 97" stroke="#555" strokeWidth="1.5"/>
                            <g className="preview-node preview-node--6">
                              <rect x="238" y="80" width="48" height="34" rx="5" fill="#e74c3c" fillOpacity="0.3" stroke="#e74c3c" strokeWidth="1.5"/>
                              <text x="262" y="101" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="500">Email</text>
                            </g>
                            {/* Branch 3 - bottom: Database → Merge */}
                            <path className="preview-line preview-line--6" d="M130 104 L150 104 L150 144 L170 144" stroke="#555" strokeWidth="1.5"/>
                            <g className="preview-node preview-node--7">
                              <rect x="170" y="127" width="48" height="34" rx="5" fill="#1abc9c" fillOpacity="0.3" stroke="#1abc9c" strokeWidth="1.5"/>
                              <text x="194" y="148" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="500">DB</text>
                            </g>
                            {/* Merge node */}
                            <path className="preview-line preview-line--7" d="M286 67 L300 67 L300 144 L318 144" stroke="#555" strokeWidth="1.5" strokeDasharray="3 2"/>
                            <path className="preview-line preview-line--7b" d="M286 114 L300 114" stroke="#555" strokeWidth="1.5" strokeDasharray="3 2"/>
                            <path className="preview-line preview-line--7c" d="M218 144 L238 144" stroke="#555" strokeWidth="1.5"/>
                            <g className="preview-node preview-node--8">
                              <rect x="238" y="127" width="48" height="34" rx="5" fill="#95a5a6" fillOpacity="0.3" stroke="#95a5a6" strokeWidth="1.5"/>
                              <text x="262" y="148" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="500">Merge</text>
                            </g>
                          </svg>
                        </div>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={handleNewN8nWorkflow}
                      disabled={creatingN8n}
                      className={`new-workflow-type-btn new-workflow-type-btn--n8n ${workflowTypeHover === 'wokkio' ? 'new-workflow-type-btn--has-preview' : ''}`}
                      onMouseEnter={() => setWorkflowTypeHover('n8n')}
                      onMouseLeave={() => setWorkflowTypeHover(null)}
                    >
                      <span className="new-workflow-type-btn__title">
                        <MaterialIcon icon="smart_toy" size={24} />
                        Integration automation
                      </span>
                      <span className="new-workflow-type-btn__desc">Trigger-based workflows for API calls, webhooks, routing, and AI processing.</span>
                      <span className="new-workflow-type-btn__usecases-title">Typical use cases</span>
                      <ul className="new-workflow-type-btn__usecases">
                        <li>Connect apps via APIs (sync records, create/update entities)</li>
                        <li>Webhook-driven automations (react to events in real time)</li>
                        <li>Scheduled workflows (daily reports, backups, recurring sync)</li>
                        <li>Data transformations & routing (filtering, mapping, branching logic)</li>
                        <li>Notifications & incident workflows (Slack/Email/SMS, ticket creation)</li>
                        <li>AI-assisted processing (enrichment, classification, summarization)</li>
                      </ul>
                      {workflowTypeHover === 'wokkio' && (
                        <div className="new-workflow-type-btn__preview new-workflow-type-btn__preview--wokkio">
                          <svg viewBox="0 0 340 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                            {/* Navigate */}
                            <g className="preview-node preview-node--1">
                              <rect x="140" y="8" width="60" height="30" rx="5" fill="#27ae60" fillOpacity="0.3" stroke="#27ae60" strokeWidth="1.5"/>
                              <text x="170" y="27" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="500">Navigate</text>
                            </g>
                            <g className="preview-detail preview-detail--1">
                              <text x="210" y="27" fill="rgba(255,255,255,0.4)" fontSize="7">https://...</text>
                            </g>
                            <path className="preview-line preview-line--1" d="M170 38 L170 48" stroke="#27ae60" strokeWidth="1.5"/>
                            {/* Wait for selector */}
                            <g className="preview-node preview-node--2">
                              <rect x="140" y="48" width="60" height="30" rx="5" fill="#27ae60" fillOpacity="0.3" stroke="#27ae60" strokeWidth="1.5"/>
                              <text x="170" y="67" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="500">Wait</text>
                            </g>
                            <g className="preview-detail preview-detail--2">
                              <text x="210" y="67" fill="rgba(255,255,255,0.4)" fontSize="7">.container</text>
                            </g>
                            <path className="preview-line preview-line--2" d="M170 78 L170 88" stroke="#27ae60" strokeWidth="1.5"/>
                            {/* Loop start */}
                            <g className="preview-node preview-node--3">
                              <rect x="120" y="88" width="100" height="24" rx="4" fill="none" stroke="#27ae60" strokeWidth="1" strokeDasharray="4 2"/>
                              <text x="130" y="103" fill="#27ae60" fontSize="7" fontWeight="500">forEach</text>
                              <text x="175" y="103" fill="rgba(255,255,255,0.4)" fontSize="7">.list-item</text>
                            </g>
                            <path className="preview-line preview-line--3" d="M170 112 L170 120" stroke="#27ae60" strokeWidth="1.5"/>
                            {/* Click inside loop */}
                            <g className="preview-node preview-node--4">
                              <rect x="145" y="120" width="50" height="26" rx="4" fill="#27ae60" fillOpacity="0.3" stroke="#27ae60" strokeWidth="1.2"/>
                              <text x="170" y="137" textAnchor="middle" fill="#fff" fontSize="7" fontWeight="500">Click</text>
                            </g>
                            <path className="preview-line preview-line--4" d="M170 146 L170 154" stroke="#27ae60" strokeWidth="1.2"/>
                            {/* Condition */}
                            <g className="preview-node preview-node--5">
                              <polygon points="170,154 195,168 170,182 145,168" fill="#27ae60" fillOpacity="0.3" stroke="#27ae60" strokeWidth="1.2"/>
                              <text x="170" y="171" textAnchor="middle" fill="#fff" fontSize="6" fontWeight="500">if</text>
                            </g>
                            {/* True branch - Extract */}
                            <path className="preview-line preview-line--5" d="M195 168 L220 168 L220 158" stroke="#27ae60" strokeWidth="1.2"/>
                            <g className="preview-node preview-node--6">
                              <rect x="205" y="132" width="50" height="26" rx="4" fill="#27ae60" fillOpacity="0.3" stroke="#27ae60" strokeWidth="1.2"/>
                              <text x="230" y="149" textAnchor="middle" fill="#fff" fontSize="7" fontWeight="500">Extract</text>
                            </g>
                            <g className="preview-detail preview-detail--6">
                              <text x="262" y="149" fill="rgba(255,255,255,0.4)" fontSize="6">→ data</text>
                            </g>
                            {/* False branch - Screenshot */}
                            <path className="preview-line preview-line--6" d="M145 168 L120 168 L120 158" stroke="#27ae60" strokeWidth="1.2"/>
                            <g className="preview-node preview-node--7">
                              <rect x="85" y="132" width="50" height="26" rx="4" fill="#27ae60" fillOpacity="0.3" stroke="#27ae60" strokeWidth="1.2"/>
                              <text x="110" y="149" textAnchor="middle" fill="#fff" fontSize="7" fontWeight="500">Screenshot</text>
                            </g>
                            {/* Loop end indicator */}
                            <path className="preview-line preview-line--7" d="M120 132 L120 95 L120 95" stroke="#27ae60" strokeWidth="1" strokeDasharray="3 2"/>
                            <path className="preview-line preview-line--7b" d="M220 132 L220 112 L220 112" stroke="#27ae60" strokeWidth="1" strokeDasharray="3 2"/>
                            {/* End node */}
                            <path className="preview-line preview-line--8" d="M170 182 L170 190" stroke="#27ae60" strokeWidth="1.2"/>
                            <g className="preview-node preview-node--8">
                              <circle cx="170" cy="195" r="5" fill="#27ae60" fillOpacity="0.5" stroke="#27ae60" strokeWidth="1"/>
                            </g>
                          </svg>
                        </div>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </header>

        <div className="workflows-tabs">
          <button
            type="button"
            className={`workflows-tabs__tab ${listTab === 'workflows' ? 'workflows-tabs__tab--active' : ''}`}
            onClick={() => setListTab('workflows')}
          >
            Workflows
          </button>
          <button
            type="button"
            className={`workflows-tabs__tab ${listTab === 'executions' ? 'workflows-tabs__tab--active' : ''}`}
            onClick={() => setListTab('executions')}
          >
            Executions
          </button>
        </div>

        <div className="workflows-tab-panels">
          {listTab === 'workflows' && (
            <div className="workflows-list-section">
              {!workflows.length ? (
                <div className="workflows-list-empty">No workflows yet. Create the first one with the "New workflow" button.</div>
              ) : (
                <>
                  <div className="workflows-list-header">
                    <div className="workflows-list-header__name">Name</div>
                    <div className="workflows-list-header__date">Created at</div>
                    <div className="workflows-list-header__date">Published at</div>
                    <div className="workflows-list-header__date">Updated at</div>
                    <div className="workflows-list-header__actions" />
                  </div>
                  <div className="workflows-list-rows">
                  {workflows.map((w) => {
                    const createdAt = (w.createdAt ?? w.created_at) != null ? new Date(w.createdAt ?? w.created_at).toLocaleString() : '—';
                    const updatedAt = (w.updatedAt ?? w.updated_at) != null ? new Date(w.updatedAt ?? w.updated_at).toLocaleString() : '—';
                    const publishedAt = (w.publishedAt ?? w.published_at) != null ? new Date(w.publishedAt ?? w.published_at).toLocaleString() : '—';
                    const isKebabOpen = kebabOpenId === w._id;
                    return (
                      <div
                        key={w._id}
                        className="workflows-list-row"
                        onClick={() => openWorkflow(w._id)}
                      >
                        <div className="workflows-list-row__name">
                          <MaterialIcon
                            icon={w.workflowType === 'n8n' ? 'smart_toy' : 'automation'}
                            size={20}
                            className="workflows-list-row__type-icon"
                            title={w.workflowType === 'n8n' ? 'n8n workflow' : 'Wokkio workflow'}
                          />
                          {w.published && <span className="workflows-list-row__live-dot" title="Live" />}
                          <span>{w.name || '—'}</span>
                        </div>
                        <div className="workflows-list-row__date">{createdAt}</div>
                        <div className="workflows-list-row__date">{publishedAt}</div>
                        <div className="workflows-list-row__date">{updatedAt}</div>
                        <div className="workflows-list-row__actions" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className="workflows-list-row__kebab"
                            onClick={(e) => { e.stopPropagation(); setKebabOpenId(isKebabOpen ? null : w._id); }}
                            aria-label="Menu"
                          >
                            <MaterialIcon icon="more_vert" size={20} />
                          </button>
                          {isKebabOpen && (
                            <div className="workflows-list-row__menu" onClick={(e) => e.stopPropagation()}>
                              <button type="button" onClick={() => { setKebabOpenId(null); openWorkflow(w._id); }}>Open</button>
                              <button type="button" onClick={(e) => { setKebabOpenId(null); handleDeleteClick(w._id, e); }}>Delete</button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  </div>
                </>
              )}
            </div>
          )}

          {listTab === 'executions' && (
            <section className="workflows-history-section">
              {recentRunsLoading ? (
                <div className="workflows-history-empty">Loading...</div>
              ) : !recentRuns?.length ? (
                <div className="workflows-history-empty">No runs yet.</div>
              ) : (
                <div className="workflows-history-list">
                  {recentRuns.map((run) => {
                    const w = workflows.find((x) => x._id === run.workflowId);
                    const wName = w?.name || run.workflowId || '—';
                    const runKey = run._id?.toString?.() ?? run.id ?? run._id;
                    const isFailed = run.status === 'failed' || run.status === 'error';
                    const statusLabel = run.status === 'running' ? 'Running' : run.status === 'success' ? 'OK' : isFailed ? 'Error' : run.status || '—';
                    const statusClass = run.status === 'running' ? 'running' : run.status === 'success' ? 'success' : isFailed ? 'failed' : '';
                    return (
                      <div
                        key={runKey}
                        role="button"
                        tabIndex={0}
                        className="workflows-history-item"
                        onClick={() => navigate(`/workflows/runs/${run._id}`)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/workflows/runs/${run._id}`); } }}
                      >
                        <div className="workflows-history-item__main">
                          <MaterialIcon
                            icon={w?.workflowType === 'n8n' ? 'smart_toy' : 'automation'}
                            size={18}
                            className="workflows-history-item__type-icon"
                            title={w?.workflowType === 'n8n' ? 'n8n workflow' : 'Wokkio workflow'}
                          />
                          <span className="workflows-history-item__name">{wName}</span>
                          <span className={`workflows-history-item__status workflows-history-item__status--${statusClass}`}>
                            {statusLabel}
                          </span>
                        </div>
                        <div className="workflows-history-item__meta">
                          <span className="workflows-history-item__date">
                            {run.startedAt ? new Date(run.startedAt).toLocaleString() : '—'}
                          </span>
                          <span className="workflows-history-item__link">Run detail →</span>
                        </div>
                      </div>
                    );
                  })}
                  {recentRunsPagination && recentRunsPagination.totalPages > 1 && (
                    <div className="workflows-history-pagination">
                      <button
                        type="button"
                        className="pagination-btn"
                        onClick={() => setRecentRunsPage(Math.max(1, recentRunsPage - 1))}
                        disabled={recentRunsPage <= 1}
                      >
                        ← Previous
                      </button>
                      <span className="workflows-history-pagination__info">
                        Page {recentRunsPagination.page} of {recentRunsPagination.totalPages} ({recentRunsPagination.total} total)
                      </span>
                      <button
                        type="button"
                        className="pagination-btn"
                        onClick={() => setRecentRunsPage(Math.min(recentRunsPagination.totalPages, recentRunsPage + 1))}
                        disabled={recentRunsPage >= recentRunsPagination.totalPages}
                      >
                        Next →
                      </button>
                    </div>
                  )}
                </div>
              )}
            </section>
          )}
        </div>

        {deleteConfirmWorkflow && (
          <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001 }} onClick={() => setDeleteConfirmWorkflow(null)}>
            <div className="modal" style={{ background: '#252525', padding: 24, borderRadius: 8, minWidth: 360 }} onClick={(e) => e.stopPropagation()}>
              <h3>Naozaj zmazať workflow?</h3>
              <p style={{ marginTop: 8, color: '#aaa' }}>
                Workflow „{deleteConfirmWorkflow.name}“ bude natrvalo zmazaný. Túto akciu nemožno vrátiť späť.
              </p>
              <div style={{ marginTop: 24, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setDeleteConfirmWorkflow(null)}>Zrušiť</button>
                <button type="button" className="btn btn-danger" onClick={handleDeleteConfirm}>Zmazať</button>
              </div>
            </div>
          </div>
        )}
        {runModal && (() => {
          const showValidation = runForm._attemptedRun;
          const isQueryEmpty = (varName) => showValidation && !(runForm.queryParams?.[varName] || '').trim();
          const isBodyEmpty = (varName) => showValidation && !(runForm.bodyParams?.[varName] || '').trim();
          const hasParams = runForm._extractedVars?.query?.length > 0 || runForm._extractedVars?.body?.length > 0;
          return (
          <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setRunModal(null)}>
            <div className="modal" style={{ background: '#252525', padding: 24, borderRadius: 8, minWidth: 400, maxWidth: 500, maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
              <h3>Run workflow: {runModal.name}</h3>
              
              {/* Workflow input parameters intro */}
              {hasParams && (
                <p style={{ marginTop: 12, fontSize: 13, color: '#999', lineHeight: 1.5 }}>
                  This workflow requires input parameters to run. Please provide values for each field below.
                </p>
              )}
              
              {/* Query parameters */}
              {runForm._extractedVars?.query?.length > 0 && (
                <div style={{ marginTop: 16, padding: 12, background: '#1a1a1a', borderRadius: 6, border: '1px solid #333' }}>
                  <div style={{ fontSize: 12, color: '#888', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <MaterialIcon icon="link" size={14} />
                    <span>Query parameters</span>
                  </div>
                  {runForm._extractedVars.query.map((varName) => (
                    <div key={`query-${varName}`} style={{ marginTop: 8 }}>
                      <label style={{ fontSize: 13, color: '#ccc' }}>{varName} <span style={{ color: '#e74c3c' }}>*</span></label>
                      <input
                        type="text"
                        value={runForm.queryParams?.[varName] || ''}
                        onChange={(e) => setRunForm((f) => ({ ...f, queryParams: { ...f.queryParams, [varName]: e.target.value } }))}
                        placeholder={varName}
                        style={{ width: '100%', padding: 8, marginTop: 4, background: '#252525', border: `1px solid ${isQueryEmpty(varName) ? '#e74c3c' : '#444'}`, borderRadius: 4, color: '#fff' }}
                      />
                    </div>
                  ))}
                </div>
              )}
              
              {/* Body parameters */}
              {runForm._extractedVars?.body?.length > 0 && (
                <div style={{ marginTop: 12, padding: 12, background: '#1a1a1a', borderRadius: 6, border: '1px solid #333' }}>
                  <div style={{ fontSize: 12, color: '#888', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <MaterialIcon icon="data_object" size={14} />
                    <span>Body parameters</span>
                  </div>
                  {runForm._extractedVars.body.map((varName) => (
                    <div key={`body-${varName}`} style={{ marginTop: 8 }}>
                      <label style={{ fontSize: 13, color: '#ccc' }}>{varName} <span style={{ color: '#e74c3c' }}>*</span></label>
                      <input
                        type="text"
                        value={runForm.bodyParams?.[varName] || ''}
                        onChange={(e) => setRunForm((f) => ({ ...f, bodyParams: { ...f.bodyParams, [varName]: e.target.value } }))}
                        placeholder={varName}
                        style={{ width: '100%', padding: 8, marginTop: 4, background: '#252525', border: `1px solid ${isBodyEmpty(varName) ? '#e74c3c' : '#444'}`, borderRadius: 4, color: '#fff' }}
                      />
                    </div>
                  ))}
                </div>
              )}

              <div style={{ marginTop: 16 }}>
                <label className={`workflow-checkbox-option ${runForm.useProxy ? 'is-checked' : ''}`}>
                  <input type="checkbox" checked={runForm.useProxy} onChange={(e) => setRunForm((f) => ({ ...f, useProxy: e.target.checked }))} />
                  <span className="workflow-checkbox-box" aria-hidden />
                  <span className="workflow-checkbox-label">Proxy</span>
                </label>
              </div>
              <div style={{ marginTop: 12 }}>
                <label className={`workflow-checkbox-option ${runForm.headless ? 'is-checked' : ''} ${runForm.recordingMode ? 'opacity-60' : ''}`}>
                  <input type="checkbox" checked={runForm.headless} disabled={runForm.recordingMode} onChange={(e) => setRunForm((f) => ({ ...f, headless: e.target.checked }))} />
                  <span className="workflow-checkbox-box" aria-hidden />
                  <span className="workflow-checkbox-label">Headless</span>
                </label>
                {runForm.recordingMode && <p style={{ margin: '2px 0 0 24px', fontSize: 11, color: '#888' }}>Headless cannot be enabled in recording mode.</p>}
              </div>
              <div style={{ marginTop: 12 }}>
                <label className={`workflow-checkbox-option ${runForm.fresh ? 'is-checked' : ''}`}>
                  <input type="checkbox" checked={runForm.fresh || false} onChange={(e) => setRunForm((f) => ({ ...f, fresh: e.target.checked }))} />
                  <span className="workflow-checkbox-box" aria-hidden />
                  <span className="workflow-checkbox-label">Clean session</span>
                </label>
                <p style={{ margin: '4px 0 0 24px', fontSize: 11, color: '#777', lineHeight: 1.4 }}>Start with a fresh browser profile — no cookies, cache, or saved logins</p>
              </div>
              <div style={{ marginTop: 12, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <label className={`workflow-checkbox-option ${runForm.recordingMode ? 'is-checked' : ''}`} style={{ margin: 0 }}>
                  <input type="checkbox" checked={runForm.recordingMode || false} onChange={(e) => setRunForm((f) => ({ ...f, recordingMode: e.target.checked, ...(e.target.checked ? { headless: false } : {}) }))} />
                  <span className="workflow-checkbox-box" aria-hidden />
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <MaterialIcon icon="fiber_manual_record" size={16} style={{ color: runForm.recordingMode ? '#38bdf8' : '#64748b' }} />
                    <span className="workflow-checkbox-label">Recording mode</span>
                  </span>
                </label>
                <p style={{ margin: 0, flex: 1, fontSize: 11, color: '#777', lineHeight: 1.4 }}>Browser stays open after the last step; add more steps via the Wokkio Chrome extension toolbar.</p>
              </div>
              <div style={{ marginTop: 24, display: 'flex', gap: 8 }}>
                <button type="button" className="btn btn-primary" onClick={handleRun}>Run</button>
                <button type="button" className="btn btn-secondary" onClick={() => setRunModal(null)}>Cancel</button>
              </div>
            </div>
          </div>
          );
        })()}
      </div>
    </Layout>
  );
}

export default Workflows;
